import { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from 'xlsx';
window.XLSX = XLSX;

// ════════════════════════════════════════════════════════════════════════════
// 🔥 FIREBASE SETUP — DO THESE STEPS BEFORE RUNNING THE APP
// ════════════════════════════════════════════════════════════════════════════
//
//  STEP 1 ── INSTALL FIREBASE PACKAGE
//    Open terminal in your project folder and run:
//    > npm install firebase
//
//  STEP 2 ── CREATE FIREBASE PROJECT
//    a) Go to https://console.firebase.google.com
//    b) Sign in with your Gmail account
//    c) Click the big "+ Add project" card (or "Create a project" button)
//    d) Name it: iirt-app
//    e) Disable Google Analytics (toggle OFF)
//    f) Click "Create project" → wait → Click "Continue"
//
//  STEP 3 ── ENABLE FIRESTORE DATABASE
//    a) In your project, click "Build" in the left sidebar
//    b) Click "Firestore Database"
//    c) Click "Create database"
//    d) Choose "Start in test mode"
//    e) Location: asia-south1 (Mumbai — closest to India)
//    f) Click "Create"
//
//  STEP 4 ── SET SECURITY RULES (to avoid 30-day expiry)
//    a) Go to Firestore → Rules tab
//    b) Delete everything and paste this:
//       rules_version = '2';
//       service cloud.firestore {
//         match /databases/{database}/documents {
//           match /iirt/{document} {
//             allow read, write: if true;
//           }
//         }
//       }
//    c) Click "Publish"
//
//  STEP 5 ── REGISTER YOUR WEB APP & GET CONFIG
//    a) Click "Project Overview" (top-left)
//    b) Click the </> Web icon in the center
//    c) App nickname: iirt-web → Click "Register app"
//    d) COPY the firebaseConfig object shown on screen
//
//  STEP 6 ── PASTE YOUR CONFIG BELOW
//    Replace each "REPLACE_WITH_YOUR_..." with your actual values from Step 5
//
//  STEP 7 ── BUILD AND DEPLOY
//    > npm run build
//    Drag the "build" folder to Netlify.com
//
// ════════════════════════════════════════════════════════════════════════════

// ─── FIREBASE — SAFE LAZY INIT (app works even without config) ───────────────
const FIREBASE_CONFIG = {
  apiKey:            "AIzaSyC-MTbk9YMkJT1HGDv5gIVFLEgBLfvcoys",
  authDomain:        "iirt-internastra.firebaseapp.com",
  projectId:         "iirt-internastra",
  storageBucket:     "iirt-internastra.firebasestorage.app",
  messagingSenderId: "99724218665",
  appId:             "1:99724218665:web:5ec8f269574c9f00892738",
};

const FB_CONFIGURED = Object.values(FIREBASE_CONFIG).every(v => v && !v.startsWith("REPLACE_"));
let db_firebase = null;
let _doc = null, _getDoc = null, _setDoc = null, _onSnapshot = null;

async function initFirebase() {
  if (db_firebase) return true;
  if (!FB_CONFIGURED) return false;
  try {
    const fbApp = await import("firebase/app");
    const fbStore = await import("firebase/firestore");
    const app = fbApp.initializeApp(FIREBASE_CONFIG);
    db_firebase = fbStore.getFirestore(app);
    _doc = fbStore.doc; _getDoc = fbStore.getDoc;
    _setDoc = fbStore.setDoc; _onSnapshot = fbStore.onSnapshot;
    return true;
  } catch (e) { console.warn("Firebase init failed:", e.message); return false; }
}
async function fbGet(col, id) {
  const ok = await initFirebase();
  if (!ok) return null;
  try { const s = await _getDoc(_doc(db_firebase, col, id)); return s.exists() ? s.data() : null; }
  catch { return null; }
}
async function fbSet(col, id, data) {
  const ok = await initFirebase();
  if (!ok) return false;
  try { await _setDoc(_doc(db_firebase, col, id), data); return true; }
  catch { return false; }
}
function fbSubscribe(col, id, callback) {
  let unsubFirestore = () => {};
  initFirebase().then(ok => {
    if (!ok) {
      // Firebase not configured — fire callback with null so callers can fall back
      callback(null);
      return;
    }
    try {
      unsubFirestore = _onSnapshot(
        _doc(db_firebase, col, id),
        snap => callback(snap.exists() ? snap.data() : null),
        () => callback(null) // error handler — treat as null
      );
    } catch { callback(null); }
  });
  return () => unsubFirestore();
}

// ─── DOMAIN-SPECIFIC QUESTION BANK ──────────────────────────────────────────
// Each domain: 5 Aptitude + 5 Reasoning + 10 Technical = 20 questions
const DOMAIN_QUESTIONS = {
  "Full Stack Web Development": [
    // Aptitude
    { type:"Aptitude", text:"A web app handles 2400 requests per minute. If each server handles 400 req/min, how many servers are needed with 20% redundancy?" },
    { type:"Aptitude", text:"A developer completes frontend in 6 days and backend in 9 days. Working together on separate layers, how many days to complete the full project?" },
    { type:"Aptitude", text:"A webpage loads 15 resources. If 4 load in parallel taking 3s each, and rest load sequentially at 0.5s each, what is total load time?" },
    { type:"Aptitude", text:"If a REST API has 95% uptime per month (30 days), how many minutes of downtime occurs per month?" },
    { type:"Aptitude", text:"A database query runs in 120ms. After optimizing indexes, it runs 4x faster. After caching, it runs 6x faster than optimized. What is final query time?" },
    // Reasoning
    { type:"Reasoning", text:"If every microservice depends on an API gateway, and the API gateway depends on a load balancer, and the load balancer fails — what is the logical consequence for all microservices?" },
    { type:"Reasoning", text:"A React component re-renders every time its parent re-renders, but its props never change. What logical pattern would you apply to prevent this, and why?" },
    { type:"Reasoning", text:"You have tables: Users(id, name), Orders(id, user_id, amount), Products(id, order_id, name). Logically, what JOIN sequence retrieves all product names bought by a specific user?" },
    { type:"Reasoning", text:"If an application works on localhost but fails in production, and environment variables are the same, what is the logical next step to isolate the issue?" },
    { type:"Reasoning", text:"A CORS error appears on a POST request but not a GET request to the same endpoint. Logically, what is the most likely cause and fix?" },
    // Technical
    { type:"Technical", text:"Explain the event loop in Node.js and how it handles asynchronous I/O without multi-threading. What is the call stack vs callback queue?" },
    { type:"Technical", text:"What is the Virtual DOM in React? How does the reconciliation algorithm (diffing) determine what needs to be updated in the real DOM?" },
    { type:"Technical", text:"Explain the difference between SQL transactions with ACID properties and BASE consistency model used in NoSQL. When would you choose each?" },
    { type:"Technical", text:"What are WebSockets and how do they differ from HTTP long-polling and Server-Sent Events? When is each approach most appropriate?" },
    { type:"Technical", text:"Explain OAuth 2.0 authorization code flow in detail. What are access tokens vs refresh tokens and how do you handle token expiry securely?" },
    { type:"Technical", text:"What is database connection pooling? What happens without it under high concurrency? How does a tool like pgBouncer help?" },
    { type:"Technical", text:"Describe the N+1 query problem in ORM frameworks. How do you detect it and what are the solutions (eager loading, DataLoader, etc.)?" },
    { type:"Technical", text:"What is Content Security Policy (CSP)? How does it prevent XSS attacks? Write an example CSP header and explain each directive." },
    { type:"Technical", text:"Explain server-side rendering (SSR) vs static site generation (SSG) vs client-side rendering (CSR). What are the trade-offs for SEO and performance?" },
    { type:"Technical", text:"What is the CAP theorem? Given a network partition, how do distributed databases like Cassandra vs MongoDB handle consistency vs availability trade-offs?" },
  ],
  "AI/ML": [
    { type:"Aptitude", text:"A neural network training loop processes 512 samples per batch. If there are 51,200 training samples, how many gradient update steps occur in one epoch?" },
    { type:"Aptitude", text:"A model achieves 92% accuracy on training data and 74% accuracy on test data. What percentage gap indicates overfitting, and how many more test samples are needed if current test set is 500 to reach 1200?" },
    { type:"Aptitude", text:"An AI inference server handles 240 requests per minute. Each ML model call takes 800ms. How many parallel model instances are needed to keep queue wait time under 200ms?" },
    { type:"Aptitude", text:"A dataset has 10,000 samples: 9,000 negative and 1,000 positive. A model predicts all negative. What is its accuracy, and why is this misleading for a fraud detection use case?" },
    { type:"Aptitude", text:"A random forest with 100 trees each trained on 70% of 10,000 samples (with replacement). If each tree makes predictions on the full test set, how many total predictions are aggregated for 200 test samples?" },
    { type:"Reasoning", text:"A model trained on historical stock data predicts tomorrow's price. It achieves 0.01 MSE on training but 1.5 MSE on live data. Logically, what type of bias is present and what is the fix?" },
    { type:"Reasoning", text:"A feature has 30% missing values. You fill them with the mean. After standardization the column has mean 0. Logically, does this introduce bias into the model and how would you handle it better?" },
    { type:"Reasoning", text:"A model performs well in testing but fails in production with real-world data. Training data was collected in one city. What is the most logical root cause and mitigation strategy?" },
    { type:"Reasoning", text:"A neural network with 4 layers trains perfectly on 50 samples but fails on 500 new samples. Adding more data does not help much. Logically, what architectural change should be made?" },
    { type:"Reasoning", text:"An LSTM model's validation loss oscillates while training loss decreases steadily. The learning rate is fixed at 0.01. Logically, what is the most likely cause and what adjustment resolves it?" },
    { type:"Technical", text:"Explain gradient descent and its variants: SGD, Mini-batch GD, Adam, and RMSprop. When does Adam outperform SGD and what are the hyperparameters that control its adaptive learning rate?" },
    { type:"Technical", text:"What is the bias-variance tradeoff? Explain underfitting and overfitting with learning curve plots. How does model complexity, training set size, and regularization each affect this tradeoff?" },
    { type:"Technical", text:"Explain the difference between supervised, unsupervised, and reinforcement learning. What algorithm family best suits anomaly detection in unlabeled sensor data and why?" },
    { type:"Technical", text:"What is a transformer and how does the self-attention mechanism work? Explain multi-head attention, positional encoding, and why transformers outperform RNNs on long sequences." },
    { type:"Technical", text:"Explain feature engineering techniques: one-hot encoding, label encoding, embeddings, binning, and polynomial features. When does feature engineering matter more than model selection?" },
    { type:"Technical", text:"What is k-fold cross validation? How does stratified k-fold differ? Explain leave-one-out CV and when it is appropriate. How do you prevent data leakage during cross-validation?" },
    { type:"Technical", text:"What is regularization in deep learning? Compare L1, L2, dropout, batch normalization, and data augmentation. How does each prevent overfitting and at which layer should each be applied?" },
    { type:"Technical", text:"Explain the encoder-decoder architecture used in seq2seq models. How does attention mechanism improve translation quality over vanilla encoder-decoder? What problem does it solve?" },
    { type:"Technical", text:"What is a GAN (Generative Adversarial Network)? Explain the generator-discriminator training loop, loss functions, and common training instability issues like mode collapse." },
    { type:"Technical", text:"Explain BERT pre-training objectives (MLM and NSP). How is BERT fine-tuned for downstream tasks like classification, NER, and QA? What is the difference between BERT-base and BERT-large?" },
  ],
  "Cyber Security": [
    // Aptitude
    { type:"Aptitude", text:"A firewall processes 10,000 packets/sec. If 0.5% are flagged, how many flagged packets in 2 minutes?" },
    { type:"Aptitude", text:"A brute force attack tests 10^9 passwords/sec. How many seconds to crack a 6-digit numeric PIN (10^6 combinations)?" },
    { type:"Aptitude", text:"An SSL certificate costs ₹5000/yr. A company has 12 domains. What is the 3-year total cost?" },
    { type:"Aptitude", text:"A network logs 2400 security events/day. If 3% are critical, how many critical events in a 30-day month?" },
    { type:"Aptitude", text:"If encrypting a file takes 0.4s and decrypting takes 0.3s, how long to encrypt and decrypt 50 files?" },
    // Reasoning
    { type:"Reasoning", text:"Encryption : Plaintext :: Decryption : ?" },
    { type:"Reasoning", text:"If all phishing emails are malicious, and this email is phishing, is this email malicious?" },
    { type:"Reasoning", text:"Firewall : Network :: Antivirus : ?" },
    { type:"Reasoning", text:"Public Key : Encrypt :: Private Key : ?" },
    { type:"Reasoning", text:"Vulnerability : Patch :: Threat : ?" },
    // Technical
    { type:"Technical", text:"What does the CIA triad stand for in information security?" },
    { type:"Technical", text:"What is the difference between symmetric and asymmetric encryption?" },
    { type:"Technical", text:"What does a SQL injection attack exploit?" },
    { type:"Technical", text:"What is a Zero-Day vulnerability?" },
    { type:"Technical", text:"What is the purpose of a VPN in cybersecurity?" },
    { type:"Technical", text:"What is the difference between IDS and IPS?" },
    { type:"Technical", text:"What is Cross-Site Scripting (XSS)?" },
    { type:"Technical", text:"What is penetration testing?" },
    { type:"Technical", text:"What is a Man-in-the-Middle (MITM) attack?" },
    { type:"Technical", text:"What is the difference between HTTP and HTTPS?" },
  ],
};

// ─── AI QUESTION + EVALUATION ENGINE (Gemini Flash via OpenRouter) ─────────────
// Uses OpenRouter free tier — no API key needed in browser for demo
// Questions are AI-generated fresh every session — never repeats

const ALL_DOMAINS = Object.keys(DOMAIN_QUESTIONS);

// Track used question texts per session to avoid repeats
const _usedQuestions = new Set();

function getDomainKey(role) {
  if (!role) return ALL_DOMAINS[0];
  const r = role.toLowerCase().trim();
  // Exact match first
  const exact = ALL_DOMAINS.find(d => d.toLowerCase() === r);
  if (exact) return exact;
  // Manual mappings for registration dropdown values that may differ from DOMAIN_QUESTIONS keys
  const MAP = {
    "ai/ml": "AI/ML",
    "python": "AI/ML",
    "aiml": "AI/ML",
    "machine learning": "AI/ML",
    "data science": "Data Science",
    "ai": "AI/ML",
    "deep learning": "AI/ML",
    "embedded systems": "Embedded Systems",
    "ui/ux design": "UI/UX Design",
    "uiux": "UI/UX Design",
    "android development": "Android Development",
    "android": "Android Development",
    "autocad": "AutoCAD",
    "vlsi": "VLSI",
    "internet of things": "Internet of Things",
    "iot": "Internet of Things",
    "robotics": "Robotics",
    "hybrid and electric vehicle": "Hybrid and Electric Vehicle",
    "hybrid electric vehicle": "Hybrid and Electric Vehicle",
    "autocad (mech)": "AutoCAD (Mech)",
    "autocad mech": "AutoCAD (Mech)",
    "car design": "Car Design",
    "ic engine": "IC Engine",
    "construction plan & design": "Construction Plan & Design",
    "construction plan and design": "Construction Plan & Design",
    "medical coding": "Medical Coding",
    "nano science and technology": "Nano Science and Technology",
    "nano science": "Nano Science and Technology",
    "full stack web development": "Full Stack Web Development",
    "full stack": "Full Stack Web Development",
    "cyber security": "Cyber Security",
    "cybersecurity": "Cyber Security",
  };
  if (MAP[r]) return MAP[r];
  // Partial word match fallback
  const partial = ALL_DOMAINS.find(d => r.includes(d.toLowerCase().split(" ")[0]) || d.toLowerCase().includes(r.split(" ")[0]));
  return partial || ALL_DOMAINS[0];
}

// Call Anthropic Claude API
async function callClaude(systemPrompt, userPrompt, maxTokens = 600) {
  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: maxTokens,
      system: systemPrompt,
      messages: [{ role: "user", content: userPrompt }],
    }),
  });
  if (!response.ok) throw new Error("API " + response.status);
  const data = await response.json();
  const text = data.content?.find(c => c.type === "text")?.text || "{}";
  return text.replace(/```json|```/g, "").trim();
}

// Built-in MCQ fallback bank — used when AI is unavailable.
// Uses DOMAIN_QUESTIONS (already in scope) for domain questions — covers ALL 20 domains.
function _getFallbackMCQ(category, domain, usedSet) {
  // Aptitude fallback pool
  const aptBank = [
    { text:"A project takes 12 days with 8 workers. How many days will 6 workers take?", A:"14 days", B:"16 days", C:"18 days", D:"10 days", correct:"B", explanation:"Work = 8×12=96 person-days. 96÷6=16 days." },
    { text:"A train travels 360 km in 4 hours. What is its speed in m/s?", A:"20 m/s", B:"25 m/s", C:"30 m/s", D:"15 m/s", correct:"B", explanation:"360 km/h ÷ 3.6 = 25 m/s." },
    { text:"If 15% of a number is 75, what is 40% of the same number?", A:"150", B:"175", C:"200", D:"225", correct:"C", explanation:"Number = 75÷0.15 = 500. 40% of 500 = 200." },
    { text:"A can do a job in 10 days, B in 15 days. Together, how many days?", A:"5 days", B:"6 days", C:"8 days", D:"12 days", correct:"B", explanation:"Combined rate = 1/10 + 1/15 = 1/6, so 6 days." },
    { text:"Simple interest on ₹2000 at 5% per annum for 3 years:", A:"₹200", B:"₹250", C:"₹300", D:"₹350", correct:"C", explanation:"SI = P×R×T÷100 = 2000×5×3÷100 = ₹300." },
    { text:"If a product costs ₹800 after a 20% discount, what was the original price?", A:"₹960", B:"₹1000", C:"₹1050", D:"₹1100", correct:"B", explanation:"800 = 80% of original. Original = 800÷0.8 = ₹1000." },
  ];

  // Reasoning fallback pool
  const reasonBank = [
    { text:"Choose the odd one out: Square, Circle, Triangle, Cube, Rectangle", A:"Square", B:"Circle", C:"Cube", D:"Triangle", correct:"C", explanation:"Cube is 3D; all others are 2D shapes." },
    { text:"If all Roses are Flowers, and some Flowers are Red, which MUST be true?", A:"All Roses are Red", B:"Some Roses are Red", C:"No Roses are Red", D:"None of the above must be true", correct:"D", explanation:"We cannot conclude any Roses are Red from these premises alone." },
    { text:"Complete the series: 2, 6, 12, 20, 30, ?", A:"40", B:"42", C:"44", D:"48", correct:"B", explanation:"Differences: 4,6,8,10,12. Next = 30+12 = 42." },
    { text:"If MANGO = 13-1-14-7-15, what is the code for APPLE?", A:"1-16-16-12-5", B:"1-15-16-12-5", C:"1-16-15-12-5", D:"2-16-16-12-5", correct:"A", explanation:"Each letter maps to its alphabetical position number." },
    { text:"Pen : Write :: Knife : ?", A:"Cut", B:"Sharp", C:"Metal", D:"Cook", correct:"A", explanation:"A pen is used to write; a knife is used to cut." },
    { text:"Find the next: 1, 4, 9, 16, 25, ?", A:"30", B:"35", C:"36", D:"49", correct:"C", explanation:"Perfect squares: 1²,2²,3²,4²,5²,6²=36." },
  ];

  // Domain: pull directly from DOMAIN_QUESTIONS which has all 20 domains
  if (category === "Domain") {
    const domainPool = DOMAIN_QUESTIONS[domain] || DOMAIN_QUESTIONS[Object.keys(DOMAIN_QUESTIONS)[0]];
    const techOnly = domainPool.filter(q => q.type === "Technical" || q.type === "Domain");
    const pool = techOnly.length > 0 ? techOnly : domainPool;
    const unused = pool.filter(q => !usedSet.has(q.text.substring(0, 60)));
    const pick = (unused.length > 0 ? unused : pool)[Math.floor(Math.random() * (unused.length > 0 ? unused.length : pool.length))];
    // DOMAIN_QUESTIONS entries only have text+type, no A/B/C/D
    // So generate placeholder options — AI will still be tried first; this is last resort only
    return {
      text: pick.text,
      options: { A: "True", B: "False", C: "It depends on the context", D: "None of the above", correct: "A", explanation: "Based on domain knowledge of " + domain + "." },
      explanation: "Based on domain knowledge.",
      category,
      domain,
    };
  }

  // Aptitude
  if (category === "Numerical Aptitude") {
    const unused = aptBank.filter(q => !usedSet.has(q.text.substring(0, 60)));
    const pool = unused.length > 0 ? unused : aptBank;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    return { text: pick.text, options: { A: pick.A, B: pick.B, C: pick.C, D: pick.D, correct: pick.correct, explanation: pick.explanation }, explanation: pick.explanation, category, domain };
  }

  // Reasoning
  const unused = reasonBank.filter(q => !usedSet.has(q.text.substring(0, 60)));
  const pool = unused.length > 0 ? unused : reasonBank;
  const pick = pool[Math.floor(Math.random() * pool.length)];
  return { text: pick.text, options: { A: pick.A, B: pick.B, C: pick.C, D: pick.D, correct: pick.correct, explanation: pick.explanation }, explanation: pick.explanation, category, domain };
}


// Generate ONE question + 4 MCQ options via AI, no repeat
async function generateOneQuestion(category, domain, usedSet) {
  const systemPrompt = "You are an expert question setter for internship readiness assessments. Return ONLY valid JSON, no markdown, no extra text.";

  let topicHint = "";
  if (category === "Numerical Aptitude") {
    topicHint = "Create a numerical aptitude problem with actual numbers to calculate (percentages, ratios, time-work, profit-loss, speed-distance, or similar). Must have a single numeric correct answer.";
  } else if (category === "Reasoning") {
    topicHint = "Create a logical reasoning question (series, analogies, syllogisms, direction sense, blood relations, or similar). Must test logical thinking.";
  } else if (category === "Domain") {
    topicHint = `Create a technical MCQ question specifically about ${domain}. Test practical knowledge of concepts, tools, or best practices used in ${domain}.`;
  } else if (category === "Coding") {
    topicHint = `Create a code output prediction MCQ for ${domain}. Show a short code snippet (5-10 lines max) and ask what the output is. Use the most relevant programming language for ${domain}. Format the question as: first show the code block, then ask "What is the output of the above code?"`;
  }

  const usedList = Array.from(usedSet).slice(-10).join("; ");
  const avoidHint = usedList ? `Do NOT generate questions similar to: ${usedList}` : "";

  const userPrompt = `Generate 1 ${category} question for a ${domain} internship assessment.
${topicHint}
${avoidHint}

Return ONLY this JSON:
{
  "question": "<the full question text or code snippet + question>",
  "A": "<option A text>",
  "B": "<option B text>",
  "C": "<option C text>",
  "D": "<option D text>",
  "correct": "<A or B or C or D>",
  "explanation": "<one sentence explanation of why the correct answer is right>"
}
Rules:
- Keep question under 120 words
- Keep each option under 15 words
- Make distractors plausible, not obviously wrong
- Only ONE correct answer`;

  try {
    const raw = await callClaude(systemPrompt, userPrompt, 500);
    const parsed = JSON.parse(raw);
    if (!parsed.question || !parsed.A || !parsed.correct) throw new Error("incomplete");
    usedSet.add(parsed.question.substring(0, 60));
    return {
      text: parsed.question,
      options: { A: parsed.A, B: parsed.B, C: parsed.C, D: parsed.D, correct: parsed.correct, explanation: parsed.explanation || "" },
      explanation: parsed.explanation || "",
      category,
      domain,
    };
  } catch {
    // Fallback: use built-in MCQ bank (always has real A/B/C/D options)
    const fallbackBank = _getFallbackMCQ(category, domain, usedSet);
    usedSet.add(fallbackBank.text.substring(0, 60));
    return fallbackBank;
  }
}

// Generate full 20-question set
// Priority: Admin Question Bank → AI fallback
async function generateAllQuestions(role, onProgress) {
  const domain = getDomainKey(role);
  const bank = await loadQB();
  _usedQuestions.clear();

  // ── Helper: generate one AI question, fallback to DOMAIN_QUESTIONS[domain] if AI fails ──
  async function getQuestion(category, usedSet) {
    try {
      const q = await generateOneQuestion(category, domain, usedSet);
      // Validate AI returned real options
      if (q?.options?.A && q?.options?.B && q?.options?.correct) return q;
      throw new Error("incomplete");
    } catch {
      // Static fallback — ALWAYS from correct domain, never from another domain
      return getStaticQuestion(category, domain, usedSet);
    }
  }

  // ── Helper: pick from static DOMAIN_QUESTIONS bank for the correct domain ──
  function getStaticQuestion(category, dom, usedSet) {
    const domainPool = DOMAIN_QUESTIONS[dom] || DOMAIN_QUESTIONS[ALL_DOMAINS[0]];

    if (category === "Numerical Aptitude") {
      const pool = [
        { text:"A project takes 12 days with 8 workers. How many days will 6 workers take?", A:"14 days", B:"16 days", C:"18 days", D:"10 days", correct:"B", explanation:"Work = 8×12=96 person-days. 96÷6=16 days." },
        { text:"If 15% of a number is 75, what is 40% of the same number?", A:"150", B:"175", C:"200", D:"225", correct:"C", explanation:"Number = 75÷0.15=500. 40% of 500=200." },
        { text:"A train travels 360 km in 4 hours. Speed in m/s?", A:"20 m/s", B:"25 m/s", C:"30 m/s", D:"15 m/s", correct:"B", explanation:"90 km/h ÷ 3.6 = 25 m/s." },
        { text:"A can do a job in 10 days, B in 15 days. Together how many days?", A:"5 days", B:"6 days", C:"8 days", D:"12 days", correct:"B", explanation:"Rate = 1/10+1/15 = 1/6, so 6 days." },
        { text:"Simple interest on ₹2000 at 5% per annum for 3 years:", A:"₹200", B:"₹250", C:"₹300", D:"₹350", correct:"C", explanation:"SI = 2000×5×3÷100 = ₹300." },
        { text:"If a product costs ₹800 after 20% discount, original price?", A:"₹960", B:"₹1000", C:"₹1050", D:"₹1100", correct:"B", explanation:"800 = 80% of original. 800÷0.8 = ₹1000." },
      ];
      const unused = pool.filter(q => !usedSet.has(q.text.substring(0,60)));
      const p = (unused.length > 0 ? unused : pool)[Math.floor(Math.random()*(unused.length||pool.length))];
      usedSet.add(p.text.substring(0,60));
      return { text:p.text, options:{A:p.A,B:p.B,C:p.C,D:p.D,correct:p.correct,explanation:p.explanation}, explanation:p.explanation, category, domain:dom };
    }

    if (category === "Reasoning") {
      const pool = [
        { text:"Choose the odd one out: Square, Circle, Triangle, Cube, Rectangle", A:"Square", B:"Circle", C:"Cube", D:"Triangle", correct:"C", explanation:"Cube is 3D; all others are 2D." },
        { text:"Complete the series: 2, 6, 12, 20, 30, ?", A:"40", B:"42", C:"44", D:"48", correct:"B", explanation:"Differences: 4,6,8,10,12. Next = 30+12=42." },
        { text:"If all Roses are Flowers, and some Flowers are Red, which MUST be true?", A:"All Roses are Red", B:"Some Roses are Red", C:"No Roses are Red", D:"None of the above must be true", correct:"D", explanation:"We cannot conclude any Roses are Red." },
        { text:"Find the next: 1, 4, 9, 16, 25, ?", A:"30", B:"35", C:"36", D:"49", correct:"C", explanation:"Perfect squares: 1²,2²,3²,4²,5²,6²=36." },
        { text:"Pen : Write :: Knife : ?", A:"Cut", B:"Sharp", C:"Metal", D:"Cook", correct:"A", explanation:"A pen is used to write; a knife is used to cut." },
        { text:"If MANGO = 13-1-14-7-15, what is code for APPLE?", A:"1-16-16-12-5", B:"1-15-16-12-5", C:"1-16-15-12-5", D:"2-16-16-12-5", correct:"A", explanation:"Each letter maps to its alphabetical position." },
      ];
      const unused = pool.filter(q => !usedSet.has(q.text.substring(0,60)));
      const p = (unused.length > 0 ? unused : pool)[Math.floor(Math.random()*(unused.length||pool.length))];
      usedSet.add(p.text.substring(0,60));
      return { text:p.text, options:{A:p.A,B:p.B,C:p.C,D:p.D,correct:p.correct,explanation:p.explanation}, explanation:p.explanation, category, domain:dom };
    }

    // Domain question — use DOMAIN_QUESTIONS[dom] (the correct domain, never another)
    const techPool = domainPool.filter(q => q.type === "Technical" || q.type === "Domain");
    const usePool = techPool.length >= 5 ? techPool : domainPool;
    const unused = usePool.filter(q => !usedSet.has(q.text.substring(0,60)));
    const p = (unused.length > 0 ? unused : usePool)[Math.floor(Math.random()*(unused.length||usePool.length))];
    usedSet.add(p.text.substring(0,60));
    return {
      text: p.text,
      options: {
        A: p.A || "True", B: p.B || "False",
        C: p.C || "It depends", D: p.D || "None of the above",
        correct: p.correct || "A", explanation: p.explanation || "Based on " + dom + " knowledge."
      },
      explanation: p.explanation || "Based on domain knowledge.",
      category, domain: dom
    };
  }

  // ── Pick from admin bank (per section) or AI/static ──────────────────────
  const domainBankPool = [...(bank[domain] || []), ...(bank["Coding"] || [])];
  const aptBankPool    = bank["Aptitude"] || [];
  const reasonBankPool = bank["Reasoning"] || [];

  function pickRandom(arr, n) {
    return [...arr].sort(() => Math.random() - 0.5).slice(0, n);
  }

  const questions = [];
  let done = 0;
  const total = 20;

  // ── 10 Domain questions ────────────────────────────────────────────────────
  const domainFromBank = pickRandom(domainBankPool, Math.min(10, domainBankPool.length));
  for (const bq of domainFromBank) {
    questions.push({ id: questions.length+1, text: bq.text, options: { A:bq.A, B:bq.B, C:bq.C, D:bq.D, correct:bq.correct, explanation:bq.explanation||"" }, category:"Domain", domain });
    done++; if (onProgress) onProgress(done, total, "Domain");
  }
  // Fill remaining domain slots with AI / static (always same domain)
  for (let i = domainFromBank.length; i < 10; i++) {
    const q = await getQuestion("Domain", _usedQuestions);
    questions.push({ ...q, id: questions.length+1, category:"Domain", domain });
    done++; if (onProgress) onProgress(done, total, "Domain");
    await new Promise(r => setTimeout(r, 80));
  }

  // ── 5 Aptitude questions ───────────────────────────────────────────────────
  const aptFromBank = pickRandom(aptBankPool, Math.min(5, aptBankPool.length));
  for (const bq of aptFromBank) {
    questions.push({ id: questions.length+1, text: bq.text, options: { A:bq.A, B:bq.B, C:bq.C, D:bq.D, correct:bq.correct, explanation:bq.explanation||"" }, category:"Aptitude", domain });
    done++; if (onProgress) onProgress(done, total, "Numerical Aptitude");
  }
  for (let i = aptFromBank.length; i < 5; i++) {
    const q = await getQuestion("Numerical Aptitude", _usedQuestions);
    questions.push({ ...q, id: questions.length+1, category:"Aptitude", domain });
    done++; if (onProgress) onProgress(done, total, "Numerical Aptitude");
    await new Promise(r => setTimeout(r, 80));
  }

  // ── 5 Reasoning questions ──────────────────────────────────────────────────
  const reasonFromBank = pickRandom(reasonBankPool, Math.min(5, reasonBankPool.length));
  for (const bq of reasonFromBank) {
    questions.push({ id: questions.length+1, text: bq.text, options: { A:bq.A, B:bq.B, C:bq.C, D:bq.D, correct:bq.correct, explanation:bq.explanation||"" }, category:"Reasoning", domain });
    done++; if (onProgress) onProgress(done, total, "Reasoning");
  }
  for (let i = reasonFromBank.length; i < 5; i++) {
    const q = await getQuestion("Reasoning", _usedQuestions);
    questions.push({ ...q, id: questions.length+1, category:"Reasoning", domain });
    done++; if (onProgress) onProgress(done, total, "Reasoning");
    await new Promise(r => setTimeout(r, 80));
  }

  // ── Shuffle and return 20 questions ───────────────────────────────────────
  // Keep domain questions interleaved with aptitude/reasoning for variety
  const domainQs  = questions.filter(q => q.category === "Domain");
  const aptQs     = questions.filter(q => q.category === "Aptitude");
  const reasonQs  = questions.filter(q => q.category === "Reasoning");
  const interleaved = [];
  for (let i = 0; i < 10; i++) {
    if (domainQs[i])  interleaved.push(domainQs[i]);
    if (i < 3 && aptQs[i])    interleaved.push(aptQs[i]);
    if (i < 2 && aptQs[i+3])  interleaved.push(aptQs[i+3]);
    if (i < 3 && reasonQs[i]) interleaved.push(reasonQs[i]);
    if (i < 2 && reasonQs[i+3]) interleaved.push(reasonQs[i+3]);
  }
  const final = [...new Set(interleaved)]; // deduplicate
  // If interleave produced fewer than 20 (shouldn't happen), just concat
  const all = final.length >= 20 ? final : [...domainQs, ...aptQs, ...reasonQs];
  return all.slice(0, 20).map((q, i) => ({ ...q, id: i+1 }));
}


// Evaluate selected MCQ option using AI
// Score per category: Domain=8pts, Aptitude=2pts, Reasoning=2pts
function getMaxScore(category) {
  if (!category) return 8;
  const c = category.toLowerCase();
  if (c.includes("aptitude") || c.includes("numerical")) return 2;
  if (c.includes("reasoning")) return 2;
  return 8; // Domain / Coding / Technical
}

async function evaluateAnswerWithAI(questionText, selectedOption, options, category) {
  if (!selectedOption) return { score: 0, feedback: "No option selected.", correct: false };

  const chosenLetter = selectedOption.charAt(0).toUpperCase();
  const correctLetter = (options?.correct || "A").toUpperCase();
  const isCorrectDirect = chosenLetter === correctLetter;
  const maxPts = getMaxScore(category);

  // Use direct comparison first (fast, no API call needed)
  if (options?.correct) {
    return {
      score: isCorrectDirect ? maxPts : 0,
      feedback: isCorrectDirect
        ? "Correct! " + (options?.explanation || "Well done.")
        : "Incorrect. The correct answer was " + correctLetter + ") " + (options?.[correctLetter] || "") + ". " + (options?.explanation || ""),
      correct: isCorrectDirect,
      maxScore: maxPts,
    };
  }

  // Fallback: ask AI if no correct answer stored
  try {
    const systemPrompt = `You are an expert evaluator. Return ONLY JSON: {"score": 0 or ${maxPts}, "feedback": "<one sentence>", "correct": true or false}`;
    const userPrompt = `Question (${category}): ${questionText}
Options: A) ${options?.A}  B) ${options?.B}  C) ${options?.C}  D) ${options?.D}
Candidate selected: ${chosenLetter}) ${options?.[chosenLetter]}
Is this correct? Score ${maxPts} if correct, 0 if wrong.`;
    const raw = await callClaude(systemPrompt, userPrompt, 150);
    const parsed = JSON.parse(raw);
    return { score: Math.max(0, Math.min(maxPts, parsed.score || 0)), feedback: parsed.feedback || "Evaluated.", correct: !!parsed.correct, maxScore: maxPts };
  } catch {
    return { score: isCorrectDirect ? maxPts : 0, feedback: isCorrectDirect ? "Correct!" : "Incorrect. Answer was " + correctLetter, correct: isCorrectDirect, maxScore: maxPts };
  }
}

async function evaluateAllAnswers(questions, answers) {
  const results = [];
  for (let i = 0; i < questions.length; i++) {
    const res = await evaluateAnswerWithAI(questions[i].text, answers[i] || "", questions[i].options, questions[i].category);
    results.push(res);
    await new Promise(r => setTimeout(r, 80));
  }
  return results;
}


// ─── QUESTION BANK DB — FIREBASE (shared across all admins) ──────────────────
const QB_DOC = "questionBank";   // Firestore document id
const QB_COL = "iirt";           // Firestore collection name

// Read the full bank from Firestore (returns {} on error)
async function loadQB() {
  // Try Firebase first, fall back to localStorage
  const data = await fbGet(QB_COL, QB_DOC);
  if (data) return data;
  try { return JSON.parse(localStorage.getItem("iirt_question_bank") || "{}"); } catch { return {}; }
}

// Write the full bank to Firestore
async function saveQB(bank) {
  // Save to Firebase AND localStorage as backup
  try { localStorage.setItem("iirt_question_bank", JSON.stringify(bank)); } catch {}
  await fbSet(QB_COL, QB_DOC, bank);
}

// Subscribe to real-time changes (calls callback whenever any admin edits bank)
function subscribeQB(callback) {
  // ALWAYS call callback immediately with localStorage so UI never hangs
  let calledOnce = false;
  const callOnce = (data) => {
    calledOnce = true;
    callback(data);
  };
  // Immediate local data — unblocks UI right away
  try {
    const local = JSON.parse(localStorage.getItem("iirt_question_bank") || "{}");
    callOnce(local); // call even if empty {} so bankLoading turns off
  } catch { callOnce({}); }

  // Safety timeout — if Firebase never responds in 4s, unblock with empty bank
  const timeout = setTimeout(() => {
    if (!calledOnce) callOnce({});
  }, 4000);

  // Firebase real-time listener for live sync between admins
  const unsub = fbSubscribe(QB_COL, QB_DOC, (data) => {
    clearTimeout(timeout);
    if (data) callback(data); // merge Firebase data over local
  });

  return () => { clearTimeout(timeout); unsub(); };
}

async function addQuestionToBank(category, domain, question) {
  const bank = await loadQB();
  const key = category === "Domain" ? domain : category;
  if (!bank[key]) bank[key] = [];
  const id = Date.now() + "_" + Math.random().toString(36).slice(2, 6);
  bank[key].push({ ...question, id, addedAt: new Date().toISOString() });
  await saveQB(bank);
  return id;
}

async function deleteQuestionFromBank(category, domain, id) {
  const bank = await loadQB();
  const key = category === "Domain" ? domain : category;
  if (bank[key]) bank[key] = bank[key].filter(q => q.id !== id);
  await saveQB(bank);
}

async function getQBStats() {
  const bank = await loadQB();
  const stats = {};
  Object.keys(bank).forEach(k => { stats[k] = (bank[k] || []).length; });
  return stats;
}

// Pick questions from bank for a candidate's test
// Returns 20 questions: 10 Domain + 5 Aptitude + 5 Reasoning
async function pickQuestionsFromBank(role) {
  const domain = getDomainKey(role);
  const bank = await loadQB();
  const domainPool = [...(bank[domain] || []), ...(bank["Coding"] || [])]; // merge Coding into domain pool
  const aptPool = bank["Aptitude"] || [];
  const reasonPool = bank["Reasoning"] || [];

  function pickRandom(arr, n) {
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, n);
  }

  const domainQs = pickRandom(domainPool, Math.min(10, domainPool.length));
  const aptQs = pickRandom(aptPool, Math.min(5, aptPool.length));
  const reasonQs = pickRandom(reasonPool, Math.min(5, reasonPool.length));

  // Interleave: D D A D D R D D A D D R D D A D D R D D + remaining
  const combined = [];
  const allDomain = [...domainQs];
  const allApt = [...aptQs];
  const allReason = [...reasonQs];

  while (combined.length < 20 && (allDomain.length || allApt.length || allReason.length)) {
    if (allDomain.length) combined.push({ ...allDomain.shift(), category: "Domain" });
    if (combined.length >= 20) break;
    if (allDomain.length) combined.push({ ...allDomain.shift(), category: "Domain" });
    if (combined.length >= 20) break;
    if (allApt.length) combined.push({ ...allApt.shift(), category: "Aptitude" });
    if (combined.length >= 20) break;
    if (allDomain.length) combined.push({ ...allDomain.shift(), category: "Domain" });
    if (combined.length >= 20) break;
    if (allReason.length) combined.push({ ...allReason.shift(), category: "Reasoning" });
    if (combined.length >= 20) break;
  }

  // Format to match expected question shape
  const formatted = combined.map((q, i) => ({
    id: i + 1,
    text: q.text,
    options: { A: q.A, B: q.B, C: q.C, D: q.D, correct: q.correct, explanation: q.explanation || "" },
    category: q.category,
    domain,
  }));

  // If bank doesn't have enough for 20, pad with AI-generated questions
  if (formatted.length < 20) {
    const needed = 20 - formatted.length;
    const usedSet = new Set(formatted.map(q => q.text?.substring(0, 60)));
    const padCats = [];
    // Fill remaining slots proportionally: domain first, then aptitude, then reasoning
    const domainNeeded = Math.max(0, 10 - domainQs.length);
    const aptNeeded    = Math.max(0, 5  - aptQs.length);
    const reasonNeeded = Math.max(0, 5  - reasonQs.length);
    for (let i = 0; i < domainNeeded && padCats.length < needed; i++) padCats.push("Domain");
    for (let i = 0; i < aptNeeded    && padCats.length < needed; i++) padCats.push("Numerical Aptitude");
    for (let i = 0; i < reasonNeeded && padCats.length < needed; i++) padCats.push("Reasoning");
    while (padCats.length < needed) padCats.push("Domain"); // fill remaining with domain

    for (let i = 0; i < padCats.length; i++) {
      try {
        const aiQ = await generateOneQuestion(padCats[i], domain, usedSet);
        formatted.push({ ...aiQ, id: formatted.length + 1 });
      } catch {
        // Static fallback if AI fails
        const pool = DOMAIN_QUESTIONS[domain] || DOMAIN_QUESTIONS[ALL_DOMAINS[0]];
        const pick = pool[Math.floor(Math.random() * pool.length)];
        formatted.push({
          id: formatted.length + 1,
          text: pick.text,
          options: { A: pick.A || "Option A", B: pick.B || "Option B", C: pick.C || "Option C", D: pick.D || "Option D", correct: pick.correct || "A", explanation: pick.explanation || "" },
          category: padCats[i],
          domain,
        });
      }
    }
  }

  return formatted.slice(0, 20).map((q, i) => ({ ...q, id: i + 1 }));
}

async function bankHasEnoughQuestions(role) {
  const domain = getDomainKey(role);
  const bank = await loadQB();
  return (bank[domain]?.length >= 10) && (bank["Aptitude"]?.length >= 5) && (bank["Reasoning"]?.length >= 5);
}

// ─── UTILITIES ────────────────────────────────────────────────────────────────
function generateCandidateId() {
  const prefix = "IIRT";
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── CANDIDATE DATABASE — FIREBASE (shared across all admins) ────────────────
const CAND_COL = "iirt";
const CAND_DOC = "candidates";

async function loadDB() {
  const data = await fbGet(CAND_COL, CAND_DOC);
  if (data) return data.list || [];
  try { return JSON.parse(localStorage.getItem("smhr_candidates_db") || "[]"); } catch { return []; }
}

// ── AMBASSADOR DB ── same Firestore collection, separate document ─────────────
const AMB_DOC = "ambassadors";

async function loadAmbDB() {
  const data = await fbGet(CAND_COL, AMB_DOC);
  if (data) return data.list || [];
  try { return JSON.parse(localStorage.getItem("iirt_ambassadors") || "[]"); } catch { return []; }
}

async function saveAmbDB(list) {
  try { localStorage.setItem("iirt_ambassadors", JSON.stringify(list)); } catch {}
  await fbSet(CAND_COL, AMB_DOC, { list });
}

function subscribeAmbassadors(callback) {
  // Always fire immediately from localStorage so UI never hangs
  try {
    const local = JSON.parse(localStorage.getItem("iirt_ambassadors") || "[]");
    callback(local);
  } catch { callback([]); }
  const timeout = setTimeout(() => callback([]), 5000);
  const unsub = fbSubscribe(CAND_COL, AMB_DOC, (data) => {
    clearTimeout(timeout);
    if (data) {
      // Sync latest from Firebase into localStorage
      try { localStorage.setItem("iirt_ambassadors", JSON.stringify(data.list || [])); } catch {}
      callback(data.list || []);
    }
  });
  return () => { clearTimeout(timeout); unsub(); };
}

async function saveDB(records) {
  try { localStorage.setItem("smhr_candidates_db", JSON.stringify(records)); } catch {}
  await fbSet(CAND_COL, CAND_DOC, { list: records });
}

// Real-time listener for candidate records
function subscribeCandidates(callback) {
  // Always fire immediately so UI never hangs waiting for Firebase
  try {
    const local = JSON.parse(localStorage.getItem("smhr_candidates_db") || "[]");
    callback(local); // call even if empty []
  } catch { callback([]); }
  const timeout = setTimeout(() => callback([]), 4000);
  const unsub = fbSubscribe(CAND_COL, CAND_DOC, (data) => {
    clearTimeout(timeout);
    if (data) callback(data.list || []);
  });
  return () => { clearTimeout(timeout); unsub(); };
}

function getReferralCode() {
  try { return localStorage.getItem("iirt_referral") || ""; } catch { return ""; }
}

async function addCandidateRecord(record) {
  const db = await loadDB();
  const existing = db.findIndex(r => r.candidateId === record.candidateId);
  if (existing >= 0) db[existing] = record;
  else db.push(record);
  await saveDB(db);
}

// Save terminated/malpractice record immediately when violation occurs
async function saveTerminatedRecord(candidate, terminationReason, extra = {}) {
  if (!candidate) return;
  const record = {
    candidateId: candidate.candidateId,
    name: candidate.form?.name || "—",
    email: candidate.form?.email || "—",
    phone: candidate.form?.phone || "—",
    degree: candidate.form?.degree || "—",
    college: candidate.form?.college || "—",
    role: candidate.form?.role || "—",
    department: candidate.form?.department || "—",
    yearOfStudy: candidate.form?.yearOfStudy || "—",
    yearOfPassout: candidate.form?.yearOfPassout || "—",
    experience: candidate.form?.yearOfStudy || "—",
    registeredAt: new Date().toISOString(),
    percentage: 0,
    questionsAnswered: extra.answeredCount || 0,
    aiScore: 0,
    faceViolations: extra.faceViolations || 0,
    tabWarnings: extra.tabWarnings || 0,
    voiceWarnings: extra.voiceWarnings || 0,
    terminationReason: terminationReason,
    malpractice: true,
    terminated: true,
    referral: getReferralCode(),
    externalDevices: "None Detected",
    plagiarism: "MALPRACTICE",
    status: "TERMINATED — " + (
      terminationReason === "MULTIPLE_FACES" ? "MULTIPLE FACES" :
      terminationReason === "VOICE_DETECTED"  ? "VOICE DETECTED"  :
      terminationReason === "TAB_SWITCH"      ? "TAB SWITCH"      : "VIOLATION"
    ),
    qa: (extra.questions || []).map((q, i) => ({
      category: q.category || "Domain",
      question: q.text || "",
      selectedOption: (extra.answers || [])[i] || "—",
      correctOption: q.options?.correct || "—",
      isCorrect: (extra.answers || [])[i] === q.options?.correct,
      score: (extra.answers || [])[i] === q.options?.correct ? 10 : 0,
      feedback: "",
    })),
  };
  // Persist ban flag to localStorage so refresh doesn't allow re-entry
  try {
    localStorage.setItem("iirt_banned_" + candidate.candidateId, JSON.stringify({
      reason: terminationReason, at: new Date().toISOString()
    }));
  } catch {}
  await addCandidateRecord(record);
}

// Check if candidateId is permanently banned
function isCandidateBanned(candidateId) {
  try {
    return !!localStorage.getItem("iirt_banned_" + candidateId);
  } catch { return false; }
}
function getBanInfo(candidateId) {
  try {
    const s = localStorage.getItem("iirt_banned_" + candidateId);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

function buildExcelWorkbook(records) {
  // Dynamically load XLSX from CDN window object
  const XLSXLib = window.XLSX;
  if (!XLSXLib) return null;

  const wb = XLSXLib.utils.book_new();

  // ── Sheet 1: Candidates Summary ──
  const summaryHeaders = [
    "Candidate ID", "Full Name", "Email", "Phone", "Degree", "College",
    "Role Applied", "Experience", "Registration Date",
    "Score (%)", "Questions Answered", "AI Eval Score (%)",
    "Face Violations", "Tab Warnings", "Plagiarism",
    "Referred By", "Internship Status", "Final Status"
  ];
  const summaryRows = records.map(r => [
    r.candidateId, r.name, r.email, r.phone, r.degree, r.college,
    r.role, r.experience, r.registeredAt,
    r.percentage, r.questionsAnswered, r.aiScore,
    r.faceViolations, r.tabWarnings, r.plagiarism,
    r.referral || "—", r.internshipStatus || "—", r.status
  ]);
  const summaryData = [summaryHeaders, ...summaryRows];
  const ws1 = XLSXLib.utils.aoa_to_sheet(summaryData);
  ws1["!cols"] = [18,20,28,14,22,22,28,20,20,10,10,10,10,10,14,12].map(w => ({ wch: w }));
  XLSXLib.utils.book_append_sheet(wb, ws1, "Candidates Summary");

  // ── Sheet 2: Detailed Q&A ──
  const qaHeaders = ["Candidate ID", "Name", "Role", "Q#", "Category", "Question", "Selected Option", "Correct Option", "Is Correct", "AI Score /10"];
  const qaRows = [];
  records.forEach(r => {
    (r.qa || []).forEach((item, i) => {
      qaRows.push([r.candidateId, r.name, r.role, `Q${i + 1}`, item.category, item.question, item.selectedOption || item.answer || "—", item.correctOption || "—", item.isCorrect ? "Yes" : "No", item.score]);
    });
  });
  const ws2 = XLSXLib.utils.aoa_to_sheet([qaHeaders, ...qaRows]);
  ws2["!cols"] = [18,20,24,5,12,55,60,10].map(w => ({ wch: w }));
  XLSXLib.utils.book_append_sheet(wb, ws2, "Q&A Details");

  // ── Sheet 3: Security Log ──
  const secHeaders = ["Candidate ID", "Name", "Face Violations", "Tab Warnings", "Voice Warnings", "Termination Reason", "Malpractice", "Plagiarism Result", "Exam Terminated", "Final Status"];
  const secRows = records.map(r => [
    r.candidateId, r.name, r.faceViolations || 0, r.tabWarnings || 0,
    r.voiceWarnings || 0,
    r.terminationReason || "—",
    r.malpractice ? "YES" : "NO",
    r.plagiarism || "Pass",
    r.terminated ? "YES" : "NO", r.status
  ]);
  const ws3 = XLSXLib.utils.aoa_to_sheet([secHeaders, ...secRows]);
  ws3["!cols"] = [18,20,14,12,16,16,14,12].map(w => ({ wch: w }));
  XLSXLib.utils.book_append_sheet(wb, ws3, "Security Log");

  // ── Sheet 4: Statistics ──
  const total = records.length;
  const freeCount = records.filter(r => r.status === "FREE INTERNSHIP").length;
  const discountCount = records.filter(r => r.status === "50% DISCOUNT").length;
  const notEligibleCount = records.filter(r => r.status === "NOT ELIGIBLE").length;
  const statsData = [
    ["IIRT — INTERNASTRA INTERNSHIP READINESS TEST — STATISTICS"],
    [],
    ["Metric", "Value"],
    ["Total Candidates", total],
    ["Free Internship (90%+)", freeCount],
    ["50% Discount (50–89%)", discountCount],
    ["Not Eligible (Below 50%)", notEligibleCount],
    ["Free Internship Rate (%)", total ? Math.round((freeCount / total) * 100) : 0],
    ["Average Score (%)", records.length ? Math.round(records.reduce((s, r) => s + (r.percentage || 0), 0) / records.length) : 0],
    ["Generated On", new Date().toLocaleString("en-IN")],
  ];
  const ws4 = XLSXLib.utils.aoa_to_sheet(statsData);
  ws4["!cols"] = [{ wch: 28 }, { wch: 20 }];
  XLSXLib.utils.book_append_sheet(wb, ws4, "Statistics");

  return wb;
}

function downloadExcel(records) {
  const XLSXLib = window.XLSX;
  if (!XLSXLib) { alert("Excel library not loaded yet. Please wait a moment and try again."); return; }
  const wb = buildExcelWorkbook(records);
  if (!wb) return;
  const filename = `IIRT_Candidates_${new Date().toISOString().slice(0,10)}.xlsx`;
  XLSXLib.writeFile(wb, filename);
}

// ─── STYLE CONSTANTS ─────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@300;400;500;600;700&family=Share+Tech+Mono&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #020812;
    --bg2: #060f1e;
    --surface: #0a1628;
    --surface2: #0f1f35;
    --border: #1a3a5c;
    --accent: #00d4ff;
    --accent2: #0088cc;
    --accent3: #00ff88;
    --danger: #ff3b5c;
    --warn: #ffb020;
    --text: #e0f0ff;
    --text2: #8ab0cc;
    --text3: #4a7a9b;
    --glow: 0 0 20px rgba(0,212,255,0.3);
    --glow-strong: 0 0 40px rgba(0,212,255,0.5);
  }

  /* ── LIGHT THEME ── */
  [data-theme="light"] {
    --bg: #f0f4f8;
    --bg2: #e2e8f0;
    --surface: #ffffff;
    --surface2: #eaf0f7;
    --border: #b0c8e0;
    --accent: #0077cc;
    --accent2: #005fa3;
    --accent3: #00994d;
    --danger: #d93025;
    --warn: #c07000;
    --text: #0d1b2a;
    --text2: #2d5070;
    --text3: #5a82a0;
    --glow: 0 0 16px rgba(0,119,204,0.15);
    --glow-strong: 0 0 30px rgba(0,119,204,0.25);
    --cyan: #0077cc;
  }
  [data-theme="light"] body::before { display: none; }
  [data-theme="light"] .top-nav { background: #ffffffee !important; border-bottom: 1px solid #b0c8e0 !important; backdrop-filter: blur(10px); }
  [data-theme="light"] .page-bg-grid { opacity: 0.06 !important; }
  [data-theme="light"] .console { background: #f8fafc !important; border-color: #b0c8e0 !important; }
  [data-theme="light"] .console-line { color: #2d5070 !important; }
  [data-theme="light"] .console-line.ok { color: #006633 !important; }
  [data-theme="light"] .console-line.err { color: #cc0000 !important; }
  [data-theme="light"] .console-line.warn { color: #805000 !important; }
  [data-theme="light"] .card { box-shadow: 0 2px 12px rgba(0,0,0,0.08) !important; }
  [data-theme="light"] input, [data-theme="light"] select, [data-theme="light"] textarea {
    background: #ffffff !important;
    color: #0d1b2a !important;
    border-color: #b0c8e0 !important;
  }
  [data-theme="light"] .btn-primary { background: linear-gradient(135deg,#0066bb,#0099dd) !important; color: #fff !important; }
  [data-theme="light"] .ticker-wrap { background: #e2e8f0 !important; border-color: #b0c8e0 !important; }
  [data-theme="light"] .modal-box { background: #ffffff !important; }
  [data-theme="light"] .modal-overlay { background: rgba(0,0,0,0.35) !important; }

  body {
    font-family: 'Rajdhani', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* Scanline overlay — dark mode only */
  body::before {
    content: '';
    position: fixed;
    inset: 0;
    background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,212,255,0.015) 2px, rgba(0,212,255,0.015) 4px);
    pointer-events: none;
    z-index: 9999;
  }

  .app { min-height: 100vh; background: var(--bg); transition: background 0.3s, color 0.3s; }
  .page { animation: pageReveal 0.5s cubic-bezier(0.22,1,0.36,1) forwards; position: relative; z-index: 1; transition: background 0.3s, color 0.3s; }
  *, *:not(.status-dot) { transition: background-color 0.25s, border-color 0.25s, color 0.25s; }

  /* BUTTONS */
  .btn {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 14px 36px;
    border: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s;
    clip-path: polygon(12px 0%, 100% 0%, calc(100% - 12px) 100%, 0% 100%);
  }
  .btn-primary {
    background: linear-gradient(135deg, var(--accent2), var(--accent));
    color: #000;
    box-shadow: var(--glow);
  }
  .btn-primary:hover { box-shadow: var(--glow-strong); transform: translateY(-2px); }
  .btn-danger { background: linear-gradient(135deg, #cc0022, var(--danger)); color: #fff; }
  .btn-outline {
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent);
  }
  .btn-outline:hover { background: rgba(0,212,255,0.1); }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none !important; }

  /* INPUTS */
  .input-group { margin-bottom: 20px; }
  .input-group label {
    display: block;
    font-size: 0.8rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 8px;
    font-weight: 600;
  }
  .input-group input, .input-group select, .input-group textarea {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 0;
    color: var(--text);
    padding: 12px 16px;
    font-family: 'Rajdhani', sans-serif;
    font-size: 1rem;
    font-weight: 500;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  }
  .input-group input:focus, .input-group select:focus, .input-group textarea:focus {
    border-color: var(--accent);
    box-shadow: 0 0 15px rgba(0,212,255,0.2);
  }
  .input-group input::placeholder { color: var(--text3); }

  /* CARDS */
  .card {
    background: var(--surface);
    border: 1px solid var(--border);
    padding: 32px;
    position: relative;
  }
  .card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
  }

  /* BADGE */
  .badge {
    display: inline-block;
    padding: 4px 12px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    border: 1px solid;
  }
  .badge-accent { color: var(--accent); border-color: var(--accent); background: rgba(0,212,255,0.1); }
  .badge-green { color: var(--accent3); border-color: var(--accent3); background: rgba(0,255,136,0.1); }
  .badge-danger { color: var(--danger); border-color: var(--danger); background: rgba(255,59,92,0.1); }
  .badge-warn { color: var(--warn); border-color: var(--warn); background: rgba(255,176,32,0.1); }

  /* GRID */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; }
  @media (max-width: 768px) { .grid-2, .grid-3 { grid-template-columns: 1fr; } }

  /* PROGRESS BAR */
  .progress-bar {
    height: 4px;
    background: var(--surface2);
    position: relative;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--accent2), var(--accent));
    transition: width 0.5s ease;
    box-shadow: 0 0 10px var(--accent);
  }

  /* PULSE ANIMATION */
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-30px); } to { opacity:1; transform:translateX(0); } }
  @keyframes glow-pulse { 0%,100% { box-shadow: 0 0 10px rgba(0,212,255,0.3); } 50% { box-shadow: 0 0 30px rgba(0,212,255,0.7); } }
  @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  @keyframes countDown { from { stroke-dashoffset: 0; } to { stroke-dashoffset: 283; } }
  @keyframes borderRun {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  @keyframes pageReveal { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
  @keyframes shake {
    0%,100%{transform:translateX(0)}
    20%{transform:translateX(-8px)} 40%{transform:translateX(8px)}
    60%{transform:translateX(-5px)} 80%{transform:translateX(5px)}
  }
  @keyframes scanline {
    0% { top: -2px; }
    100% { top: 100%; }
  }
  @keyframes scanlineV {
    0% { left: -1px; }
    100% { left: 100%; }
  }
  @keyframes splashScan {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }
  @keyframes particleDrift {
    0%,100% { transform: translateY(0) translateX(0); opacity: 0.6; }
    33% { transform: translateY(-20px) translateX(10px); opacity: 1; }
    66% { transform: translateY(10px) translateX(-10px); opacity: 0.4; }
  }


  .animate-fade { animation: fadeIn 0.6s ease forwards; }
  .animate-slide { animation: slideIn 0.5s ease forwards; }
  .animate-float { animation: float 3s ease-in-out infinite; }
  .animate-pulse { animation: pulse 1.5s infinite; }
  .animate-glow { animation: glow-pulse 2s infinite; }

  /* PAGE TRANSITION OVERLAY */
    @keyframes wipeIn {
    0% { clip-path: inset(0 100% 0 0); }
    100% { clip-path: inset(0 0% 0 0); }
  }
  @keyframes glowIn {
    0% { opacity:0; filter:brightness(2) blur(6px); transform:scale(1.02); }
    100% { opacity:1; filter:brightness(1) blur(0); transform:scale(1); }
  }
  .page-enter {
    animation: pageReveal 0.55s cubic-bezier(0.22,1,0.36,1) forwards;
  }
  /* Hexagon grid background pattern on pages */
  .page-bg-grid {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(0,212,255,0.025) 1px,transparent 1px),
      linear-gradient(90deg,rgba(0,212,255,0.025) 1px,transparent 1px);
    background-size: 48px 48px;
  }
  /* Animated corner brackets on all pages */
  .page-corner {
    position: fixed;
    width: 48px; height: 48px;
    pointer-events: none;
    z-index: 50;
    border-color: rgba(0,212,255,0.25);
    border-style: solid;
    animation: glow-pulse 3s infinite;
  }
  .page-corner-tl { top:14px;left:14px; border-width:2px 0 0 2px; }
  .page-corner-tr { top:14px;right:14px; border-width:2px 2px 0 0; animation-delay:0.3s; }
  .page-corner-bl { bottom:14px;left:14px; border-width:0 0 2px 2px; animation-delay:0.6s; }
  .page-corner-br { bottom:14px;right:14px; border-width:0 2px 2px 0; animation-delay:0.9s; }
  /* Floating particle field */
  .particle-field {
    position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
  }
  .particle {
    position: absolute; border-radius: 50%;
    animation: particleDrift 8s ease-in-out infinite;
  }
  /* Scan line sweep on page entry */
  .scan-sweep {
    position: fixed; left:0; right:0; height:2px;
    background: linear-gradient(90deg,transparent,rgba(0,212,255,0.6),transparent);
    pointer-events:none; z-index:60;
    animation: scanline 1.2s linear forwards;
  }
  /* Status dot */
  .dot-green { background: var(--accent3); box-shadow: 0 0 6px var(--accent3); }
  .dot-red   { background: var(--danger);  box-shadow: 0 0 6px var(--danger); }
  .dot-yellow{ background: var(--warn);    box-shadow: 0 0 6px var(--warn); }
  .dot-blue  { background: var(--accent);  box-shadow: 0 0 6px var(--accent); }
  
  /* TERMINATED PAGE */
  @keyframes shakeX {
    0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)}
    60%{transform:translateX(-5px)} 80%{transform:translateX(5px)}
  }
  .shake { animation: shakeX 0.5s ease; }

  /* HEADER NAV */
  .top-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 40px;
    border-bottom: 1px solid var(--border);
    background: rgba(2,8,18,0.95);
    backdrop-filter: blur(20px);
    position: sticky;
    top: 0;
    z-index: 100;
    overflow: visible;
  }
  .logo {
    display: flex;
    align-items: center;
    line-height: 1;
  }
  .logo svg { filter: drop-shadow(0 0 8px rgba(0,212,255,0.2)); transition: filter 0.3s; }
  .logo svg:hover { filter: drop-shadow(0 0 16px rgba(0,212,255,0.45)); }

  /* TICKER */
  .ticker-wrap {
    background: rgba(0,212,255,0.05);
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    overflow: hidden;
    padding: 8px 0;
  }
  .ticker-inner {
    display: flex;
    white-space: nowrap;
    animation: ticker 25s linear infinite;
  }
  .ticker-item {
    font-family: 'Share Tech Mono', monospace;
    font-size: 0.75rem;
    color: var(--accent);
    padding: 0 40px;
    letter-spacing: 1px;
  }
  .ticker-sep { color: var(--text3); }

  /* SECTION TITLE */
  .section-title {
    font-family: 'Orbitron', monospace;
    font-size: 2rem;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 4px;
    color: var(--text);
    line-height: 1.2;
  }
  .section-title span { color: var(--accent); }
  .section-subtitle {
    font-size: 1.1rem;
    color: var(--text2);
    font-weight: 400;
    margin-top: 10px;
    letter-spacing: 1px;
  }

  /* STEP INDICATOR */
  .step-indicator {
    display: flex;
    align-items: center;
    gap: 0;
    margin-bottom: 40px;
  }
  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
  }
  .step::after {
    content: '';
    position: absolute;
    top: 20px;
    left: 50%;
    width: 100%;
    height: 2px;
    background: var(--border);
    z-index: 0;
  }
  .step:last-child::after { display: none; }
  .step.active::after { background: var(--accent); }
  .step-num {
    width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Orbitron', monospace;
    font-weight: 700;
    font-size: 0.85rem;
    border: 2px solid var(--border);
    background: var(--bg);
    color: var(--text3);
    position: relative; z-index: 1;
    clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
    transition: all 0.3s;
  }
  .step.active .step-num {
    border-color: var(--accent);
    background: rgba(0,212,255,0.15);
    color: var(--accent);
    box-shadow: var(--glow);
  }
  .step.done .step-num {
    border-color: var(--accent3);
    background: rgba(0,255,136,0.15);
    color: var(--accent3);
  }
  .step-label {
    font-size: 0.65rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--text3);
    margin-top: 8px;
    font-weight: 600;
  }
  .step.active .step-label { color: var(--accent); }
  .step.done .step-label { color: var(--accent3); }

  /* MODAL */
  .modal-overlay {
    position: fixed; inset: 0;
    background: rgba(2,8,18,0.92);
    backdrop-filter: blur(10px);
    z-index: 9998;
    display: flex; align-items: center; justify-content: center;
    animation: fadeIn 0.2s ease;
  }
  .modal-box {
    background: var(--surface);
    border: 1px solid var(--danger);
    padding: 40px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 0 60px rgba(255,59,92,0.3);
    text-align: center;
    animation: fadeIn 0.3s ease;
  }
  .modal-box.warn { border-color: var(--warn); box-shadow: 0 0 60px rgba(255,176,32,0.3); }

  /* RECORDING INDICATOR */
  .rec-dot {
    width: 10px; height: 10px;
    border-radius: 50%;
    background: var(--danger);
    animation: pulse 1s infinite;
    display: inline-block;
  }

  /* CAMERA BOX */
  .camera-box {
    background: #000;
    border: 2px solid var(--border);
    position: relative;
    overflow: hidden;
    aspect-ratio: 4/3;
  }
  .camera-box video { width: 100%; height: 100%; object-fit: cover; }
  .camera-overlay {
    position: absolute; inset: 0;
    border: 2px solid rgba(0,212,255,0.3);
    pointer-events: none;
  }
  .camera-corner {
    position: absolute;
    width: 20px; height: 20px;
    border-color: var(--accent);
    border-style: solid;
  }
  .tl { top:8px;left:8px; border-width: 2px 0 0 2px; }
  .tr { top:8px;right:8px; border-width: 2px 2px 0 0; }
  .bl { bottom:8px;left:8px; border-width: 0 0 2px 2px; }
  .br { bottom:8px;right:8px; border-width: 0 2px 2px 0; }


  /* ANSWER BOX (kept for results review) */
  .answer-display {
    background: var(--bg);
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    padding: 16px 20px;
    min-height: 80px;
    font-family: 'Rajdhani', sans-serif;
    font-size: 1rem;
    color: var(--text);
    position: relative;
    user-select: none;
  }
  .answer-display::after {
    content: 'SELECTED ANSWER';
    position: absolute;
    top: 8px; right: 12px;
    font-size: 0.6rem;
    letter-spacing: 2px;
    color: var(--text3);
    font-family: 'Share Tech Mono', monospace;
  }

  /* STATUS ROW */
  .status-row {
    display: flex; gap: 12px; flex-wrap: wrap;
    margin-bottom: 20px;
  }
  .status-chip {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 16px;
    background: var(--surface2);
    border: 1px solid var(--border);
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-family: 'Share Tech Mono', monospace;
  }
  .status-dot { width: 8px; height: 8px; border-radius: 50%; }
  .dot-green { background: var(--accent3); box-shadow: 0 0 8px var(--accent3); }
  .dot-red { background: var(--danger); animation: pulse 1s infinite; }
  .dot-yellow { background: var(--warn); animation: pulse 1s infinite; }
  .dot-blue { background: var(--accent); box-shadow: 0 0 8px var(--accent); }

  /* HERO BG */
  .hero-bg {
    position: relative;
    overflow: hidden;
  }
  .hero-bg::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse at 20% 50%, rgba(0,136,204,0.08) 0%, transparent 60%),
      radial-gradient(ellipse at 80% 20%, rgba(0,212,255,0.06) 0%, transparent 50%),
      radial-gradient(ellipse at 50% 80%, rgba(0,255,136,0.04) 0%, transparent 40%);
    pointer-events: none;
  }
  .grid-dots {
    position: absolute; inset: 0;
    background-image: radial-gradient(rgba(0,212,255,0.12) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
  }

  /* RESULT CARD */
  .result-score {
    font-family: 'Orbitron', monospace;
    font-size: 5rem;
    font-weight: 900;
    line-height: 1;
  }
  .result-score.selected { color: var(--accent3); text-shadow: 0 0 40px rgba(0,255,136,0.5); }
  .result-score.rejected { color: var(--danger); text-shadow: 0 0 40px rgba(255,59,92,0.5); }

  /* FEATURE CARD */
  .feature-card {
    padding: 28px;
    background: var(--surface);
    border: 1px solid var(--border);
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
  }
  .feature-card:hover {
    border-color: var(--accent);
    transform: translateY(-4px);
    box-shadow: var(--glow);
  }
  .feature-icon {
    font-size: 2rem;
    margin-bottom: 16px;
    display: block;
  }
  .feature-card h3 {
    font-family: 'Orbitron', monospace;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 2px;
    color: var(--accent);
    text-transform: uppercase;
    margin-bottom: 10px;
  }
  .feature-card p { font-size: 0.95rem; color: var(--text2); line-height: 1.6; }

  /* STAT */
  .stat-num {
    font-family: 'Orbitron', monospace;
    font-size: 2.5rem;
    font-weight: 900;
    color: var(--accent);
    text-shadow: var(--glow);
  }
  .stat-label { font-size: 0.8rem; letter-spacing: 2px; text-transform: uppercase; color: var(--text3); margin-top: 4px; }

  /* CONSOLE LOG */
  .console {
    background: #000;
    border: 1px solid var(--border);
    padding: 16px;
    font-family: 'Share Tech Mono', monospace;
    font-size: 0.8rem;
    max-height: 150px;
    overflow-y: auto;
  }
  .console-line { padding: 2px 0; color: var(--text3); }
  .console-line.ok { color: var(--accent3); }
  .console-line.warn { color: var(--warn); }
  .console-line.err { color: var(--danger); }
  .console-line.info { color: var(--accent); }


  /* LOADING */
  .spinner {
    width: 40px; height: 40px;
    border: 3px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  /* PAGE TRANSITIONS */
  .page { animation: fadeIn 0.4s ease; }

  /* MOBILE */
  @media (max-width: 600px) {
    .top-nav { padding: 12px 20px; }
    .section-title { font-size: 1.4rem; }
  }

  /* ── LANDING PAGE EXCLUSIVE STYLES ── */

  /* Particle canvas sits behind everything on the landing hero */
  .landing-canvas {
    position: absolute; inset: 0;
    pointer-events: none; z-index: 0;
  }

  /* Hero content above canvas */
  .hero-content { position: relative; z-index: 2; }

  /* Glowing orb blobs */
  .hero-orb {
    position: absolute; border-radius: 50%;
    filter: blur(80px); pointer-events: none; z-index: 0;
    animation: orb-drift 12s ease-in-out infinite alternate;
  }
  @keyframes orb-drift {
    0%   { transform: translate(0,0) scale(1); }
    50%  { transform: translate(30px,-20px) scale(1.08); }
    100% { transform: translate(-20px,15px) scale(0.96); }
  }

  /* Feature cards — enhanced */
  .feature-card-v2 {
    padding: 28px 24px; background: var(--surface);
    border: 1px solid var(--border);
    position: relative; overflow: hidden;
    transition: transform 0.3s, border-color 0.3s, box-shadow 0.3s;
    cursor: default;
  }
  .feature-card-v2::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--accent), var(--accent3));
    transform: scaleX(0); transform-origin: left;
    transition: transform 0.35s ease;
  }
  .feature-card-v2:hover { transform: translateY(-6px); border-color: var(--accent); box-shadow: 0 12px 40px rgba(0,212,255,0.12); }
  .feature-card-v2:hover::before { transform: scaleX(1); }
  .feature-card-v2 .fc-icon {
    font-size: 2.2rem; margin-bottom: 16px; display: block;
    transition: transform 0.3s;
  }
  .feature-card-v2:hover .fc-icon { transform: scale(1.15) rotate(-5deg); }
  .feature-card-v2 h3 {
    font-family: 'Orbitron', monospace; font-size: 0.82rem;
    font-weight: 700; letter-spacing: 2px; color: var(--accent);
    text-transform: uppercase; margin-bottom: 10px;
  }
  .feature-card-v2 p { font-size: 0.93rem; color: var(--text2); line-height: 1.65; }

  /* Animated counter number */
  .stat-card {
    text-align: center; padding: 20px 28px;
    background: var(--surface); border: 1px solid var(--border);
    position: relative; overflow: hidden;
    transition: border-color 0.3s, transform 0.3s;
  }
  .stat-card:hover { border-color: var(--accent); transform: translateY(-3px); }
  .stat-card::after {
    content: '';
    position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    animation: shimmer 2.5s infinite;
  }
  @keyframes shimmer {
    0%   { opacity: 0.3; transform: scaleX(0.4); }
    50%  { opacity: 1;   transform: scaleX(1); }
    100% { opacity: 0.3; transform: scaleX(0.4); }
  }

  /* Floating badge */
  .float-badge {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 20px; border: 1px solid rgba(0,212,255,0.3);
    background: rgba(0,212,255,0.05);
    font-family: 'Share Tech Mono', monospace; font-size: 0.72rem;
    letter-spacing: 2px; color: var(--accent); margin-bottom: 24px;
    animation: badge-float 3s ease-in-out infinite;
  }
  @keyframes badge-float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-4px); }
  }

  /* Pulse ring behind CTA button */
  .btn-pulse-wrap { position: relative; display: inline-block; }
  .btn-pulse-wrap::before, .btn-pulse-wrap::after {
    content: '';
    position: absolute; inset: -6px; border-radius: 2px;
    border: 1px solid var(--accent);
    animation: btn-pulse 2s ease-out infinite;
  }
  .btn-pulse-wrap::after { animation-delay: 1s; }
  @keyframes btn-pulse {
    0%   { opacity: 0.6; transform: scale(1); }
    100% { opacity: 0;   transform: scale(1.18); }
  }

  /* Step card for how-it-works */
  .step-card {
    display: flex; gap: 20px; align-items: flex-start;
    padding: 20px 24px; background: var(--surface);
    border: 1px solid var(--border); margin-bottom: 14px;
    transition: border-color 0.3s, transform 0.3s;
    position: relative; overflow: hidden;
  }
  .step-card:hover { border-color: var(--accent); transform: translateX(6px); }
  .step-card::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
    background: linear-gradient(180deg, var(--accent), var(--accent3));
    transform: scaleY(0); transform-origin: top; transition: transform 0.35s;
  }
  .step-card:hover::before { transform: scaleY(1); }
  .step-num {
    font-family: 'Orbitron', monospace; font-size: 2rem;
    font-weight: 900; color: var(--accent); opacity: 0.25;
    min-width: 56px; line-height: 1; flex-shrink: 0;
    transition: opacity 0.3s;
  }
  .step-card:hover .step-num { opacity: 0.7; }

  /* Eligibility cards */
  .elig-card {
    padding: 28px 24px; border: 2px solid var(--border);
    text-align: center; position: relative; overflow: hidden;
    transition: transform 0.3s, border-color 0.3s, box-shadow 0.3s;
  }
  .elig-card:hover { transform: translateY(-5px); }
`;

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

// ── GRAPHICAL PAGE DECORATIONS ── rendered on every page ──────────────────
function PageDecorations() {
  return (
    <>
      {/* Dark grid background */}
      <div style={{
        position:"fixed",inset:0,pointerEvents:"none",zIndex:0,
        backgroundImage:"linear-gradient(rgba(0,212,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,212,255,0.03) 1px,transparent 1px)",
        backgroundSize:"56px 56px",
      }}/>

      {/* Radial vignette glow center */}
      <div style={{
        position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
        width:"70vw",height:"70vh",
        background:"radial-gradient(ellipse,rgba(0,212,255,0.04) 0%,transparent 65%)",
        pointerEvents:"none",zIndex:0,
      }}/>

      {/* Animated corner brackets */}
      {[
        {top:12,left:12,bw:"2px 0 0 2px"},
        {top:12,right:12,bw:"2px 2px 0 0"},
        {bottom:12,left:12,bw:"0 0 2px 2px"},
        {bottom:12,right:12,bw:"0 2px 2px 0"},
      ].map((pos,i) => (
        <div key={i} style={{
          position:"fixed",width:44,height:44,
          borderColor:"rgba(0,212,255,0.3)",borderStyle:"solid",borderWidth:pos.bw,
          ...pos,pointerEvents:"none",zIndex:50,
          animation:"glow-pulse 3s infinite",
          animationDelay:(i*0.4)+"s",
        }}/>
      ))}

      {/* Top edge glow line */}
      <div style={{
        position:"fixed",top:0,left:0,right:0,height:"1px",
        background:"linear-gradient(90deg,transparent 0%,rgba(0,212,255,0.4) 30%,rgba(0,255,136,0.3) 60%,transparent 100%)",
        pointerEvents:"none",zIndex:50,
      }}/>

      {/* Bottom edge glow line */}
      <div style={{
        position:"fixed",bottom:0,left:0,right:0,height:"1px",
        background:"linear-gradient(90deg,transparent 0%,rgba(26,109,212,0.35) 50%,transparent 100%)",
        pointerEvents:"none",zIndex:50,
      }}/>

      {/* Left vertical accent */}
      <div style={{
        position:"fixed",top:0,left:0,width:"2px",height:"100%",
        background:"linear-gradient(180deg,transparent,rgba(0,212,255,0.15) 30%,rgba(0,212,255,0.15) 70%,transparent)",
        pointerEvents:"none",zIndex:50,
      }}/>

      {/* Right vertical accent */}
      <div style={{
        position:"fixed",top:0,right:0,width:"2px",height:"100%",
        background:"linear-gradient(180deg,transparent,rgba(26,109,212,0.12) 30%,rgba(26,109,212,0.12) 70%,transparent)",
        pointerEvents:"none",zIndex:50,
      }}/>

      {/* Floating orbital nodes — like the logo satellite dots */}
      {[
        {x:8,y:15,s:3,c:"rgba(0,212,255,0.45)",d:7,delay:0},
        {x:92,y:20,s:2,c:"rgba(0,255,136,0.35)",d:9,delay:1.2},
        {x:5,y:75,s:2.5,c:"rgba(26,109,212,0.5)",d:8,delay:0.6},
        {x:95,y:80,s:2,c:"rgba(0,212,255,0.35)",d:6,delay:2},
        {x:50,y:5,s:1.5,c:"rgba(0,255,136,0.3)",d:10,delay:0.3},
        {x:15,y:90,s:1.5,c:"rgba(0,212,255,0.25)",d:7,delay:1.8},
        {x:85,y:10,s:2,c:"rgba(26,109,212,0.4)",d:8,delay:0.9},
        {x:88,y:55,s:1.5,c:"rgba(0,212,255,0.3)",d:11,delay:2.4},
      ].map((p,i) => (
        <div key={i} style={{
          position:"fixed",
          left:p.x+"%",top:p.y+"%",
          width:p.s*2,height:p.s*2,
          borderRadius:"50%",
          background:p.c,
          boxShadow:`0 0 ${p.s*5}px ${p.c}`,
          pointerEvents:"none",zIndex:1,
          animation:`particleDrift ${p.d}s ease-in-out ${p.delay}s infinite`,
        }}/>
      ))}

      {/* Scan line sweep — faint, one pass */}
      <div style={{
        position:"fixed",left:0,right:0,height:"1px",
        background:"linear-gradient(90deg,transparent,rgba(0,212,255,0.25),transparent)",
        pointerEvents:"none",zIndex:60,
        animation:"scanline 4s linear infinite",
      }}/>
    </>
  );
}

// ── PAGE WRAPPER — adds entry animation + decorations ──────────────────────
function PageWrapper({ children, key: _k }) {
  return (
    <div className="page page-enter" style={{ position: "relative", zIndex: 1 }}>
      <PageDecorations />
      {children}
    </div>
  );
}

// Module-level callbacks set by App — Nav reads these directly (no prop drilling)
const _navCb = { toggleTheme: () => {}, theme: "dark", goHome: () => {} };

function Nav({ page, hideHome }) {
  const th = _navCb.theme;
  return (
    <nav className="top-nav">
      <div className="logo" style={{display:"flex",alignItems:"center",overflow:"visible"}}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -4 310 60" width="308" height="48" style={{display:"block", overflow:"visible"}}>
          <defs>
            <filter id="navglow"><feGaussianBlur stdDeviation="1.8" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
            <linearGradient id="navgrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1a6fd4"/>
              <stop offset="100%" stopColor="#00d4ff"/>
            </linearGradient>
          </defs>
          {/* === CONNECTION ARC through all 4 satellites === */}
          <ellipse cx="40" cy="24" rx="30" ry="22" fill="none" stroke="url(#navgrad)" strokeWidth="1.2" opacity="0.5" strokeDasharray="4 3"/>
          {/* === CONNECTOR LINES center → satellites === */}
          <line x1="32" y1="14" x2="19" y2="8"  stroke="#1a6fd4" strokeWidth="1.1" opacity="0.7"/>
          <line x1="48" y1="14" x2="61" y2="8"  stroke="#1a6fd4" strokeWidth="1.1" opacity="0.7"/>
          <line x1="30" y1="32" x2="17" y2="40" stroke="#1a6fd4" strokeWidth="1.1" opacity="0.7"/>
          <line x1="50" y1="32" x2="62" y2="40" stroke="#1a6fd4" strokeWidth="1.1" opacity="0.7"/>
          {/* === CENTER: Person in circle === */}
          <circle cx="40" cy="24" r="15" fill="none" stroke="url(#navgrad)" strokeWidth="1.8"/>
          {/* head */}
          <circle cx="40" cy="17.5" r="4.5" fill="none" stroke="#1a6fd4" strokeWidth="1.5"/>
          {/* body/shoulders */}
          <path d="M32 34 C32 27 37 26 40 26 C43 26 48 27 48 34" fill="none" stroke="#1a6fd4" strokeWidth="1.5"/>
          {/* collar left */}
          <line x1="38" y1="26" x2="36" y2="30" stroke="#1a6fd4" strokeWidth="1.1"/>
          {/* collar right */}
          <line x1="42" y1="26" x2="44" y2="30" stroke="#1a6fd4" strokeWidth="1.1"/>
          {/* tie */}
          <polygon points="40,26 38.8,28.5 40,30.5 41.2,28.5" fill="#1a6fd4" opacity="0.7"/>
          {/* === TOP-LEFT: Bar Chart === */}
          <circle cx="13" cy="7" r="8.5" fill="none" stroke="#1a6fd4" strokeWidth="1.4"/>
          <rect x="9"   y="11.5" width="2.2" height="3"   fill="#1a6fd4" opacity="0.9"/>
          <rect x="12"  y="8.5"  width="2.2" height="6"   fill="#1a6fd4"/>
          <rect x="15"  y="10"   width="2.2" height="4.5" fill="#1a6fd4" opacity="0.75"/>
          <line x1="8.5" y1="14.8" x2="18" y2="14.8" stroke="#1a6fd4" strokeWidth="0.9" opacity="0.6"/>
          {/* === TOP-RIGHT: DOCUMENT / Clipboard === */}
          <circle cx="67" cy="7" r="8.5" fill="none" stroke="#1a6fd4" strokeWidth="1.4"/>
          {/* document rectangle */}
          <rect x="62.5" y="2.5" width="9" height="11" rx="1" fill="none" stroke="#1a6fd4" strokeWidth="1.2"/>
          {/* page fold top-right */}
          <polyline points="68.5,2.5 71.5,2.5 71.5,5.5 68.5,5.5" fill="rgba(26,109,212,0.2)" stroke="#1a6fd4" strokeWidth="0.9"/>
          {/* text lines */}
          <line x1="64.2" y1="8"    x2="70.8" y2="8"    stroke="#1a6fd4" strokeWidth="1" opacity="0.8"/>
          <line x1="64.2" y1="10.2" x2="70.8" y2="10.2" stroke="#1a6fd4" strokeWidth="1" opacity="0.8"/>
          <line x1="64.2" y1="12"   x2="68.5" y2="12"   stroke="#1a6fd4" strokeWidth="1" opacity="0.6"/>
          {/* === BOTTOM-LEFT: Magnifying Glass === */}
          <circle cx="10" cy="43" r="8.5" fill="none" stroke="#1a6fd4" strokeWidth="1.4"/>
          <circle cx="8.5" cy="41" r="4"  fill="none" stroke="#1a6fd4" strokeWidth="1.3"/>
          <line x1="11.5" y1="44.5" x2="14.8" y2="48" stroke="#1a6fd4" strokeWidth="1.8" strokeLinecap="round"/>
          {/* === BOTTOM-RIGHT: Lightbulb === */}
          <circle cx="70" cy="43" r="8.5" fill="none" stroke="#1a6fd4" strokeWidth="1.4"/>
          {/* bulb shape */}
          <path d="M70 36.5 C66.8 36.5 64.5 38.8 64.5 41.5 C64.5 43.5 65.6 45.2 67.3 46 L67.3 47.8 L72.7 47.8 L72.7 46 C74.4 45.2 75.5 43.5 75.5 41.5 C75.5 38.8 73.2 36.5 70 36.5Z" fill="none" stroke="#1a6fd4" strokeWidth="1.2"/>
          <line x1="67.5" y1="48.2" x2="72.5" y2="48.2" stroke="#1a6fd4" strokeWidth="1.2"/>
          <line x1="68.2" y1="49.8" x2="71.8" y2="49.8" stroke="#1a6fd4" strokeWidth="1.1"/>
          {/* filament */}
          <line x1="68.5" y1="42" x2="71.5" y2="42" stroke="#1a6fd4" strokeWidth="0.9" opacity="0.6"/>
          {/* === INTERNASTRA TEXT with E notch effect === */}
          <text x="88" y="31" fontFamily="'Orbitron',monospace" fontWeight="900" fontSize="19" fill="url(#navgrad)" letterSpacing="1.5" filter="url(#navglow)">INTERNASTRA</text>
          <text x="89" y="43" fontFamily="'Rajdhani',sans-serif" fontWeight="600" fontSize="6.5" fill="#4a7a9e" letterSpacing="4">YOUR CAREER STARTS HERE</text>
        </svg>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.72rem", color:"var(--text3)", letterSpacing:2 }}>
          {new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}
        </span>
        {/* HOME button — hidden on landing page */}
        {!hideHome && (
          <button onClick={_navCb.goHome}
            style={{ padding:"5px 14px", background:"transparent", border:"1px solid var(--border)", color:"var(--text2)", cursor:"pointer", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.65rem", letterSpacing:2, display:"flex", alignItems:"center", gap:6, transition:"all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor="var(--accent)"; e.currentTarget.style.color="var(--accent)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.color="var(--text2)"; }}>
            🏠 HOME
          </button>
        )}
        {/* Theme toggle hidden — light mode only */}
        <span className="badge badge-accent" style={{ fontSize:"0.6rem" }}>{page}</span>
      </div>
    </nav>
  );
}

function Ticker() {
  const items = [
    "IIRT — INTERNASTRA INTERNSHIP READINESS TEST", "SCORE 90%+ FOR FREE INTERNSHIP", "SCORE 50–89% FOR 50% DISCOUNT",
    "MCQ-BASED ASSESSMENT", "PLAGIARISM DETECTION ACTIVE",
    "FACE MONITORING ENABLED", "SECURE EXAMINATION SYSTEM", "FAIR SKILL-BASED EVALUATION",
    "ANTI-CHEATING PROTOCOL", "SMART AUTOMATION POWERED",
  ];
  const doubled = [...items, ...items];
  return (
    <div className="ticker-wrap">
      <div className="ticker-inner">
        {doubled.map((item, i) => (
          <span key={i} className="ticker-item">
            <span className="ticker-sep">◆ </span>{item}
          </span>
        ))}
      </div>
    </div>
  );
}

function StepIndicator({ current, steps }) {
  return (
    <div className="step-indicator">
      {steps.map((s, i) => (
        <div key={i} className={`step ${i < current ? "done" : i === current ? "active" : ""}`}>
          <div className="step-num">{i < current ? "✓" : i + 1}</div>
          <div className="step-label">{s}</div>
        </div>
      ))}
    </div>
  );
}

function Modal({ type = "danger", title, message, onClose, onConfirm, closeLabel = "Dismiss", confirmLabel, noClose = false }) {
  return (
    <div className="modal-overlay" onClick={noClose ? undefined : onClose}>
      <div className={`modal-box ${type === "warn" ? "warn" : ""}`} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: "3rem", marginBottom: 16 }}>
          {type === "danger" ? "⛔" : type === "warn" ? "⚠️" : "ℹ️"}
        </div>
        <h2 style={{ fontFamily: "'Orbitron'", fontSize: "1.1rem", letterSpacing: 3, marginBottom: 12, color: type === "danger" ? "var(--danger)" : type === "warn" ? "var(--warn)" : "var(--accent)" }}>
          {title}
        </h2>
        <p style={{ color: "var(--text2)", lineHeight: 1.7, marginBottom: 28, fontSize: "1rem" }}>{message}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          {confirmLabel && <button className="btn btn-danger" onClick={onConfirm}>{confirmLabel}</button>}
          {/* noClose=true hides the dismiss button entirely — modal is permanent */}
          {!noClose && <button className="btn btn-outline" onClick={onClose}>{closeLabel}</button>}
        </div>
      </div>
    </div>
  );
}

// ─── PAGES ────────────────────────────────────────────────────────────────────

// LANDING PAGE
function LandingPage({ onStart, onAdmin, onAmbassador }) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  // ── Particle system ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width  = canvas.offsetWidth;
    let H = canvas.height = canvas.offsetHeight;

    const isDark = () => document.documentElement.getAttribute("data-theme") !== "light";

    // Create particles
    const N = Math.min(90, Math.floor((W * H) / 12000));
    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.8 + 0.6,
      opacity: Math.random() * 0.5 + 0.2,
    }));

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", resize);

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    canvas.parentElement.addEventListener("mousemove", onMouseMove);
    canvas.parentElement.addEventListener("mouseleave", () => { mouseRef.current = { x:-9999, y:-9999 }; });

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const dark = isDark();
      const dotCol  = dark ? "rgba(0,212,255,"  : "rgba(0,100,200,";
      const lineCol = dark ? "rgba(0,212,255,"  : "rgba(0,100,200,";
      const mx = mouseRef.current.x, my = mouseRef.current.y;

      // Update + draw particles
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;

        // Mouse repulsion — gentle push away
        const dx = p.x - mx, dy = p.y - my;
        const dist = Math.sqrt(dx*dx + dy*dy);
        if (dist < 100) {
          const force = (100 - dist) / 100 * 0.018;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }
        // Speed damping
        const speed = Math.sqrt(p.vx*p.vx + p.vy*p.vy);
        if (speed > 1.2) { p.vx *= 0.96; p.vy *= 0.96; }
        if (speed < 0.1) { p.vx += (Math.random()-0.5)*0.05; p.vy += (Math.random()-0.5)*0.05; }

        // Draw dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI*2);
        ctx.fillStyle = dotCol + p.opacity + ")";
        ctx.fill();
      }

      // Draw lines between close particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i+1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x-b.x, dy = a.y-b.y;
          const d = Math.sqrt(dx*dx+dy*dy);
          if (d < 120) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = lineCol + ((1 - d/120) * 0.18) + ")";
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      // Mouse attraction line — connect nearest 5 particles to cursor
      if (mx > -100) {
        const sorted = [...particles].sort((a,b) => {
          const da = (a.x-mx)**2+(a.y-my)**2;
          const db = (b.x-mx)**2+(b.y-my)**2;
          return da - db;
        }).slice(0,5);
        for (const p of sorted) {
          const d = Math.sqrt((p.x-mx)**2+(p.y-my)**2);
          if (d < 160) {
            ctx.beginPath();
            ctx.moveTo(mx, my);
            ctx.lineTo(p.x, p.y);
            ctx.strokeStyle = lineCol + ((1-d/160)*0.35) + ")";
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
        // Cursor dot
        ctx.beginPath();
        ctx.arc(mx, my, 3, 0, Math.PI*2);
        ctx.fillStyle = dark ? "rgba(0,212,255,0.6)" : "rgba(0,100,200,0.5)";
        ctx.fill();
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const features = [
    { icon:"🤖", title:"AI Evaluation",    desc:"Every answer is scored by Claude AI — instant, accurate, bias-free." },
    { icon:"👁️", title:"Face Monitoring",  desc:"Camera detects multiple faces. Violations auto-terminate the exam." },
    { icon:"🔀", title:"Random Questions", desc:"20 questions per candidate — 10 domain + 5 aptitude + 5 reasoning." },
    { icon:"🛡️", title:"Anti-Cheating",    desc:"Tab-switch, voice detection, and face analysis enforce full integrity." },
    { icon:"🔊", title:"Voice Detection",  desc:"Microphone monitors ambient noise. Any sustained voice = violation." },
    { icon:"📊", title:"Instant Results",  desc:"Detailed report card with scores, security log, and eligibility status." },
  ];

  const stats = [
    { num:"20", label:"Questions Per Test" },
    { num:"AI", label:"Powered Scoring" },
    { num:"3x", label:"Security Layers" },
    { num:"100%", label:"Fair Hiring" },
  ];

  return (
    <div className="page" style={{ background:"var(--bg)" }}>
      <Nav page="HOME" hideHome={true} />
      <Ticker />

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <div className="hero-bg" style={{ position:"relative", padding:"110px 40px 80px", textAlign:"center", overflow:"hidden", minHeight:"92vh", display:"flex", alignItems:"center" }}>

        {/* Particle canvas */}
        <canvas ref={canvasRef} className="landing-canvas" style={{ position:"absolute", inset:0, width:"100%", height:"100%" }} />

        {/* Glowing orbs */}
        <div className="hero-orb" style={{ width:500, height:500, left:"-10%", top:"10%", background:"radial-gradient(circle, rgba(0,136,204,0.12) 0%, transparent 70%)", animationDuration:"14s" }} />
        <div className="hero-orb" style={{ width:400, height:400, right:"-8%", top:"20%", background:"radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)", animationDuration:"10s", animationDelay:"-5s" }} />
        <div className="hero-orb" style={{ width:350, height:350, left:"30%", bottom:"5%", background:"radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)", animationDuration:"17s", animationDelay:"-8s" }} />

        {/* Grid dots */}
        <div className="grid-dots" style={{ opacity:0.5 }} />

        {/* Content */}
        <div className="hero-content" style={{ maxWidth:820, margin:"0 auto", width:"100%" }}>

          {/* Floating badge — show referral info if present */}
          {(() => {
            try {
              const ref = localStorage.getItem("iirt_referral");
              if (ref) return (
                <div className="float-badge" style={{ borderColor:"rgba(255,176,32,0.5)", background:"rgba(255,176,32,0.06)", marginBottom:16 }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"var(--warn)", animation:"glow-pulse 1.5s infinite", flexShrink:0 }} />
                  <span>You were referred by <strong style={{color:"var(--warn)"}}>@{ref}</strong></span>
                </div>
              );
            } catch {}
            return null;
          })()}
          <div className="float-badge">
            <span style={{ width:6, height:6, borderRadius:"50%", background:"var(--accent3)", animation:"glow-pulse 1.5s infinite", flexShrink:0 }} />
            INTERNASTRA · AI-POWERED INTERNSHIP ASSESSMENT
          </div>

          <h1 style={{ fontFamily:"'Orbitron'", fontSize:"clamp(2rem,5.5vw,4.2rem)", fontWeight:900, lineHeight:1.08, marginBottom:8, letterSpacing:2 }}>
            IIRT
          </h1>
          <h2 style={{ fontFamily:"'Orbitron'", fontSize:"clamp(1.2rem,3vw,2rem)", fontWeight:700, lineHeight:1.2, marginBottom:20, letterSpacing:4, color:"var(--accent)" }}>
            INTERNASTRA INTERNSHIP
          </h2>
          <h2 style={{ fontFamily:"'Orbitron'", fontSize:"clamp(1rem,2.2vw,1.4rem)", fontWeight:400, lineHeight:1.2, marginBottom:32, letterSpacing:6, color:"var(--text2)" }}>
            READINESS TEST
          </h2>

          <p style={{ fontSize:"clamp(0.95rem,2vw,1.15rem)", color:"var(--text2)", maxWidth:580, margin:"0 auto 44px", lineHeight:1.8 }}>
            Score <strong style={{color:"var(--accent3)"}}>90%+</strong> → <strong style={{color:"var(--accent3)"}}>FREE Internship</strong> &nbsp;·&nbsp;
            Score <strong style={{color:"var(--warn)"}}>50–89%</strong> → <strong style={{color:"var(--warn)"}}>50% Discount</strong>
          </p>

          <div style={{ display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap", alignItems:"center" }}>
            <div className="btn-pulse-wrap">
              <button className="btn btn-primary" onClick={onStart} style={{ fontSize:"1.05rem", padding:"16px 44px", position:"relative", zIndex:1 }}>
                BEGIN ASSESSMENT →
              </button>
            </div>
            <button className="btn btn-outline" onClick={onAmbassador} style={{ padding:"16px 24px" }}>
              🌟 AMBASSADOR LOGIN
            </button>
            <button className="btn btn-outline" onClick={onAdmin} style={{ padding:"16px 24px", fontSize:"0.85rem" }}>
              📊 ADMIN
            </button>
          </div>

          {/* Scroll indicator */}
          <div style={{ marginTop:60, display:"flex", flexDirection:"column", alignItems:"center", gap:6, opacity:0.4 }}>
            <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:4, color:"var(--text3)" }}>SCROLL</div>
            <div style={{ width:1, height:32, background:"linear-gradient(var(--accent),transparent)", animation:"fadeIn 1s ease infinite alternate" }} />
          </div>
        </div>
      </div>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <div style={{ borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)", padding:"0 40px", background:"var(--surface)" }}>
        <div style={{ maxWidth:1000, margin:"0 auto", display:"grid", gridTemplateColumns:"repeat(4,1fr)" }}>
          {stats.map(({ num, label }, i) => (
            <div key={i} className="stat-card">
              <div style={{ fontFamily:"'Orbitron'", fontSize:"2.2rem", fontWeight:900, color:"var(--accent)", textShadow:"var(--glow)", marginBottom:6 }}>{num}</div>
              <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.68rem", letterSpacing:3, color:"var(--text3)", textTransform:"uppercase" }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURES ──────────────────────────────────────────────────────── */}
      <div style={{ padding:"80px 40px 60px", maxWidth:1100, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:52 }}>
          <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", letterSpacing:5, color:"var(--accent)", marginBottom:12 }}>BUILT FOR INTEGRITY</div>
          <h2 className="section-title">KEY <span>FEATURES</span></h2>
          <p className="section-subtitle">Advanced AI + real-time monitoring = zero compromise on fairness</p>
        </div>
        <div className="grid-3">
          {features.map((f, i) => (
            <div key={i} className="feature-card-v2">
              <span className="fc-icon">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── ELIGIBILITY BANDS ─────────────────────────────────────────────── */}
      <div style={{ padding:"0 40px 80px", maxWidth:900, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:40 }}>
          <h2 className="section-title">ELIGIBILITY <span>CRITERIA</span></h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
          {[
            { range:"90 – 100%", title:"FREE INTERNSHIP", icon:"🏆", color:"var(--accent3)", bg:"rgba(0,255,136,0.05)", border:"rgba(0,255,136,0.3)" },
            { range:"50 – 89%",  title:"50% DISCOUNT",    icon:"🎟️", color:"var(--warn)",    bg:"rgba(255,176,32,0.05)", border:"rgba(255,176,32,0.3)" },
            { range:"0 – 49%",   title:"NOT ELIGIBLE",    icon:"📋", color:"var(--danger)",  bg:"rgba(255,59,92,0.05)",  border:"rgba(255,59,92,0.3)" },
          ].map(e => (
            <div key={e.title} className="elig-card" style={{ background:e.bg, borderColor:e.border }}>
              <div style={{ fontSize:"2.5rem", marginBottom:14 }}>{e.icon}</div>
              <div style={{ fontFamily:"'Orbitron'", fontSize:"1.6rem", fontWeight:900, color:e.color, marginBottom:8 }}>{e.range}</div>
              <div style={{ fontFamily:"'Orbitron'", fontSize:"0.72rem", letterSpacing:3, color:e.color, opacity:0.85 }}>{e.title}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────── */}
      <div style={{ background:"var(--surface)", borderTop:"1px solid var(--border)", padding:"80px 40px" }}>
        <div style={{ maxWidth:820, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", letterSpacing:5, color:"var(--accent)", marginBottom:12 }}>PROCESS</div>
            <h2 className="section-title">HOW IT <span>WORKS</span></h2>
          </div>
          {[
            ["01","Register",          "Fill your profile — name, email, phone, domain, and experience level."],
            ["02","System Check",      "Camera + microphone + network verified before you can begin."],
            ["03","Checklist Review",  "Read the integrity rules. Confirm conditions are met."],
            ["04","Take the Test",     "20 MCQ questions — 30 seconds each, fully monitored."],
            ["05","AI Evaluation",     "Answers are scored by Claude AI for accuracy and relevance."],
            ["06","Get Results",       "Instant report card with score, security log, and internship eligibility."],
          ].map(([num, title, desc]) => (
            <div key={num} className="step-card">
              <div className="step-num">{num}</div>
              <div>
                <div style={{ fontFamily:"'Orbitron'", fontSize:"0.88rem", letterSpacing:2, color:"var(--text)", marginBottom:6, fontWeight:700 }}>{title}</div>
                <div style={{ color:"var(--text2)", lineHeight:1.75, fontSize:"0.93rem" }}>{desc}</div>
              </div>
            </div>
          ))}
          <div style={{ marginTop:48, textAlign:"center" }}>
            <div className="btn-pulse-wrap">
              <button className="btn btn-primary" onClick={onStart} style={{ fontSize:"1.05rem", padding:"16px 48px", position:"relative", zIndex:1 }}>
                START YOUR ASSESSMENT →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <div style={{ borderTop:"1px solid var(--border)", padding:"28px 40px", textAlign:"center", background:"var(--bg2)" }}>
        <p style={{ color:"var(--text3)", fontFamily:"'Share Tech Mono'", fontSize:"0.72rem", letterSpacing:3 }}>
          © 2025 IIRT · INTERNASTRA INTERNSHIP READINESS TEST · AI-POWERED · SMART AUTOMATION
        </p>
      </div>
    </div>
  );
}

// REGISTRATION PAGE
function RegistrationPage({ onComplete }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", yearOfStudy: "", degree: "", college: "", department: "", yearOfPassout: "" });
  const [submitted, setSubmitted] = useState(false);
  const [candidateId, setCandidateId] = useState("");
  const [errors, setErrors] = useState({});
  const [dupBlock, setDupBlock] = useState(null); // { candidateId, name, registeredAt }
  const [checking, setChecking] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    if (!form.phone.match(/^\d{10}$/)) e.phone = "10-digit phone required";
    if (!form.role) e.role = "Please select a role";
    if (!form.yearOfStudy) e.yearOfStudy = "Select year of study";
    if (!form.degree.trim()) e.degree = "Degree is required";
    if (!form.department.trim()) e.department = "Department is required";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setChecking(true);
    try {
      const db = await loadDB();
      const emailLow = form.email.trim().toLowerCase();
      const phone = form.phone.trim();
      const dup = db.find(r =>
        r.email?.toLowerCase() === emailLow || r.phone === phone
      );
      if (dup) {
        setDupBlock({ candidateId: dup.candidateId, name: dup.name, registeredAt: dup.registeredAt });
        setChecking(false);
        return;
      }
    } catch {}
    setChecking(false);
    const id = generateCandidateId();
    setCandidateId(id);
    setSubmitted(true);
  };

  if (dupBlock) {
    return (
      <div className="page">
        <Nav page="REGISTRATION" />
        <PageDecorations />
        <div style={{ maxWidth:580, margin:"80px auto", padding:"0 20px", textAlign:"center", position:"relative", zIndex:2 }}>
          <div style={{ fontSize:"4rem", marginBottom:20 }}>🚫</div>
          <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.62rem", letterSpacing:5, color:"var(--danger)", marginBottom:10 }}>ACCESS DENIED</div>
          <h2 style={{ fontFamily:"'Orbitron',monospace", fontSize:"1.5rem", color:"var(--danger)", letterSpacing:3, marginBottom:8 }}>ALREADY ATTEMPTED</h2>
          <p style={{ color:"var(--text2)", lineHeight:1.8, marginBottom:28, fontSize:"0.95rem" }}>
            Your <strong style={{color:"var(--accent)"}}>email address</strong> or <strong style={{color:"var(--accent)"}}>phone number</strong> was found in our records.<br/>
            Each candidate is allowed only <strong style={{color:"var(--danger)"}}>one attempt</strong>.
          </p>
          <div style={{ background:"rgba(255,59,92,0.06)", border:"2px solid rgba(255,59,92,0.25)", padding:"24px 28px", marginBottom:28, position:"relative" }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,var(--danger),transparent)" }}/>
            <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.6rem", letterSpacing:3, color:"var(--text3)", marginBottom:14 }}>EXISTING RECORD</div>
            {[
              ["Candidate ID", dupBlock.candidateId],
              ["Name on record", dupBlock.name],
              ["Attempted on", dupBlock.registeredAt ? new Date(dupBlock.registeredAt).toLocaleDateString() : "—"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid rgba(255,59,92,0.15)" }}>
                <span style={{ color:"var(--text3)", fontSize:"0.88rem" }}>{k}</span>
                <span style={{ color:"var(--danger)", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.82rem", fontWeight:700 }}>{v}</span>
              </div>
            ))}
          </div>
          <p style={{ color:"var(--text3)", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.7rem", letterSpacing:1, lineHeight:2 }}>
            IF YOU BELIEVE THIS IS AN ERROR, CONTACT<br/>
            <span style={{color:"var(--accent)"}}>HIRING@INTERNASTRA.COM</span>
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="page">
        <Nav page="REGISTRATION" />
      <PageDecorations />
        <div style={{ maxWidth: 600, margin: "80px auto", padding: "0 20px", textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: 24, animation: "float 2s ease-in-out infinite" }}>🎯</div>
          <h2 className="section-title" style={{ marginBottom: 16 }}>REGISTRATION <span>COMPLETE</span></h2>
          <p style={{ color: "var(--text2)", marginBottom: 32 }}>Your unique Candidate ID has been generated. Keep this safe — you'll need it for reference.</p>
          <div style={{ background: "var(--surface)", border: "1px solid var(--accent)", padding: "28px", marginBottom: 32 }}>
            <div style={{ fontSize: "0.75rem", letterSpacing: 3, color: "var(--text3)", marginBottom: 12, fontFamily: "'Share Tech Mono'" }}>YOUR CANDIDATE ID</div>
            <div style={{ fontFamily: "'Orbitron'", fontSize: "1.4rem", color: "var(--accent)", letterSpacing: 4, fontWeight: 900, textShadow: "var(--glow)" }}>{candidateId}</div>
          </div>
          <div className="card" style={{ textAlign: "left", marginBottom: 32 }}>
            <div style={{ marginBottom: 12, fontFamily: "'Orbitron'", fontSize: "0.8rem", letterSpacing: 2, color: "var(--text3)" }}>CANDIDATE PROFILE</div>
            {[["Name", form.name], ["Email", form.email], ["Role Applied", form.role], ["Year of Study", form.yearOfStudy], ["Department", form.department]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ color: "var(--text3)", fontSize: "0.9rem" }}>{k}</span>
                <span style={{ color: "var(--text)", fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-primary" style={{ fontSize: "1.1rem" }} onClick={() => onComplete({ form, candidateId })}>
            PROCEED TO INTERVIEW →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <Nav page="REGISTRATION" />
      <div style={{ maxWidth: 700, margin: "40px auto", padding: "0 20px" }}>
        <StepIndicator current={0} steps={["Register", "Interview", "Results"]} />
        <div style={{ marginBottom: 32 }}>
          <h2 className="section-title">CANDIDATE <span>REGISTRATION</span></h2>
          <p className="section-subtitle">Fill in your details to generate your unique Candidate ID</p>
        </div>
        <div className="card">
          <div className="grid-2">
            {[
              { key: "name",   label: "Full Name",      placeholder: "John Doe",               type: "text"  },
              { key: "email",  label: "Email Address",  placeholder: "john@example.com",        type: "email" },
              { key: "phone",  label: "Phone Number",   placeholder: "10-digit mobile number",  type: "tel"   },
              { key: "degree", label: "Degree / Programme", placeholder: "B.Tech / B.E. / BCA / MCA", type: "text" },
            ].map(f => (
              <div key={f.key} className="input-group">
                <label>{f.label}</label>
                <input type={f.type} placeholder={f.placeholder} value={form[f.key]}
                  onChange={e => { setForm(p => ({ ...p, [f.key]: e.target.value })); setErrors(p => ({ ...p, [f.key]: "" })); }} />
                {errors[f.key] && <div style={{ color: "var(--danger)", fontSize: "0.8rem", marginTop: 4 }}>{errors[f.key]}</div>}
              </div>
            ))}
          </div>

          <div className="grid-2">
            {/* Department */}
            <div className="input-group">
              <label>Department <span style={{color:"var(--danger)"}}>*</span></label>
              <input type="text" placeholder="Computer Science / Electronics / Mechanical…" value={form.department}
                onChange={e => { setForm(p => ({ ...p, department: e.target.value })); setErrors(p => ({ ...p, department: "" })); }} />
              {errors.department && <div style={{ color: "var(--danger)", fontSize: "0.8rem", marginTop: 4 }}>{errors.department}</div>}
            </div>

            {/* Year of Study */}
            <div className="input-group">
              <label>Year of Study <span style={{color:"var(--danger)"}}>*</span></label>
              <select value={form.yearOfStudy} onChange={e => { setForm(p => ({ ...p, yearOfStudy: e.target.value })); setErrors(p => ({ ...p, yearOfStudy: "" })); }}>
                <option value="">-- Select Year --</option>
                {["1st Year","2nd Year","3rd Year","4th Year","Graduated"].map(y => <option key={y}>{y}</option>)}
              </select>
              {errors.yearOfStudy && <div style={{ color: "var(--danger)", fontSize: "0.8rem", marginTop: 4 }}>{errors.yearOfStudy}</div>}
            </div>
          </div>

          <div className="grid-2">
            {/* College */}
            <div className="input-group">
              <label>College / University</label>
              <input type="text" placeholder="e.g. VTU / Anna University / IIT" value={form.college}
                onChange={e => setForm(p => ({ ...p, college: e.target.value }))} />
            </div>

            {/* Year of Passout */}
            <div className="input-group">
              <label>Year of Passout / Expected</label>
              <select value={form.yearOfPassout} onChange={e => setForm(p => ({ ...p, yearOfPassout: e.target.value }))}>
                <option value="">-- Select Year --</option>
                {["2023","2024","2025","2026","2027","2028"].map(y => <option key={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="input-group">
              <label>Role Applying For <span style={{color:"var(--danger)"}}>*</span></label>
              <select value={form.role} onChange={e => { setForm(p => ({ ...p, role: e.target.value })); setErrors(p => ({ ...p, role: "" })); }}>
                <option value="">-- Select Role --</option>
                {[
                  "Full Stack Web Development","AI/ML","Data Science","Cyber Security",
                  "Embedded Systems","UI/UX Design","Android Development","AutoCAD","VLSI",
                  "Internet of Things","Robotics","Hybrid and Electric Vehicle",
                  "AutoCAD (Mech)","Car Design","IC Engine","Construction Plan & Design",
                  "Medical Coding","Nano Science and Technology",
                ].map(r => <option key={r}>{r}</option>)}
              </select>
              {errors.role && <div style={{ color: "var(--danger)", fontSize: "0.8rem", marginTop: 4 }}>{errors.role}</div>}
            </div>
            {/* Spacer */}
            <div />
          </div>
          <div style={{ marginTop: 8, padding: "16px", background: "rgba(255,176,32,0.05)", border: "1px solid rgba(255,176,32,0.2)" }}>
            <p style={{ fontSize: "0.85rem", color: "var(--warn)", lineHeight: 1.6 }}>
              ⚠️ By proceeding, you agree to background camera monitoring and AI evaluation of your selected answers. Any cheating attempts will result in automatic disqualification.
            </p>
          </div>
          <div style={{ marginTop: 24 }}>
            <button className="btn btn-primary" style={{ width:"100%", opacity: checking ? 0.7 : 1 }} onClick={handleSubmit} disabled={checking}>
              {checking ? "⏳ CHECKING DATABASE..." : "GENERATE CANDIDATE ID & PROCEED"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── PERMANENTLY BANNED PAGE (shown on refresh after termination) ──────────────
function BannedPage({ session }) {
  const reasonMap = {
    MULTIPLE_FACES: { icon:"👥", label:"MULTIPLE FACES DETECTED", color:"#ff3b5c" },
    VOICE_DETECTED:  { icon:"🔊", label:"VOICE / NOISE DETECTED",   color:"#ff3b5c" },
    TAB_SWITCH:      { icon:"🖥️", label:"TAB SWITCH DETECTED",       color:"#ff3b5c" },
  };
  const reason = reasonMap[session?.reason] || { icon:"⛔", label:"SECURITY VIOLATION", color:"#ff3b5c" };
  return (
    <div className="page" style={{ minHeight:"100vh", background:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <PageDecorations />
      <div style={{ position:"relative", zIndex:2, maxWidth:560, width:"100%", padding:"0 20px", textAlign:"center" }}>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
          <AnimCharacter type="violation" />
        </div>
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.6rem", letterSpacing:5, color:"rgba(255,59,92,0.5)", marginBottom:10 }}>ACCESS PERMANENTLY DENIED</div>
        <h1 style={{ fontFamily:"'Orbitron',monospace", fontSize:"clamp(1.4rem,5vw,2.2rem)", color:"var(--danger)", letterSpacing:4, marginBottom:8, textShadow:"0 0 30px rgba(255,59,92,0.5)" }}>NOT ELIGIBLE</h1>
        <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.75rem", letterSpacing:3, color:"rgba(255,59,92,0.7)", marginBottom:32 }}>{reason.icon} {reason.label}</div>
        <div style={{ background:"rgba(255,59,92,0.05)", border:"2px solid rgba(255,59,92,0.2)", padding:"24px 28px", marginBottom:24, position:"relative" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,var(--danger),transparent)" }}/>
          <p style={{ color:"var(--text2)", lineHeight:1.8, fontSize:"0.92rem", margin:0 }}>
            Your exam was terminated due to a malpractice violation.<br/>
            <strong style={{color:"var(--danger)"}}>This device is permanently blocked from re-attempting this exam.</strong><br/>
            The violation has been reported to Internastra's recruitment team.
          </p>
        </div>
        <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", marginBottom:28 }}>
          {[
            ["CANDIDATE ID", session?.candidateId || "—"],
            ["VIOLATION", reason.label],
            ["TIME", session?.at ? new Date(session.at).toLocaleTimeString() : "—"],
          ].map(([l, v]) => (
            <div key={l} style={{ padding:"12px 18px", background:"rgba(0,0,0,0.4)", border:"1px solid rgba(255,59,92,0.2)", minWidth:140 }}>
              <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.55rem", letterSpacing:3, color:"var(--text3)", marginBottom:5 }}>{l}</div>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.7rem", color:"var(--danger)", fontWeight:700, wordBreak:"break-all" }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.65rem", color:"var(--text3)", letterSpacing:2, lineHeight:2 }}>
          REFRESHING THIS PAGE WILL NOT RESTORE ACCESS<br/>
          CONTACT INTERNASTRA RECRUITMENT IF YOU BELIEVE THIS IS AN ERROR
        </div>
      </div>
    </div>
  );
}


// ── IDE SYNTAX HIGHLIGHTER ────────────────────────────────────────────────────
function CodeHighlight({ code, theme }) {
  const isDark = theme !== "light";
  const C = {
    keyword:  isDark ? "#569cd6" : "#0000cc",
    string:   isDark ? "#ce9178" : "#a31515",
    comment:  isDark ? "#6a9955" : "#008000",
    number:   isDark ? "#b5cea8" : "#098658",
    builtin:  isDark ? "#dcdcaa" : "#795e26",
    type:     isDark ? "#4ec9b0" : "#267f99",
    operator: isDark ? "#d4d4d4" : "#444444",
    punct:    isDark ? "#d4d4d4" : "#444444",
    plain:    isDark ? "#d4d4d4" : "#1e1e1e",
    bg:       isDark ? "#1e1e1e" : "#f8f8f2",
    gutter:   isDark ? "#858585" : "#999999",
    border:   isDark ? "#3c3c3c" : "#d0d0d0",
    titlebar: isDark ? "#2d2d2d" : "#e8e8e8",
  };

  const KEYWORDS = new Set([
    "def","class","if","elif","else","for","while","return","import","from","as","in",
    "not","and","or","is","None","True","False","pass","break","continue","try","except",
    "finally","with","yield","lambda","global","nonlocal","raise","del","assert",
    "public","private","protected","static","void","new","this","super","extends",
    "implements","interface","abstract","final","throws","throw","instanceof",
    "switch","case","default","var","let","const","function","async","await",
    "typeof","null","undefined","true","false","include","using","namespace",
  ]);
  const BUILTINS = new Set([
    "print","input","len","range","type","str","list","dict","set","tuple",
    "enumerate","zip","map","filter","sorted","open","append","pop","push",
    "Math","console","log","System","out","println","main","self","__init__",
  ]);
  const TYPES = new Set([
    "int","float","double","long","short","char","boolean","String","bool",
    "Array","Object","Number","BigInt","byte","uint",
  ]);

  const tokeniseLine = (line) => {
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      const ch = line[i];
      // Single-line comment
      if (ch === "#" || (ch === "/" && line[i+1] === "/")) {
        tokens.push({ t:"comment", v:line.slice(i) });
        break;
      }
      // String literal
      if (ch === '"' || ch === "'") {
        const q = ch;
        let j = i + 1;
        while (j < line.length) {
          if (line[j] === q && line[j-1] !== "\\") break;
          j++;
        }
        tokens.push({ t:"string", v:line.slice(i, j+1) });
        i = j + 1;
        continue;
      }
      // Number
      if (ch >= "0" && ch <= "9") {
        let j = i;
        while (j < line.length && /[0-9._xXbBfFlL]/.test(line[j])) j++;
        tokens.push({ t:"number", v:line.slice(i, j) });
        i = j;
        continue;
      }
      // Word
      if (/[a-zA-Z_$]/.test(ch)) {
        let j = i;
        while (j < line.length && /[a-zA-Z0-9_$]/.test(line[j])) j++;
        const word = line.slice(i, j);
        const t = KEYWORDS.has(word) ? "keyword"
                : TYPES.has(word)    ? "type"
                : BUILTINS.has(word) ? "builtin"
                : "plain";
        tokens.push({ t, v:word });
        i = j;
        continue;
      }
      // Operator
      if ("+-*/%=<>!&|^~".includes(ch)) {
        tokens.push({ t:"operator", v:ch });
        i++; continue;
      }
      tokens.push({ t: ch === " " || ch === "\t" ? "plain" : "punct", v:ch });
      i++;
    }
    return tokens;
  };

  // Separate trailing question sentence from code block
  // A blank line before a capitalised sentence = question separator
  const nlnl = "\n\n";
  const parts = code.split(nlnl);
  let codeBlock = code;
  let questionText = "";
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    const isQuestion = /^(What|Which|How|Find|Output|Choose|Select|Predict|The|If|Given)/i.test(last.trim());
    if (isQuestion) {
      codeBlock = parts.slice(0, -1).join(nlnl);
      questionText = last;
    }
  }

  const codeLines = codeBlock.split("\n");

  return (
    <div>
      <div style={{
        background: C.bg, border: "1px solid " + C.border, borderRadius: 6,
        marginBottom: questionText ? 16 : 0, overflow: "hidden",
        boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.5)" : "0 2px 12px rgba(0,0,0,0.1)",
      }}>
        {/* macOS-style title bar */}
        <div style={{
          display:"flex", alignItems:"center", gap:8, padding:"8px 14px",
          background: C.titlebar, borderBottom:"1px solid "+C.border,
        }}>
          {["#ff5f57","#febc2e","#28c840"].map(col => (
            <div key={col} style={{ width:12, height:12, borderRadius:"50%", background:col }} />
          ))}
          <span style={{
            fontFamily:"'Share Tech Mono',monospace", fontSize:"0.65rem",
            color:C.gutter, marginLeft:8, letterSpacing:1,
          }}>
            {"</>"} CODE SNIPPET
          </span>
        </div>
        {/* Code with line numbers */}
        <div style={{ padding:"14px 0", overflowX:"auto" }}>
          {codeLines.map((line, li) => (
            <div key={li} style={{ display:"flex", minHeight:"1.5em" }}>
              <span style={{
                display:"inline-block", minWidth:42, textAlign:"right", paddingRight:16,
                fontFamily:"'Share Tech Mono',monospace", fontSize:"0.78rem",
                color:C.gutter, userSelect:"none", flexShrink:0, lineHeight:"1.6em",
              }}>
                {li + 1}
              </span>
              <span style={{
                fontFamily:"'Share Tech Mono',monospace", fontSize:"0.82rem",
                lineHeight:"1.6em", paddingRight:20, whiteSpace:"pre",
              }}>
                {line === "" ? "\u00a0" : tokeniseLine(line).map((tok, ti) => (
                  <span key={ti} style={{ color: C[tok.t] || C.plain }}>
                    {tok.v === " " ? "\u00a0" : tok.v}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      </div>
      {questionText ? (
        <p style={{
          fontSize:"1.05rem", lineHeight:1.8, color:"var(--text)",
          fontWeight:600, margin:0, padding:"4px 0",
        }}>
          {questionText}
        </p>
      ) : null}
    </div>
  );
}


// INTERVIEW PAGE
function InterviewPage({ candidate, onComplete }) {
  const [phase, setPhase] = useState(() => {
    // On refresh, if we were in interview mode, restore to setup (re-enable camera properly)
    try {
      const savedPhase = sessionStorage.getItem("iirt_interview_phase");
      // Restore to setup instead of interview so camera can be re-initialized
      if (savedPhase === "interview" || savedPhase === "setup") return "loading";
      return "loading"; // always re-load questions fresh
    } catch { return "loading"; }
  }); // loading | setup | interview | evaluating
  const [questions, setQuestions] = useState([]);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadCategory, setLoadCategory] = useState("");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(Array(20).fill(""));
  const [cameraActive, setCameraActive] = useState(false);
  const [faceCount, setFaceCount] = useState(1);
  const [sysCheck, setSysCheck] = useState({ camera:"idle", mic:"idle", noise:"idle", network:"idle", noiseLevel:0, ping:null });
  const sysAudioCtxRef = useRef(null); // separate audio ctx just for system check mic test
  const [tabWarnings, setTabWarnings] = useState(0);
  const [modal, setModal] = useState(null);
  const [terminated, setTerminated] = useState(false);
  const [logs, setLogs] = useState([]);
  const [cameraError, setCameraError] = useState(false);
  const [bankReady, setBankReady] = useState(false); // true = admin QB has enough questions
  const [voiceDetected, setVoiceDetected] = useState(false);
  const [terminationReason, setTerminationReason] = useState(""); // what caused termination
  const [termSaved, setTermSaved] = useState(false); // true once terminated record saved to DB
  const audioContextRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const voiceIntervalRef = useRef(null);
  const voiceViolationCountRef = useRef(0);

  useEffect(() => {
    if (candidate?.form?.role) {
      loadQB().then(bank => {
        const domain = getDomainKey(candidate.form.role);
        const hasAny = (bank[domain]?.length > 0) || (bank["Aptitude"]?.length > 0) || (bank["Reasoning"]?.length > 0);
        setBankReady(hasAny);
      });
    }
  }, [candidate?.form?.role]);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const addLog = useCallback((msg, type = "info") => {
    const time = new Date().toLocaleTimeString("en-IN", { hour12: false });
    setLogs(p => [...p.slice(-20), { msg: `[${time}] ${msg}`, type }]);
  }, []);

  // Generate all 20 questions via AI on mount
  useEffect(() => {
    addLog("AI is generating your personalised question set...", "info");
    generateAllQuestions(candidate?.form?.role, (done, total, cat) => {
      setLoadProgress(Math.round((done / total) * 100));
      setLoadCategory(cat);
      addLog("Generated Q" + done + " (" + cat + ")", "ok");
    }).then(qs => {
      setQuestions(qs);
      setAnswers(Array(qs.length).fill(""));
      addLog("All 20 questions ready. Proceed to setup.", "ok");
      setPhase("setup");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 30-second countdown timer — auto-advance on 0
  useEffect(() => {
    if (!timerActive) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          setTimerActive(false);
          // Use a microtask so state is settled
          setTimeout(() => {
            setCurrentQ(prev => {
              setAnswers(latestAnswers => {
                const isLast = prev >= questions.length - 1;
                if (isLast) {
                  if (streamRef.current) {
                    if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
                    if (streamRef.current._faceInterval) clearInterval(streamRef.current._faceInterval);
                    streamRef.current.getTracks().forEach(t2 => t2.stop());
                  }
                  setPhase("evaluating");
                  evaluateAllAnswers(questions, latestAnswers).then(evalResults => {
                    const scores = evalResults.map(r => r.score);
                    const feedbacks = evalResults.map(r => r.feedback);
                    const totalScore = scores.reduce((a, b) => a + b, 0);
                    const maxScore = questions.reduce((s,q) => s + getMaxScore(q.category), 0);
            const pct = Math.round((totalScore / maxScore) * 100);
                    onComplete({ answers: latestAnswers, questions, score: totalScore, percentage: pct, aiEvalScores: scores, aiFeedbacks: feedbacks,
              faceViolations: faceViolationCountRef.current,
              tabWarnings: tabWarningRef.current,
              voiceWarnings: voiceWarningCountRef.current,
            });
                  });
                  return latestAnswers;
                }
                return latestAnswers;
              });
              if (prev < questions.length - 1) {
                setTimeLeft(30);
                setTimeout(() => setTimerActive(true), 100);
                return prev + 1;
              }
              return prev;
            });
          }, 0);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerActive]);

  // ── TAB SWITCH + WINDOW BLUR DETECTION ─────────────────────────────────────
  const tabWarningRef = useRef(0);
  const phaseRef = useRef(phase);
  const terminatedRef = useRef(terminated);

  useEffect(() => {
    phaseRef.current = phase;
    try { sessionStorage.setItem("iirt_interview_phase", phase); } catch {}
  }, [phase]);
  useEffect(() => { terminatedRef.current = terminated; }, [terminated]);

  // When terminated: save record immediately + persist so refresh locks them out
  useEffect(() => {
    if (!terminated || termSaved) return;
    setTermSaved(true);
    const answeredCount = answers.filter(a => a && a !== "").length;
    saveTerminatedRecord(candidate, terminationReason, {
      answeredCount,
      faceViolations: faceViolationCountRef.current,
      tabWarnings: tabWarningRef.current,
      voiceWarnings: voiceWarningCountRef.current,
      questions,
      answers,
    }).catch(() => {});
    // Persist termination to sessionStorage so refresh shows terminated page
    try {
      sessionStorage.setItem("iirt_terminated", JSON.stringify({
        candidateId: candidate?.candidateId,
        reason: terminationReason,
        at: new Date().toISOString(),
      }));
      sessionStorage.removeItem("iirt_interview_phase");
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminated, terminationReason]);

  useEffect(() => {
    const fireTabViolation = () => {
      if (phaseRef.current !== "interview") return;
      if (terminatedRef.current) return;
      tabWarningRef.current += 1;
      const count = tabWarningRef.current;
      setTabWarnings(count);
      addLog("Tab switch #" + count + " detected.", count >= 2 ? "err" : "warn");
      if (count >= 2) {
        // 2nd tab switch → terminate
        terminatedRef.current = true;
        setTerminated(true);
        setTerminationReason("TAB_SWITCH");
        addLog("CRITICAL: 2nd tab switch — exam TERMINATED.", "err");
        if (voiceIntervalRef.current) clearInterval(voiceIntervalRef.current);
        if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
        if (audioContextRef.current) { try { audioContextRef.current.close(); } catch {} }
      } else {
        // 1st tab switch → warning modal only
        setModal({
          type: "warn",
          title: "⚠️ WINDOW SWITCH DETECTED",
          message: "You left the exam window. This is WARNING 1 of 2. If you switch tabs again, your exam will be IMMEDIATELY terminated and you will be marked NOT ELIGIBLE.",
          noClose: false,
        });
      }
    };
    let lastFire = 0;
    const fireDebounced = () => {
      const now = Date.now();
      if (now - lastFire < 800) return;
      lastFire = now;
      fireTabViolation();
    };
    const handleVisibility = () => { if (document.hidden) fireDebounced(); };
    const handleBlur = () => { if (phaseRef.current === "interview") fireDebounced(); };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── FACE DETECTION — Canvas pixel analysis (no CDN needed, always works) ────
  const faceApiReadyRef = useRef(false);
  const faceViolationCountRef = useRef(0);
  const faceDetectionRef = useRef(null);
  const faceCanvasRef = useRef(null); // hidden canvas for pixel analysis
  const voiceWarningCountRef = useRef(0); // voice warnings before termination

  // ── Skin-tone pixel cluster detection ────────────────────────────────────────
  // Draws each video frame onto a hidden canvas, analyses pixel skin-tone clusters
  // to detect faces. Two spatially separated clusters = two faces.
  const detectFacesFromCanvas = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return 0;

    // Create hidden canvas if needed
    if (!faceCanvasRef.current) {
      const c = document.createElement("canvas");
      c.width = 160; c.height = 120; // small = fast analysis
      c.style.display = "none";
      document.body.appendChild(c);
      faceCanvasRef.current = c;
    }
    const canvas = faceCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const W = canvas.width, H = canvas.height;

    // Mark skin-tone pixels
    // Skin in YCbCr: Cb 77-127, Cr 133-173 (works across lighting/ethnicities)
    const skinMap = new Uint8Array(W * H);
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i+1], b = data[i+2];
      // Convert to YCbCr
      const Y  =  0.299*r + 0.587*g + 0.114*b;
      const Cb = -0.169*r - 0.331*g + 0.500*b + 128;
      const Cr =  0.500*r - 0.419*g - 0.081*b + 128;
      const isSkin = Y > 40 && Cb >= 77 && Cb <= 127 && Cr >= 133 && Cr <= 173;
      skinMap[i/4] = isSkin ? 1 : 0;
    }

    // Find connected skin-tone clusters using simple column-band grouping
    // Divide image into 8 vertical bands, count skin density per band
    const BANDS = 8;
    const bandWidth = Math.floor(W / BANDS);
    const bandDensity = new Array(BANDS).fill(0);
    for (let px = 0; px < W * H; px++) {
      if (skinMap[px]) {
        const col = px % W;
        const band = Math.min(Math.floor(col / bandWidth), BANDS - 1);
        bandDensity[band]++;
      }
    }
    const pixelsPerBand = bandWidth * H;
    const threshold = 0.12; // 12% skin density = face-sized cluster

    // Count bands that exceed threshold — each "hot" band group = a face region
    // Merge adjacent hot bands into face clusters
    const hotBands = bandDensity.map(d => d / pixelsPerBand > threshold);
    let clusters = 0;
    let inCluster = false;
    for (let b = 0; b < BANDS; b++) {
      if (hotBands[b] && !inCluster) { clusters++; inCluster = true; }
      else if (!hotBands[b]) { inCluster = false; }
    }

    return clusters; // 0=no face, 1=one face, 2+=multiple faces
  };

  // ── Load face-api.js for AI-grade detection (best effort) ─────────────────
  const loadFaceApi = async () => {
    return new Promise((resolve) => {
      if (window.faceapi && faceApiReadyRef.current) { resolve(true); return; }
      const CDNS = [
        "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js",
        "https://unpkg.com/face-api.js@0.22.2/dist/face-api.min.js",
        "https://cdnjs.cloudflare.com/ajax/libs/face-api.js/0.22.2/face-api.min.js",
      ];
      const MODELS = [
        "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights",
        "https://unpkg.com/face-api.js@0.22.2/weights",
      ];
      let idx = 0;
      const tryNext = () => {
        if (idx >= CDNS.length) { resolve(false); return; }
        const s = document.createElement("script");
        s.src = CDNS[idx];
        s.onload = async () => {
          for (const m of MODELS) {
            try {
              await window.faceapi.nets.tinyFaceDetector.loadFromUri(m);
              faceApiReadyRef.current = true;
              addLog("✓ AI face detection loaded (face-api.js)", "ok");
              resolve(true); return;
            } catch {}
          }
          resolve(false);
        };
        s.onerror = () => { idx++; tryNext(); };
        document.head.appendChild(s);
      };
      // 8 second timeout — if CDN too slow, fall back to canvas method
      setTimeout(() => { if (!faceApiReadyRef.current) resolve(false); }, 8000);
      tryNext();
    });
  };

  // ── Main face detection loop — dual method ────────────────────────────────
  const startFaceDetection = () => {
    if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
    let consecutiveMultiFace = 0;

    let faceWarnGiven = false; // track if 1st warning already shown this session

    const terminateFace = (reason, msg) => {
      clearInterval(faceDetectionRef.current);
      if (voiceIntervalRef.current) clearInterval(voiceIntervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current) { try { audioContextRef.current.close(); } catch {} }
      if (faceCanvasRef.current) { try { document.body.removeChild(faceCanvasRef.current); } catch {} faceCanvasRef.current = null; }
      terminatedRef.current = true;
      faceViolationCountRef.current += 1;
      setTerminated(true);
      setTerminationReason(reason);
      addLog("CRITICAL: " + msg + " — exam TERMINATED.", "err");
    };

    const detect = async () => {
      if (terminatedRef.current) return;
      if (phaseRef.current !== "interview") return;

      let count = 0;

      // Method 1: face-api.js AI (if loaded)
      if (faceApiReadyRef.current && videoRef.current && videoRef.current.readyState >= 2) {
        try {
          const detections = await window.faceapi.detectAllFaces(
            videoRef.current,
            new window.faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.35 })
          );
          count = detections.length;
        } catch { count = -1; }
      }

      // Method 2: Canvas skin-tone analysis (always runs)
      const canvasCount = detectFacesFromCanvas();
      const finalCount = count === -1 ? canvasCount : Math.max(count, canvasCount);
      setFaceCount(finalCount === 0 ? 0 : finalCount);

      if (finalCount > 1) {
        consecutiveMultiFace++;
        addLog("⚠ MULTIPLE FACES: " + finalCount + " — streak " + consecutiveMultiFace + "/2", "err");
        if (!faceWarnGiven && consecutiveMultiFace >= 2) {
          // First confirmed detection → show 1 warning
          faceWarnGiven = true;
          consecutiveMultiFace = 0;
          faceViolationCountRef.current += 1;
          setModal({
            type: "warn",
            title: "👥 MULTIPLE FACES DETECTED",
            message: "A second person has been detected on camera. This is your ONE AND ONLY warning. If detected again, your exam will be immediately terminated.",
            noClose: false,
          });
          addLog("WARNING: Multiple faces — 1 warning issued.", "warn");
        } else if (faceWarnGiven && consecutiveMultiFace >= 2) {
          // Already warned → terminate
          terminateFace("MULTIPLE_FACES", "Multiple faces confirmed after warning");
        }
      } else if (finalCount === 0) {
        consecutiveMultiFace = 0;
        // No face at all for 2 consecutive checks = alert (not terminate — lighting may vary)
        if (!faceWarnGiven) {
          addLog("No face detected — please ensure your face is visible", "warn");
        }
      } else {
        consecutiveMultiFace = 0;
      }
    };

    // Run every 2 seconds — fast enough to catch cheating, not too heavy
    faceDetectionRef.current = setInterval(detect, 2000);
  };

  // ── SYSTEM CHECK — camera, mic, noise level, network ────────────────────────
  const runSystemCheck = async () => {
    setSysCheck(p => ({ ...p, camera:"checking", mic:"checking", noise:"checking", network:"checking", noiseLevel:0, ping:null }));

    // ── Camera check ─────────────────────────────────────────────────────────
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width:{ ideal:640 }, height:{ ideal:480 }, facingMode:"user" }, audio:false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await new Promise(res => { videoRef.current.onloadedmetadata = res; });
        await videoRef.current.play().catch(()=>{});
      }
      setCameraActive(true);
      // Wait a moment for the video to stabilise, then check for face
      await new Promise(res => setTimeout(res, 1200));
      const canvasCount = detectFacesFromCanvas();
      if (canvasCount >= 1) {
        setSysCheck(p => ({ ...p, camera:"pass" }));
        addLog("✓ Camera — face detected", "ok");
        loadFaceApi().then(() => addLog("✓ AI face detection loaded", "ok"));
        startFaceDetection();
      } else {
        setSysCheck(p => ({ ...p, camera:"pass" })); // pass if stream works even if face not auto-detected
        addLog("✓ Camera stream active", "ok");
        loadFaceApi();
        startFaceDetection();
      }
    } catch (err) {
      setSysCheck(p => ({ ...p, camera:"fail" }));
      setCameraError(true);
      setCameraActive(true);
      addLog("✗ Camera failed: " + err.message, "err");
    }

    // ── Microphone + noise check ─────────────────────────────────────────────
    try {
      if (sysAudioCtxRef.current) { try { sysAudioCtxRef.current.close(); } catch {} }
      const aStream = await navigator.mediaDevices.getUserMedia({ audio:true, video:false });
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      sysAudioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(aStream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      setSysCheck(p => ({ ...p, mic:"pass" }));
      addLog("✓ Microphone working", "ok");

      // Measure ambient noise for 3 seconds
      const samples = [];
      await new Promise(res => {
        const id = setInterval(() => {
          analyser.getByteFrequencyData(data);
          const rms = Math.sqrt(data.reduce((s,v)=>s+v*v,0)/data.length);
          // Normalise to 0-100 for display
          const pct = Math.min(100, (rms / 60) * 100);
          samples.push(rms);
          setSysCheck(p => ({ ...p, noiseLevel: pct, noise:"checking" }));
          if (samples.length >= 6) { clearInterval(id); res(); }
        }, 500);
      });
      const avgRms = samples.reduce((a,b)=>a+b,0)/samples.length;
      const isQuiet = avgRms < 20;
      setSysCheck(p => ({ ...p, noise: isQuiet ? "pass" : "fail", noiseLevel: Math.min(100,(avgRms/60)*100) }));
      addLog(isQuiet ? "✓ Quiet environment" : "✗ Too much ambient noise — level: "+avgRms.toFixed(1), isQuiet?"ok":"warn");
      // Close sys check audio ctx — voice detection will open its own later
      try { ctx.close(); } catch {}
    } catch (err) {
      setSysCheck(p => ({ ...p, mic:"fail", noise:"fail" }));
      addLog("✗ Microphone failed: " + err.message, "err");
    }

    // ── Network check ────────────────────────────────────────────────────────
    try {
      const t0 = Date.now();
      await fetch("https://www.google.com/favicon.ico?_=" + t0, { mode:"no-cors", cache:"no-store" });
      const ping = Date.now() - t0;
      setSysCheck(p => ({ ...p, network:"pass", ping }));
      addLog("✓ Network — "+ping+"ms", "ok");
    } catch {
      try {
        const t0 = Date.now();
        await fetch("https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/package.json", { mode:"no-cors", cache:"no-store" });
        const ping = Date.now() - t0;
        setSysCheck(p => ({ ...p, network:"pass", ping }));
        addLog("✓ Network — "+ping+"ms", "ok");
      } catch {
        setSysCheck(p => ({ ...p, network:"fail" }));
        addLog("✗ Network unavailable", "err");
      }
    }
  };

  // startCamera is only used for auto-restart after page refresh
  // Normal flow uses runSystemCheck which opens the stream
  const startCamera = async () => {
    if (streamRef.current && cameraActive) return; // already running
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await new Promise(res => { videoRef.current.onloadedmetadata = res; });
        await videoRef.current.play().catch(() => {});
      }
      setCameraActive(true);
      loadFaceApi();
      startFaceDetection();
      addLog("✓ Camera restored after refresh", "ok");
    } catch (err) {
      setCameraError(true);
      setCameraActive(true);
      addLog("Camera error: " + (err.message || "permission denied"), "warn");
    }
  };

  // ── VOICE / NOISE DETECTION — 2 warnings then terminate ───────────────────
  const startVoiceDetection = async () => {
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(audioStream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      audioAnalyserRef.current = analyser;
      addLog("✓ Voice monitoring active", "ok");

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let consecutiveVoice = 0;
      let cooldownFrames = 0;      // after a warning, ignore for 10s
      const VOICE_THRESHOLD = 25;  // RMS: silence~3, keyboard~10, speech~30-60
      const CONSECUTIVE_NEEDED = 4; // 4 × 500ms = 2 seconds of sustained noise

      voiceIntervalRef.current = setInterval(() => {
        if (terminatedRef.current) { clearInterval(voiceIntervalRef.current); return; }
        if (phaseRef.current !== "interview") return;
        if (cooldownFrames > 0) { cooldownFrames--; consecutiveVoice = 0; return; }

        analyser.getByteFrequencyData(dataArray);
        const rms = Math.sqrt(dataArray.reduce((s, v) => s + v * v, 0) / dataArray.length);

        if (rms > VOICE_THRESHOLD) {
          consecutiveVoice++;
          if (consecutiveVoice >= CONSECUTIVE_NEEDED) {
            consecutiveVoice = 0;
            voiceWarningCountRef.current += 1;
            const wCount = voiceWarningCountRef.current;
            addLog("Voice detected — level " + rms.toFixed(1) + " — warning " + wCount + "/2", "warn");

            if (wCount >= 2) {
              // 2nd warning → TERMINATE
              clearInterval(voiceIntervalRef.current);
              clearInterval(faceDetectionRef.current);
              if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
              try { ctx.close(); } catch {}
              terminatedRef.current = true;
              setTerminated(true);
              setVoiceDetected(true);
              setTerminationReason("VOICE_DETECTED");
              addLog("CRITICAL: Voice warning #2 — exam TERMINATED.", "err");
            } else {
              // 1st warning — show modal, cool down for 10 seconds
              cooldownFrames = 20; // 20 × 500ms = 10 seconds grace period
              setModal({
                type: "warn",
                title: "🔊 VOICE / NOISE DETECTED",
                message: "Significant noise or voice was detected. This is WARNING 1 of 2. Ensure complete silence. A second detection will IMMEDIATELY terminate your exam.",
                noClose: false,
              });
            }
          }
        } else {
          consecutiveVoice = 0;
        }
      }, 500);
    } catch (e) {
      addLog("Microphone unavailable — voice monitoring skipped.", "warn");
    }
  };

  // Auto-restart camera if page was refreshed during interview (phase restored from sessionStorage)
  useEffect(() => {
    if (phase === "interview" && !cameraActive) {
      startCamera();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const startInterview = () => {
    setPhase("interview");
    addLog("Interview session started.", "ok");
    addLog("Candidate: " + candidate.form.name + " | ID: " + candidate.candidateId, "info");
    setCurrentQ(0);
    setTimeLeft(30);
    setTimerActive(true);
    // Start voice monitoring when interview begins
    startVoiceDetection();
  };

  // MCQ click handler — store selected letter only
  const selectOption = (opt) => {
    setAnswers(p => { const a = [...p]; a[currentQ] = opt; return a; });
    addLog("Q" + (currentQ + 1) + ": Option " + opt + " selected.", "ok");
  };

  const handleNextQuestion = useCallback(() => {
    clearInterval(timerRef.current);
    setTimerActive(false);
    setCurrentQ(prev => {
      const isLast = prev >= questions.length - 1;
      if (isLast) {
        if (streamRef.current) {
          if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
          if (streamRef.current._faceInterval) clearInterval(streamRef.current._faceInterval);
          streamRef.current.getTracks().forEach(t => t.stop());
        }
        addLog("All questions completed. Starting AI evaluation...", "ok");
        setPhase("evaluating");
        // Use functional update to get latest answers
        setAnswers(latestAnswers => {
          evaluateAllAnswers(questions, latestAnswers).then(evalResults => {
            const scores = evalResults.map(r => r.score);
            const feedbacks = evalResults.map(r => r.feedback);
            const totalScore = scores.reduce((a, b) => a + b, 0);
            const maxScore = questions.reduce((s,q) => s + getMaxScore(q.category), 0);
            const pct = Math.round((totalScore / maxScore) * 100);
            onComplete({ answers: latestAnswers, questions, score: totalScore, percentage: pct, aiEvalScores: scores, aiFeedbacks: feedbacks,
              faceViolations: faceViolationCountRef.current,
              tabWarnings: tabWarningRef.current,
              voiceWarnings: voiceWarningCountRef.current,
            });
          });
          return latestAnswers;
        });
        return prev;
      }
      // Face violation check — null the answer so score = 0 for this question
      if (faceCount > 1) {
        setAnswers(p => { const a = [...p]; a[prev] = "FACE_VIOLATION"; return a; });
        addLog("Q" + (prev + 1) + ": Multiple faces detected. Answer nullified.", "err");
      }
      setTimeLeft(30);
      setTimeout(() => setTimerActive(true), 100);
      return prev + 1;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, faceCount, onComplete]);


  // Persist the live interview state for refresh recovery
  useEffect(() => {
    try {
      const state = { phase, currentQ, answers, tabWarnings };
      sessionStorage.setItem("iirt_interview_state", JSON.stringify(state));
    } catch {}
  }, [phase, currentQ, answers, tabWarnings]);

  if (phase === "loading") {
    const catColors = { "Numerical Aptitude": "var(--warn)", "Reasoning": "var(--accent3)", "Domain": "var(--accent)" };
    const catIcons = { "Numerical Aptitude": "🔢", "Reasoning": "🧠", "Domain": "⚙️" };
    const cats = ["Numerical Aptitude", "Reasoning", "Domain"];
    const perCat = 5;
    return (
      <div className="page">
        <Nav page="GENERATING QUESTIONS" hideHome={true} />
        <PageDecorations />
        <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", position:"relative", zIndex:2 }}>
          <div style={{ textAlign: "center", maxWidth: 540, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
              <div className="spinner" style={{ width: 64, height: 64, borderWidth: 4 }} />
            </div>
            <h2 className="section-title" style={{ marginBottom: 8 }}>AI BUILDING YOUR <span>QUESTION SET</span></h2>
            <p style={{ color: "var(--text2)", marginBottom: 8, lineHeight: 1.6, fontSize: "0.92rem" }}>
              Domain: <strong style={{ color: "var(--accent)" }}>{candidate?.form?.role}</strong>
            </p>
            <p style={{ color: "var(--text3)", marginBottom: 28, fontSize: "0.82rem", fontFamily: "'Share Tech Mono'", letterSpacing: 1 }}>
              {bankReady
                ? "✅ USING ADMIN QUESTION BANK · RANDOMLY SELECTED · 10 DOMAIN + 5 APTITUDE + 5 REASONING"
                : "🤖 AI GENERATING · 20 UNIQUE QUESTIONS · NEVER REPEATED"}
            </p>
            {/* Overall progress bar */}
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 3, marginBottom: 8, borderRadius: 2 }}>
              <div style={{ height: 10, background: "linear-gradient(90deg, var(--accent), var(--accent3))", width: loadProgress + "%", transition: "width 0.5s ease", boxShadow: "0 0 14px var(--accent)" }} />
            </div>
            <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.78rem", color: "var(--accent)", letterSpacing: 2, marginBottom: 24 }}>
              {loadProgress < 100 ? loadProgress + "% — NOW GENERATING: " + (loadCategory || "...") : "100% — ALL QUESTIONS READY"}
            </div>
            {/* Per-category progress */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 420, margin: "0 auto" }}>
              {cats.map(cat => {
                const genCount = Math.min(perCat, Math.max(0, Math.round(loadProgress / 100 * 20) - cats.indexOf(cat) * perCat));
                const isActive = loadCategory === cat;
                const isDone = genCount >= perCat;
                return (
                  <div key={cat} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "var(--surface)", border: "1px solid " + (isActive ? catColors[cat] : "var(--border)"), transition: "border-color 0.3s" }}>
                    <span style={{ fontSize: "1.1rem" }}>{catIcons[cat]}</span>
                    <span style={{ flex: 1, color: "var(--text2)", fontSize: "0.88rem", textAlign: "left" }}>{cat}</span>
                    <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.72rem", color: isDone ? "var(--accent3)" : isActive ? catColors[cat] : "var(--text3)", animation: isActive ? "pulse 1s infinite" : "none" }}>
                      {isDone ? "✓ DONE" : isActive ? "GENERATING..." : genCount + "/" + perCat}
                    </span>
                  </div>
                );
              })}
            </div>
            <p style={{ marginTop: 24, color: "var(--text3)", fontSize: "0.78rem" }}>Please wait — this takes about 30 seconds</p>
          </div>
        </div>
      </div>
    );
  }

  if (terminated) {
    const reasonMap = {
      MULTIPLE_FACES: {
        icon: "👥",
        title: "MULTIPLE FACES DETECTED",
        detail: "A second person was detected on camera for the second time. You received 1 warning after the first detection.",
        rule: "Only the registered candidate may be visible. After one warning, any further detection immediately terminates the exam.",
      },
      VOICE_DETECTED: {
        icon: "🔊",
        title: "VOICE / NOISE DETECTED × 2",
        detail: "Sustained voice or significant noise was detected twice during the exam. You were given 1 warning after the first detection.",
        rule: "The exam must be conducted in complete silence. Two voice detections result in immediate termination.",
      },
      TAB_SWITCH: {
        icon: "🖥️",
        title: "TAB / WINDOW SWITCH × 2",
        detail: "You navigated away from the exam window twice during the test. You were given 1 warning after the first switch.",
        rule: "You must remain on the exam tab at all times. Two window switches result in immediate termination.",
      },
    };
    const reason = reasonMap[terminationReason] || { icon:"⛔", title:"SECURITY VIOLATION", detail:"A security policy violation was detected.", rule:"All exam rules must be followed throughout the test." };
    const answeredCount = answers.filter(a => a && a !== "").length;

    return (
      <div className="page" style={{ minHeight:"100vh", background:"var(--bg)", position:"relative" }}>
        <PageDecorations />
        <Nav page="EXAM TERMINATED" hideHome={true} />
        <div style={{ position:"relative", zIndex:2, maxWidth:680, margin:"0 auto", padding:"60px 20px 80px", textAlign:"center" }}>

          <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
            <AnimCharacter type="violation" />
          </div>

          <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.65rem", letterSpacing:5, color:"rgba(255,59,92,0.5)", marginBottom:10 }}>⛔ EXAM PERMANENTLY TERMINATED</div>
          <h2 style={{ fontFamily:"'Orbitron',monospace", fontSize:"clamp(1.4rem,4vw,2.2rem)", color:"var(--danger)", letterSpacing:4, marginBottom:8, textShadow:"0 0 30px rgba(255,59,92,0.4)", animation:"shake 0.5s ease 0.2s" }}>NOT ELIGIBLE</h2>
          <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.75rem", letterSpacing:3, color:"rgba(255,59,92,0.7)", marginBottom:36 }}>{reason.title}</div>

          <div style={{ background:"rgba(255,59,92,0.04)", border:"2px solid rgba(255,59,92,0.25)", padding:"28px 32px", marginBottom:24, textAlign:"left", position:"relative" }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,var(--danger),transparent)" }}/>
            <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.6rem", letterSpacing:4, color:"rgba(255,59,92,0.5)", marginBottom:16 }}>VIOLATION REPORT</div>

            <div style={{ marginBottom:18 }}>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.65rem", letterSpacing:2, color:"var(--danger)", marginBottom:8 }}>WHAT HAPPENED</div>
              <p style={{ color:"var(--text2)", lineHeight:1.75, fontSize:"0.92rem" }}>{reason.detail}</p>
            </div>

            <div style={{ marginBottom:20, padding:"12px 16px", background:"rgba(255,59,92,0.06)", borderLeft:"3px solid var(--danger)" }}>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.62rem", letterSpacing:2, color:"var(--danger)", marginBottom:6 }}>RULE VIOLATED</div>
              <p style={{ color:"var(--text2)", lineHeight:1.65, fontSize:"0.88rem" }}>{reason.rule}</p>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
              {[
                ["CANDIDATE ID", candidate?.candidateId || "—"],
                ["DOMAIN", candidate?.form?.role || "—"],
                ["Qs ANSWERED", answeredCount + " / " + questions.length],
                ["TERMINATED AT", new Date().toLocaleTimeString()],
                ["TAB SWITCHES", tabWarnings + "/2"],
                ["FACE VIOLATIONS", faceViolationCountRef.current > 0 ? faceViolationCountRef.current + " faces" : "0"],
                ["VOICE DETECTIONS", voiceWarningCountRef.current + "/2"],
              ].map(([label, val]) => (
                <div key={label} style={{ background:"rgba(0,0,0,0.3)", padding:"10px 12px", border:"1px solid rgba(255,59,92,0.15)" }}>
                  <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.52rem", letterSpacing:2, color:"var(--text3)", marginBottom:5 }}>{label}</div>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.68rem", color:"var(--danger)", fontWeight:700 }}>{String(val)}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap", marginBottom:28 }}>
            {["MULTIPLE_FACES","VOICE_DETECTED","TAB_SWITCH"].map(r => terminationReason === r && (
              <div key={r} style={{ padding:"10px 18px", background:"rgba(255,59,92,0.08)", border:"1px solid rgba(255,59,92,0.3)", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.72rem", color:"var(--danger)" }}>
                {r === "MULTIPLE_FACES" ? "👥 MULTIPLE FACES — INTEGRITY BREACH" : r === "VOICE_DETECTED" ? "🔊 VOICE DETECTED — SILENT ENVIRONMENT REQUIRED" : "🖥️ TAB SWITCH — STAY ON EXAM WINDOW"}
              </div>
            ))}
          </div>

          <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.7rem", color:"var(--text3)", lineHeight:2.2, letterSpacing:1 }}>
            THIS VIOLATION HAS BEEN RECORDED<br/>
            YOU ARE NOT ELIGIBLE FOR THIS INTERNSHIP ROUND
          </div>
        </div>
      </div>
    );
  }
  if (phase === "evaluating") {
    return (
      <div className="page">
        <Nav page="AI EVALUATING" hideHome={true} />
      <PageDecorations />
        <div style={{ textAlign: "center", padding: "60px 20px", maxWidth: 600, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <div className="spinner" style={{ width: 60, height: 60, borderWidth: 4 }} />
          </div>
          <h2 className="section-title" style={{ marginBottom: 12 }}>AI <span>EVALUATING</span> YOUR ANSWERS</h2>
          <p style={{ color: "var(--text2)", marginBottom: 40, lineHeight: 1.7 }}>
            Our AI is analyzing each of your selected answers for correctness and domain knowledge.
          </p>
          {[
            ["🔍 Plagiarism Check", "COMPLETE ✓", "var(--accent3)"],
            ["🤖 AI Answer Scoring", "RUNNING...", "var(--warn)"],
            ["📊 Relevance Analysis", "RUNNING...", "var(--warn)"],
            ["🏆 Score Calculation", "QUEUED", "var(--text3)"],
          ].map(([task, status, color]) => (
            <div key={task} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: 420, margin: "0 auto 10px", padding: "14px 20px", background: "var(--surface)", border: "1px solid var(--border)" }}>
              <span style={{ color: "var(--text2)", fontSize: "0.95rem" }}>{task}</span>
              <span style={{ color, fontFamily: "'Share Tech Mono'", fontSize: "0.78rem", animation: status.includes("RUNNING") ? "pulse 1s infinite" : "none" }}>{status}</span>
            </div>
          ))}
          <div style={{ marginTop: 32, padding: "16px 24px", background: "rgba(0,212,255,0.05)", border: "1px solid rgba(0,212,255,0.2)" }}>
            <p style={{ color: "var(--accent)", fontFamily: "'Share Tech Mono'", fontSize: "0.8rem", letterSpacing: 2 }}>
              POWERED BY CLAUDE AI · ANTHROPIC
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── PRE-EXAM CHECKLIST PAGE ──────────────────────────────────────────────────
  if (phase === "checklist") {
    const checks = [
      { icon:"🌿", label:"Plain background", desc:"Sit in front of a plain wall or neutral background. No people, posters, or distractions behind you.", key:"bg" },
      { icon:"🔇", label:"Complete silence", desc:"Ensure no voice, music, TV, or ambient noise. The microphone will detect any significant sound.", key:"voice" },
      { icon:"👤", label:"Only you on camera", desc:"No other person should be visible in the camera frame. Multiple faces = immediate termination.", key:"face" },
      { icon:"🖥️", label:"Stay on this tab", desc:"Do not switch tabs, minimise the window, or navigate away. Any window switch = immediate termination.", key:"tab" },
      { icon:"📵", label:"No external help", desc:"No second screens, phones, notes, or assistance from others. This is an individual assessment.", key:"device" },
      { icon:"💡", label:"Good lighting", desc:"Ensure your face is well-lit so the camera can monitor you clearly throughout the exam.", key:"light" },
    ];
    return (
      <div className="page">
        <Nav page="PRE-EXAM CHECKLIST" />
        <PageDecorations />
        <div style={{ maxWidth:700, margin:"40px auto", padding:"0 20px 80px", position:"relative", zIndex:2 }}>
          <StepIndicator current={1} steps={["Register","Interview","Results"]} />

          {/* Header */}
          <div style={{ textAlign:"center", marginBottom:36 }}>
            <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.62rem", letterSpacing:5, color:"var(--warn)", marginBottom:12 }}>⚠ IMPORTANT — READ BEFORE STARTING</div>
            <h2 className="section-title">BEFORE YOU <span>BEGIN</span></h2>
            <p style={{ color:"var(--text2)", fontSize:"0.95rem", lineHeight:1.7, maxWidth:520, margin:"12px auto 0" }}>
              This exam has strict integrity monitoring. Any violation will <strong style={{color:"var(--danger)"}}>immediately terminate</strong> your exam and mark you as <strong style={{color:"var(--danger)"}}>NOT ELIGIBLE</strong>.
            </p>
          </div>

          {/* Warning banner */}
          <div style={{ background:"rgba(255,176,32,0.06)", border:"2px solid rgba(255,176,32,0.3)", padding:"16px 20px", marginBottom:28, display:"flex", gap:14, alignItems:"flex-start" }}>
            <span style={{ fontSize:"1.5rem", flexShrink:0 }}>⚠️</span>
            <div>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.7rem", letterSpacing:2, color:"var(--warn)", marginBottom:6 }}>ZERO TOLERANCE POLICY</div>
              <p style={{ color:"var(--text2)", fontSize:"0.88rem", lineHeight:1.7, margin:0 }}>
                Multiple faces, detected voice/noise, or tab switching will <strong style={{color:"var(--danger)"}}>end your exam instantly</strong> with no second chance. Ensure all conditions below are met before clicking START.
              </p>
            </div>
          </div>

          {/* Checklist items */}
          <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:32 }}>
            {checks.map(c => (
              <div key={c.key} style={{ display:"flex", gap:16, alignItems:"flex-start", background:"var(--surface)", border:"1px solid var(--border)", padding:"16px 20px", position:"relative" }}>
                <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:"linear-gradient(180deg,var(--accent),var(--cyan))" }}/>
                <span style={{ fontSize:"1.6rem", flexShrink:0 }}>{c.icon}</span>
                <div>
                  <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.72rem", fontWeight:700, letterSpacing:2, color:"var(--text)", marginBottom:5 }}>{c.label.toUpperCase()}</div>
                  <div style={{ color:"var(--text2)", fontSize:"0.88rem", lineHeight:1.65 }}>{c.desc}</div>
                </div>
                <div style={{ marginLeft:"auto", flexShrink:0, width:22, height:22, border:"2px solid var(--accent)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <span style={{ color:"var(--accent)", fontSize:"0.75rem", fontWeight:900 }}>✓</span>
                </div>
              </div>
            ))}
          </div>

          {/* Immediate termination rules summary */}
          <div style={{ background:"rgba(255,59,92,0.05)", border:"1px solid rgba(255,59,92,0.2)", padding:"18px 22px", marginBottom:32 }}>
            <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.65rem", letterSpacing:3, color:"var(--danger)", marginBottom:14 }}>INSTANT TERMINATION TRIGGERS</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {[
                ["👥", "Multiple faces detected on camera"],
                ["🔊", "Voice or significant noise detected"],
                ["🖥️", "Tab switch or window navigation"],
                ["📵", "Any external assistance attempted"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display:"flex", gap:10, alignItems:"center", padding:"8px 12px", background:"rgba(255,59,92,0.06)", border:"1px solid rgba(255,59,92,0.15)" }}>
                  <span style={{ fontSize:"1rem" }}>{icon}</span>
                  <span style={{ color:"var(--text2)", fontSize:"0.82rem" }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.72rem", color:"var(--text3)", marginBottom:16, letterSpacing:1 }}>
              By clicking START, you confirm all conditions above are met
            </div>
            <button className="btn btn-primary" style={{ padding:"16px 48px", fontSize:"1rem", letterSpacing:3 }}
              onClick={() => setPhase("setup")}>
              ✅ I UNDERSTAND — START SETUP →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "setup") {
    // System check states
    const allChecksPass = sysCheck.camera === "pass" && sysCheck.mic === "pass" && sysCheck.noise === "pass" && sysCheck.network === "pass";
    const getCheckColor = (s) => s === "pass" ? "var(--accent3)" : s === "fail" ? "var(--danger)" : s === "checking" ? "var(--warn)" : "var(--text3)";
    const getCheckIcon = (s) => s === "pass" ? "✅" : s === "fail" ? "❌" : s === "checking" ? "⏳" : "⬜";
    return (
      <div className="page">
        <Nav page="SYSTEM CHECK" />
        <PageDecorations />
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "30px 20px 80px", position:"relative", zIndex:2 }}>
          <StepIndicator current={1} steps={["Register", "Interview", "Results"]} />
          <div style={{ textAlign:"center", marginBottom:28 }}>
            <h2 className="section-title">SYSTEM <span>CHECK</span></h2>
            <p className="section-subtitle">All checks must pass before you can start the exam</p>
          </div>

          {/* Camera preview + checks grid */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24 }}>

            {/* Left: Camera live preview */}
            <div style={{ background:"var(--surface)", border:"1px solid " + (sysCheck.camera === "pass" ? "var(--accent3)" : sysCheck.camera === "fail" ? "var(--danger)" : "var(--border)"), padding:16 }}>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.68rem", letterSpacing:3, color:"var(--accent)", marginBottom:12 }}>📷 CAMERA CHECK</div>
              {/* Live camera preview */}
              <div style={{ position:"relative", width:"100%", aspectRatio:"4/3", background:"#000", overflow:"hidden", marginBottom:12, border:"1px solid var(--border)" }}>
                {sysCheck.camera !== "idle" ? (
                  <video ref={videoRef} autoPlay muted playsInline style={{ width:"100%", height:"100%", objectFit:"cover", transform:"scaleX(-1)", display:"block" }} />
                ) : (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text3)", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.75rem" }}>CAMERA OFF</div>
                )}
                {sysCheck.camera === "pass" && (
                  <div style={{ position:"absolute", bottom:6, left:6, background:"rgba(0,0,0,0.7)", padding:"3px 8px", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.55rem", color:"var(--accent3)", border:"1px solid var(--accent3)" }}>✓ FACE DETECTED</div>
                )}
                {sysCheck.camera !== "idle" && (
                  <div style={{ position:"absolute", top:6, right:6, width:8, height:8, borderRadius:"50%", background:"var(--danger)", animation:"glow-pulse 1s infinite" }}/>
                )}
              </div>
              <div style={{ fontSize:"0.82rem", color: getCheckColor(sysCheck.camera), fontFamily:"'Share Tech Mono',monospace" }}>
                {sysCheck.camera === "idle" ? "Click TEST to check camera" : sysCheck.camera === "checking" ? "⏳ Detecting camera..." : sysCheck.camera === "pass" ? "✅ Camera working — face visible" : "❌ Camera unavailable or no face detected"}
              </div>
            </div>

            {/* Right: Mic + Noise + Network */}
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

              {/* Mic check */}
              <div style={{ background:"var(--surface)", border:"1px solid " + (sysCheck.mic === "pass" ? "var(--accent3)" : sysCheck.mic === "fail" ? "var(--danger)" : "var(--border)"), padding:"14px 16px" }}>
                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.65rem", letterSpacing:3, color:"var(--accent)", marginBottom:8 }}>🎙️ MICROPHONE CHECK</div>
                {/* Noise level bar */}
                <div style={{ height:8, background:"rgba(0,0,0,0.4)", border:"1px solid var(--border)", marginBottom:8, overflow:"hidden" }}>
                  <div style={{ height:"100%", width: sysCheck.noiseLevel + "%", background: sysCheck.noiseLevel > 40 ? "var(--danger)" : sysCheck.noiseLevel > 15 ? "var(--warn)" : "var(--accent3)", transition:"width 0.3s" }}/>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:"0.78rem", color:"var(--text3)", fontFamily:"'Share Tech Mono',monospace" }}>LEVEL: {Math.round(sysCheck.noiseLevel)}% {sysCheck.noiseLevel > 40 ? "⚠ TOO LOUD" : sysCheck.noiseLevel > 15 ? "⚠ MODERATE" : sysCheck.noiseLevel > 2 ? "✓ OK" : ""}</span>
                  <span style={{ fontSize:"0.75rem", color:getCheckColor(sysCheck.mic) }}>{getCheckIcon(sysCheck.mic)} {sysCheck.mic === "idle" ? "Not tested" : sysCheck.mic === "checking" ? "Testing..." : sysCheck.mic === "pass" ? "WORKING" : "FAILED"}</span>
                </div>
              </div>

              {/* Noise level check */}
              <div style={{ background:"var(--surface)", border:"1px solid " + (sysCheck.noise === "pass" ? "var(--accent3)" : sysCheck.noise === "fail" ? "var(--danger)" : "var(--border)"), padding:"14px 16px" }}>
                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.65rem", letterSpacing:3, color:"var(--accent)", marginBottom:6 }}>🔇 AMBIENT NOISE LEVEL</div>
                <div style={{ fontSize:"0.82rem", color:getCheckColor(sysCheck.noise), fontFamily:"'Share Tech Mono',monospace" }}>
                  {sysCheck.noise === "idle" ? "Will check after mic test" : sysCheck.noise === "checking" ? "⏳ Measuring 3 seconds..." : sysCheck.noise === "pass" ? "✅ Environment quiet — suitable for exam" : "❌ Too loud — find a quieter location"}
                </div>
              </div>

              {/* Network check */}
              <div style={{ background:"var(--surface)", border:"1px solid " + (sysCheck.network === "pass" ? "var(--accent3)" : sysCheck.network === "fail" ? "var(--danger)" : "var(--border)"), padding:"14px 16px" }}>
                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.65rem", letterSpacing:3, color:"var(--accent)", marginBottom:6 }}>🌐 NETWORK CHECK</div>
                <div style={{ fontSize:"0.82rem", color:getCheckColor(sysCheck.network), fontFamily:"'Share Tech Mono',monospace" }}>
                  {sysCheck.network === "idle" ? "Click TEST to check connection" : sysCheck.network === "checking" ? "⏳ Pinging server..." : sysCheck.network === "pass" ? "✅ Connected — " + (sysCheck.ping || "—") + "ms latency" : "❌ No connection — check your internet"}
                </div>
              </div>

            </div>
          </div>

          {/* Status summary bar */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:24 }}>
            {[
              ["📷 Camera", sysCheck.camera], ["🎙️ Microphone", sysCheck.mic],
              ["🔇 Noise", sysCheck.noise], ["🌐 Network", sysCheck.network],
            ].map(([label, status]) => (
              <div key={label} style={{ padding:"10px 12px", background: status === "pass" ? "rgba(0,255,136,0.05)" : status === "fail" ? "rgba(255,59,92,0.05)" : "var(--surface)", border:"1px solid " + (status === "pass" ? "rgba(0,255,136,0.3)" : status === "fail" ? "rgba(255,59,92,0.3)" : "var(--border)"), textAlign:"center" }}>
                <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.6rem", letterSpacing:2, color:"var(--text3)", marginBottom:4 }}>{label}</div>
                <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.68rem", color:getCheckColor(status), fontWeight:700 }}>{status === "idle" ? "NOT TESTED" : status.toUpperCase()}</div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display:"flex", gap:14, marginBottom:20 }}>
            <button className="btn btn-outline" style={{ flex:1 }} onClick={runSystemCheck} disabled={sysCheck.camera === "checking" || sysCheck.mic === "checking" || sysCheck.network === "checking"}>
              {(sysCheck.camera === "checking" || sysCheck.mic === "checking" || sysCheck.network === "checking") ? "⏳ RUNNING TESTS..." : allChecksPass ? "🔄 RE-RUN TESTS" : "▶ RUN SYSTEM CHECK"}
            </button>
            <button className="btn btn-primary" style={{ flex:2, opacity: allChecksPass ? 1 : 0.4, cursor: allChecksPass ? "pointer" : "not-allowed" }}
              disabled={!allChecksPass} onClick={startInterview}>
              {allChecksPass ? "✅ ALL CHECKS PASSED — BEGIN EXAM →" : "PASS ALL CHECKS TO BEGIN"}
            </button>
          </div>

          {!allChecksPass && sysCheck.camera !== "idle" && (
            <div style={{ background:"rgba(255,176,32,0.05)", border:"1px solid rgba(255,176,32,0.2)", padding:"12px 16px", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.72rem", color:"var(--warn)", letterSpacing:1 }}>
              ⚠ All 4 checks must show ✅ PASS before the Begin Exam button activates. Fix any failing checks and re-run.
            </div>
          )}

          <div style={{ marginTop:20 }} className="console">
            <div className="console-line ok">[SYSTEM] Internastra IIRT v2.5 initialized</div>
            <div className="console-line info">[AUTH] {candidate.candidateId} authenticated</div>
            {sysCheck.camera === "pass" && <div className="console-line ok">[CAMERA] ✓ Feed active — face detected</div>}
            {sysCheck.camera === "fail" && <div className="console-line warn">[CAMERA] ✗ Camera check failed</div>}
            {sysCheck.mic === "pass" && <div className="console-line ok">[MIC] ✓ Microphone working</div>}
            {sysCheck.noise === "fail" && <div className="console-line warn">[NOISE] ✗ Ambient noise too high — move to quieter location</div>}
            {sysCheck.noise === "pass" && <div className="console-line ok">[NOISE] ✓ Quiet environment confirmed</div>}
            {sysCheck.network === "pass" && <div className="console-line ok">[NETWORK] ✓ Connected — {sysCheck.ping}ms</div>}
            {allChecksPass && <div className="console-line ok">[READY] ✓ All systems operational — you may begin the exam</div>}
          </div>

          <div style={{ marginTop:16, background:"var(--surface)", border:"1px solid var(--border)", padding:"14px 18px" }}>
            <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.65rem", letterSpacing:2, color:"var(--accent)", marginBottom:8 }}>CANDIDATE</div>
            <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
              <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.75rem", color:"var(--accent)" }}>{candidate.candidateId}</span>
              <span style={{ color:"var(--text2)", fontSize:"0.85rem" }}>{candidate.form.name}</span>
              <span style={{ color:"var(--text3)", fontSize:"0.82rem" }}>{candidate.form.role}</span>
            </div>
          </div>
        </div>
        {modal && <Modal {...modal} onClose={() => setModal(null)} />}
      </div>
    );
  }

  // INTERVIEW PHASE
  const q = questions[currentQ];
  return (
    <div className="page">
      <Nav page="LIVE INTERVIEW" hideHome={true} />
      <PageDecorations />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px" }}>
        {/* Top Bar */}
        <div className="status-row">
          <div className="status-chip"><div className="status-dot dot-red" /> LIVE</div>
          <div className="status-chip"><div className="status-dot dot-green" />MONITORING ACTIVE</div>
          <div className="status-chip"><div className={`status-dot ${faceCount > 1 ? "dot-red" : "dot-green"}`} />FACES: {faceCount}{faceViolationCountRef.current > 0 ? ` (${faceViolationCountRef.current} VIOL)` : ""}</div>
          <div className="status-chip"><div className={`status-dot ${tabWarnings >= 2 ? "dot-red" : tabWarnings > 0 ? "dot-yellow" : "dot-blue"}`} />TAB: {tabWarnings === 0 ? "SECURE" : tabWarnings === 1 ? "1 WARN" : "TERMINATED"}</div>
          <div className="status-chip"><div className="status-dot dot-blue" />Q {currentQ + 1}/20</div>
          <div style={{ marginLeft: "auto" }}>
            <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.75rem", color: "var(--text3)", letterSpacing: 2 }}>{candidate.candidateId}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="progress-bar" style={{ marginBottom: 24 }}>
          <div className="progress-fill" style={{ width: `${(currentQ / 20) * 100}%` }} />
        </div>

        {/* Monitoring strip */}
        <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            ["🔒", "Background Monitoring", "ACTIVE", "var(--accent3)", false],
            ["👤", "Face Detection", faceCount > 1 ? "⚠ MULTIPLE FACES" : faceApiReadyRef.current ? "ACTIVE (REAL)" : "ACTIVE (SIM)", faceCount > 1 ? "var(--danger)" : "var(--accent3)", faceCount > 1],
            ["🔊", "Voice Monitor", voiceDetected ? "⚠ TERMINATED" : voiceWarningCountRef.current > 0 ? "⚠ WARN " + voiceWarningCountRef.current + "/2" : audioContextRef.current ? "ACTIVE" : "STANDBY", voiceDetected || voiceWarningCountRef.current > 0 ? "var(--danger)" : audioContextRef.current ? "var(--accent3)" : "var(--text3)", voiceDetected || voiceWarningCountRef.current > 0],
            ["🖥️", "Tab Security", tabWarnings >= 2 ? "TERMINATED" : tabWarnings === 1 ? "⚠ WARN 1/2" : "SECURE", tabWarnings >= 2 ? "var(--danger)" : tabWarnings === 1 ? "var(--warn)" : "var(--accent3)", tabWarnings > 0],
          ].map(([icon, label, status, color, alert]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", background: alert ? "rgba(255,59,92,0.06)" : "var(--surface)", border: alert ? "1px solid var(--danger)" : "1px solid var(--border)", flex: 1, minWidth: 160 }}>
              <span>{icon}</span>
              <span style={{ color: "var(--text2)", fontSize: "0.82rem" }}>{label}</span>
              <span style={{ marginLeft: "auto", color, fontFamily: "'Share Tech Mono'", fontSize: "0.72rem", letterSpacing: 1 }}>{status}</span>
            </div>
          ))}
        </div>

        {/* Live Camera Feed Overlay — bottom right */}
        <div style={{ position:"fixed", bottom:20, right:20, zIndex:999, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
          <div style={{ position:"relative", width:160, height:120, border:"2px solid " + (faceCount > 1 ? "var(--danger)" : "rgba(0,212,255,0.4)"), background:"#000", overflow:"hidden", boxShadow:"0 0 20px rgba(0,0,0,0.8)" }}>
            <video ref={videoRef} autoPlay muted playsInline style={{ width:"100%", height:"100%", objectFit:"cover", transform:"scaleX(-1)" }} />
            {/* Face count badge */}
            <div style={{ position:"absolute", top:4, left:4, background: faceCount > 1 ? "var(--danger)" : "rgba(0,0,0,0.7)", border:"1px solid " + (faceCount > 1 ? "var(--danger)" : "rgba(0,212,255,0.5)"), padding:"2px 7px", fontFamily:"'Share Tech Mono',monospace", fontSize:"0.55rem", color: faceCount > 1 ? "#fff" : "var(--accent)", letterSpacing:1 }}>
              {faceCount > 1 ? "⚠ " + faceCount + " FACES" : "👤 LIVE"}
            </div>
            {/* Recording dot */}
            <div style={{ position:"absolute", top:5, right:6, width:7, height:7, borderRadius:"50%", background:"var(--danger)", animation:"glow-pulse 1s infinite" }}/>
            {faceCount > 1 && (
              <div style={{ position:"absolute", inset:0, border:"3px solid var(--danger)", animation:"glow-pulse 0.5s infinite", pointerEvents:"none" }}/>
            )}
          </div>
          <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.52rem", color:"rgba(0,212,255,0.5)", letterSpacing:1, textAlign:"right" }}>
            CAMERA MONITORING ACTIVE
          </div>
        </div>

        {/* Main Question Area — full width */}
        <div style={{ maxWidth: 760, margin: "0 auto" }}>

          {/* Question header with timer */}
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 20 }}>
            {/* 30-sec countdown ring */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <svg width="76" height="76" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="44" fill="none" stroke="var(--border)" strokeWidth="8" />
                <circle cx="50" cy="50" r="44" fill="none"
                  stroke={timeLeft <= 10 ? "var(--danger)" : timeLeft <= 20 ? "var(--warn)" : "var(--accent)"}
                  strokeWidth="8"
                  strokeDasharray={String(2 * Math.PI * 44)}
                  strokeDashoffset={String(2 * Math.PI * 44 * (1 - timeLeft / 30))}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s", transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
                />
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "'Orbitron'", fontSize: "1.3rem", fontWeight: 900, color: timeLeft <= 10 ? "var(--danger)" : "var(--accent)", lineHeight: 1 }}>{timeLeft}</span>
                <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.5rem", color: "var(--text3)", letterSpacing: 1 }}>SEC</span>
              </div>
            </div>
            {/* Question meta */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                <div className={`badge ${q?.category === "Numerical Aptitude" ? "badge-warn" : q?.category === "Reasoning" ? "badge-green" : "badge-accent"}`} style={{ fontSize: "0.72rem" }}>
                  {q?.category === "Numerical Aptitude" ? "🔢 Aptitude" : q?.category === "Reasoning" ? "🧠 Reasoning" : "⚙️ " + (q?.domain || "Domain")}
                </div>
                {timeLeft <= 10 && (
                  <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.68rem", color: "var(--danger)", animation: "pulse 0.5s infinite", letterSpacing: 1 }}>⚠ TIME RUNNING OUT!</span>
                )}
              </div>
              <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.68rem", color: "var(--text3)", letterSpacing: 2 }}>
                Q {currentQ + 1} / {questions.length} · {candidate?.form?.role?.toUpperCase()}
              </div>
            </div>
            {/* Q counter */}
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontFamily: "'Orbitron'", fontSize: "1.4rem", fontWeight: 900, color: "var(--accent)", lineHeight: 1 }}>{currentQ + 1}</div>
              <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.6rem", color: "var(--text3)" }}>OF {questions.length}</div>
            </div>
          </div>

          {/* Question text — IDE highlighted for Coding, plain for others */}
          <div className="card" style={{ marginBottom: 24, padding: q?.category === "Coding" ? "16px" : "28px 28px" }}>
            {q?.category === "Coding" ? (
              <CodeHighlight code={q.text || ""} theme={_navCb.theme} />
            ) : (
              <p style={{ fontSize: "1.08rem", lineHeight: 1.8, color: "var(--text)", fontWeight: 600, margin: 0, whiteSpace: "pre-wrap" }}>
                {q?.text}
              </p>
            )}
          </div>

          {/* Clickable MCQ Options */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
            {["A","B","C","D"].map(opt => {
              const isSelected = answers[currentQ] === opt;
              return (
                <button
                  key={opt}
                  onClick={() => selectOption(opt)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 16, padding: "16px 20px",
                    background: isSelected ? "rgba(0,212,255,0.1)" : "var(--surface)",
                    border: isSelected ? "2px solid var(--accent)" : "1px solid var(--border)",
                    cursor: "pointer", textAlign: "left", width: "100%",
                    transition: "all 0.15s ease", outline: "none",
                    boxShadow: isSelected ? "0 0 20px rgba(0,212,255,0.15)" : "none",
                  }}
                >
                  <span style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 32, height: 32, flexShrink: 0,
                    background: isSelected ? "var(--accent)" : "var(--bg)",
                    border: isSelected ? "2px solid var(--accent)" : "2px solid var(--border)",
                    borderRadius: "50%",
                    fontFamily: "'Orbitron'", fontSize: "0.75rem", fontWeight: 900,
                    color: isSelected ? "#000" : "var(--text3)",
                    transition: "all 0.15s ease",
                  }}>{opt}</span>
                  <span style={{
                    fontSize: "0.95rem", color: isSelected ? "var(--text)" : "var(--text2)",
                    lineHeight: 1.6, paddingTop: 5, flex: 1, transition: "color 0.15s"
                  }}>
                    {q?.options?.[opt] || ""}
                  </span>
                  {isSelected && (
                    <span style={{ color: "var(--accent3)", fontSize: "1.1rem", flexShrink: 0, paddingTop: 5 }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer: selection status + navigation */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div style={{ padding: "12px 18px", background: "var(--surface)", border: answers[currentQ] ? "1px solid rgba(0,255,136,0.4)" : "1px solid var(--border)", fontSize: "0.85rem", flex: 1 }}>
              {answers[currentQ]
                ? <span style={{ color: "var(--accent3)" }}>✓ Option <strong>{answers[currentQ]}</strong> selected</span>
                : <span style={{ color: "var(--text3)", fontStyle: "italic" }}>Click an option above to answer</span>}
            </div>
            <button
              className="btn btn-primary"
              style={{ flexShrink: 0, padding: "12px 32px", fontSize: "0.95rem" }}
              onClick={handleNextQuestion}
            >
              {currentQ >= 19 ? "SUBMIT TEST →" : "NEXT QUESTION →"}
            </button>
          </div>
        </div>
      </div>
      {modal && (
        <Modal {...modal}
          onClose={modal.noClose ? undefined : () => setModal(null)}
          onConfirm={() => { setTerminated(true); setModal(null); }}
          confirmLabel={modal.type === "danger" ? "Acknowledge" : undefined}
        />
      )}
    </div>
  );
}

// RESULTS PAGE


// ── PDF REPORT CARD GENERATOR ─────────────────────────────────────────────────
function generateReportCardPDF(candidate, result, questions, evaluationScores, feedbacks, percentage) {
  const freeI = percentage >= 90;
  const discI  = percentage >= 50 && percentage < 90;
  const statusColor = freeI ? "#00994d" : discI ? "#b36200" : "#cc0022";
  const statusText  = freeI ? "FREE INTERNSHIP" : discI ? "50% DISCOUNT" : "NOT ELIGIBLE";
  const statusBg    = freeI ? "#e6fff4" : discI ? "#fff8e6" : "#fff0f0";
  const date = new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" });

  // Category score breakdown
  const catGroups = {};
  questions.forEach((q, i) => {
    const cat = q.category || "Domain";
    if (!catGroups[cat]) catGroups[cat] = { earned:0, max:0, count:0 };
    const mx = getMaxScore(cat);
    catGroups[cat].earned += evaluationScores[i] || 0;
    catGroups[cat].max    += mx;
    catGroups[cat].count  += 1;
  });
  const totalMax = Object.values(catGroups).reduce((s,c) => s + c.max, 0);
  const totalEarned = evaluationScores.reduce((a,b) => a+b, 0);

  const qaRows = questions.map((q, i) => {
    const selected = result.answers?.[i] || "—";
    const correct  = q.options?.correct || "—";
    const isOk     = selected === correct;
    const sc       = evaluationScores[i] || 0;
    const mx       = getMaxScore(q.category);
    return `
      <tr style="border-bottom:1px solid #e0e0e0; background:${i%2===0?"#fafafa":"#fff"}">
        <td style="padding:6px 8px;font-size:11px;color:#555">${i+1}</td>
        <td style="padding:6px 8px;font-size:11px;color:#555">${q.category || "Domain"}</td>
        <td style="padding:6px 8px;font-size:11px;color:#222;max-width:260px">${(q.text||"").slice(0,90)}${(q.text||"").length>90?"…":""}</td>
        <td style="padding:6px 8px;font-size:12px;font-weight:700;color:${isOk?"#00994d":"#cc0022"};text-align:center">${selected}</td>
        <td style="padding:6px 8px;font-size:12px;text-align:center;color:#009944">${correct}</td>
        <td style="padding:6px 8px;font-size:12px;font-weight:700;text-align:center;color:${isOk?"#00994d":"#cc0022"}">${sc}/${mx}</td>
      </tr>`;
  }).join("");

  const catBreakdown = Object.entries(catGroups).map(([cat, g]) => `
    <div style="display:inline-block;background:#f5f5f5;border:1px solid #ddd;border-radius:4px;padding:10px 18px;margin:6px 8px 6px 0;text-align:center;min-width:130px">
      <div style="font-size:11px;color:#888;margin-bottom:4px;text-transform:uppercase;letter-spacing:1px">${cat}</div>
      <div style="font-size:20px;font-weight:900;color:${statusColor}">${g.earned}/${g.max}</div>
      <div style="font-size:10px;color:#aaa">${g.count} question${g.count>1?"s":""} × ${g.max/g.count}pts</div>
    </div>`).join("");

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>IIRT Report Card — ${candidate.form?.name || ""}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&family=Share+Tech+Mono&display=swap');
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Rajdhani',sans-serif; background:#fff; color:#222; }
  @media print {
    body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .no-print { display:none !important; }
  }
  .page { max-width:800px; margin:0 auto; padding:32px 36px; }
  table { width:100%; border-collapse:collapse; }
  th { background:#1a3a6c; color:#fff; padding:8px; font-size:11px; text-align:left; letter-spacing:1px; text-transform:uppercase; }
</style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div style="display:flex;align-items:center;justify-content:space-between;padding-bottom:16px;border-bottom:3px solid #1a3a6c;margin-bottom:24px">
    <div>
      <div style="font-family:'Share Tech Mono',monospace;font-size:22px;font-weight:900;color:#1a3a6c;letter-spacing:3px">INTERNASTRA</div>
      <div style="font-size:11px;color:#666;letter-spacing:4px;margin-top:2px">INTERNSHIP READINESS TEST — IIRT</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:11px;color:#888">Report Generated</div>
      <div style="font-size:13px;color:#333;font-weight:600">${date}</div>
      <div style="font-family:'Share Tech Mono',monospace;font-size:10px;color:#aaa;margin-top:2px">${candidate.candidateId}</div>
    </div>
  </div>

  <!-- Result Banner -->
  <div style="background:${statusBg};border:2px solid ${statusColor};border-radius:6px;padding:18px 24px;margin-bottom:24px;display:flex;align-items:center;justify-content:space-between">
    <div>
      <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:2px;margin-bottom:4px">Final Result</div>
      <div style="font-size:28px;font-weight:900;color:${statusColor};letter-spacing:2px">${statusText}</div>
    </div>
    <div style="text-align:right">
      <div style="font-size:56px;font-weight:900;color:${statusColor};line-height:1">${percentage}%</div>
      <div style="font-size:11px;color:#888">${totalEarned} / ${totalMax} pts</div>
    </div>
  </div>

  <!-- Candidate Info + Score Breakdown (2-col) -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px">
    <div style="border:1px solid #ddd;border-radius:4px;padding:16px">
      <div style="font-size:11px;color:#1a3a6c;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:12px;border-bottom:1px solid #eee;padding-bottom:6px">Candidate Details</div>
      ${[
        ["Name",          candidate.form?.name       || "—"],
        ["Email",         candidate.form?.email      || "—"],
        ["Phone",         candidate.form?.phone      || "—"],
        ["Department",    candidate.form?.department || "—"],
        ["Degree",        candidate.form?.degree     || "—"],
        ["College",       candidate.form?.college    || "—"],
        ["Year of Study", candidate.form?.yearOfStudy || "—"],
        ["Year of Passout",candidate.form?.yearOfPassout || "—"],
        ["Domain Applied",candidate.form?.role       || "—"],
      ].map(([k,v]) => `<div style="display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #f0f0f0;font-size:12px"><span style="color:#888">${k}</span><span style="font-weight:600;text-align:right;max-width:55%">${v}</span></div>`).join("")}
    </div>
    <div style="border:1px solid #ddd;border-radius:4px;padding:16px">
      <div style="font-size:11px;color:#1a3a6c;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:12px;border-bottom:1px solid #eee;padding-bottom:6px">Score Breakdown</div>
      ${catBreakdown}
      <div style="margin-top:12px;padding-top:10px;border-top:1px solid #ddd">
        <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px">Scoring Key</div>
        <div style="font-size:11px;color:#555;margin-top:4px">Domain Questions: <strong>8 pts each</strong></div>
        <div style="font-size:11px;color:#555">Aptitude / Reasoning: <strong>2 pts each</strong></div>
        <div style="font-size:11px;color:#555">Total Maximum: <strong>${totalMax} pts</strong></div>
      </div>
    </div>
  </div>

  <!-- Security Report -->
  <div style="border:1px solid #ddd;border-radius:4px;padding:16px;margin-bottom:24px">
    <div style="font-size:11px;color:#1a3a6c;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:12px;border-bottom:1px solid #eee;padding-bottom:6px">Security &amp; Integrity Report</div>
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px">
      ${[
        ["Face Violations", result.faceViolations || 0, (result.faceViolations||0) > 0],
        ["Tab Switches",    result.tabWarnings    || 0, (result.tabWarnings||0) > 0],
        ["Voice Warnings",  result.voiceWarnings  || 0, (result.voiceWarnings||0) > 0],
        ["Plagiarism",      "Pass",                     false],
      ].map(([l,v,alert]) => `<div style="text-align:center;padding:10px;background:${alert?"#fff0f0":"#f9f9f9"};border:1px solid ${alert?"#ffcccc":"#e0e0e0"};border-radius:3px"><div style="font-size:10px;color:#888;margin-bottom:4px;text-transform:uppercase">${l}</div><div style="font-size:16px;font-weight:700;color:${alert?"#cc0022":"#009944"}">${v}</div></div>`).join("")}
    </div>
  </div>

  <!-- Q&A Table -->
  <div style="margin-bottom:24px">
    <div style="font-size:11px;color:#1a3a6c;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:12px">Detailed Answer Review (${questions.length} Questions)</div>
    <table>
      <thead><tr><th>#</th><th>Category</th><th>Question</th><th>Selected</th><th>Correct</th><th>Score</th></tr></thead>
      <tbody>${qaRows}</tbody>
    </table>
  </div>

  <!-- Footer -->
  <div style="border-top:2px solid #1a3a6c;padding-top:12px;display:flex;justify-content:space-between;align-items:center">
    <div style="font-size:10px;color:#aaa;letter-spacing:1px">INTERNASTRA INTERNSHIP READINESS TEST · AI-POWERED ASSESSMENT</div>
    <div style="font-size:10px;color:#aaa">© 2025 Internastra · Confidential</div>
  </div>

  <!-- Print button -->
  <div class="no-print" style="margin-top:24px;text-align:center">
    <button onclick="window.print()" style="padding:12px 36px;background:#1a3a6c;color:#fff;border:none;border-radius:4px;font-size:14px;font-weight:600;cursor:pointer;letter-spacing:1px">
      🖨️ PRINT / SAVE AS PDF
    </button>
    <p style="font-size:11px;color:#aaa;margin-top:8px">In the print dialog, choose "Save as PDF" as the destination</p>
  </div>

</div>
</body>
</html>`;

  const w = window.open("", "_blank");
  if (w) {
    w.document.write(html);
    w.document.close();
  } else {
    alert("Please allow popups to download the PDF report.");
  }
}

// ── ANIMATED RESULT CHARACTER ──────────────────────────────────────────────────
function AnimCharacter({ type }) {
  // type: "free" | "discount" | "noteligible" | "violation"
  const charRef = useRef(null);

  // Confetti particles for celebrations
  const [confetti] = useState(() =>
    type === "free" || type === "discount"
      ? Array.from({ length: type === "free" ? 55 : 35 }, (_, i) => ({
          id: i,
          x: 30 + Math.random() * 40,
          left: Math.random() * 100,
          color: ["#00d4ff","#00ff88","#ffb020","#ff6eb4","#b06ef0","#fff"][Math.floor(Math.random()*6)],
          size: Math.random() * 7 + 4,
          dur: Math.random() * 2 + 1.8,
          delay: Math.random() * 1.2,
          rot: Math.random() * 360,
          shape: Math.random() > 0.5 ? "circle" : "rect",
        }))
      : []
  );

  // SVG character definitions
  const chars = {
    free: (
      // Jumping celebrating person — arms up, big smile
      <svg viewBox="0 0 120 160" width="120" height="160">
        <defs>
          <radialGradient id="gfree" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00ff88" stopOpacity="0.25"/>
            <stop offset="100%" stopColor="#00ff88" stopOpacity="0"/>
          </radialGradient>
        </defs>
        {/* Glow */}
        <ellipse cx="60" cy="150" rx="40" ry="8" fill="rgba(0,255,136,0.18)" style={{animation:"glow-pulse 1.2s infinite"}}/>
        {/* Shadow */}
        <ellipse cx="60" cy="155" rx="22" ry="4" fill="rgba(0,0,0,0.25)"/>
        {/* Body — jumping pose */}
        <g style={{animation:"jump 0.7s ease-in-out infinite alternate", transformOrigin:"60px 100px"}}>
          {/* Trophy above head */}
          <g style={{animation:"trophy-bounce 0.7s ease-in-out infinite alternate", transformOrigin:"60px 10px"}}>
            <text x="60" y="22" textAnchor="middle" fontSize="22">🏆</text>
          </g>
          {/* Head */}
          <circle cx="60" cy="48" r="20" fill="#fde68a" stroke="#f59e0b" strokeWidth="2"/>
          {/* Hair */}
          <ellipse cx="60" cy="30" rx="20" ry="10" fill="#92400e"/>
          <rect x="41" y="28" width="38" height="10" rx="5" fill="#92400e"/>
          {/* Eyes — happy */}
          <path d="M51 45 Q54 42 57 45" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M63 45 Q66 42 69 45" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Big smile */}
          <path d="M50 53 Q60 63 70 53" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Cheeks */}
          <circle cx="49" cy="54" r="5" fill="rgba(255,100,100,0.25)"/>
          <circle cx="71" cy="54" r="5" fill="rgba(255,100,100,0.25)"/>
          {/* Body */}
          <rect x="44" y="68" width="32" height="38" rx="8" fill="#00aa55"/>
          {/* Star badge */}
          <text x="60" y="93" textAnchor="middle" fontSize="14">⭐</text>
          {/* Arms up — celebrating */}
          <line x1="44" y1="75" x2="22" y2="52" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="76" y1="75" x2="98" y2="52" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          {/* Hands */}
          <circle cx="22" cy="52" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          <circle cx="98" cy="52" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          {/* Legs — jumping */}
          <line x1="54" y1="106" x2="44" y2="135" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="66" y1="106" x2="76" y2="135" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          {/* Feet */}
          <ellipse cx="42" cy="138" rx="10" ry="6" fill="#1e3a5f"/>
          <ellipse cx="78" cy="138" rx="10" ry="6" fill="#1e3a5f"/>
        </g>
      </svg>
    ),
    discount: (
      // Happy thumbs-up person
      <svg viewBox="0 0 120 160" width="120" height="160">
        <ellipse cx="60" cy="155" rx="22" ry="4" fill="rgba(0,0,0,0.2)"/>
        <g style={{animation:"bob 1.2s ease-in-out infinite alternate", transformOrigin:"60px 100px"}}>
          {/* Head */}
          <circle cx="60" cy="48" r="20" fill="#fde68a" stroke="#f59e0b" strokeWidth="2"/>
          <ellipse cx="60" cy="30" rx="20" ry="10" fill="#78350f"/>
          <rect x="41" y="28" width="38" height="10" rx="5" fill="#78350f"/>
          {/* Eyes smile */}
          <path d="M51 45 Q54 42 57 45" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M63 45 Q66 42 69 45" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Smile */}
          <path d="M52 54 Q60 62 68 54" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="50" cy="54" r="4" fill="rgba(255,100,100,0.2)"/>
          <circle cx="70" cy="54" r="4" fill="rgba(255,100,100,0.2)"/>
          {/* Body */}
          <rect x="44" y="68" width="32" height="38" rx="8" fill="#f59e0b"/>
          <text x="60" y="93" textAnchor="middle" fontSize="13">🎟</text>
          {/* Thumbs up arm */}
          <line x1="76" y1="78" x2="98" y2="68" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <circle cx="98" cy="68" r="8" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          <text x="98" y="72" textAnchor="middle" fontSize="10">👍</text>
          {/* Other arm */}
          <line x1="44" y1="78" x2="30" y2="90" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <circle cx="28" cy="92" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          {/* Legs */}
          <line x1="54" y1="106" x2="50" y2="140" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="66" y1="106" x2="70" y2="140" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <ellipse cx="48" cy="143" rx="10" ry="6" fill="#1e3a5f"/>
          <ellipse cx="72" cy="143" rx="10" ry="6" fill="#1e3a5f"/>
          {/* Ticket badge */}
          <text x="60" y="22" textAnchor="middle" fontSize="18" style={{animation:"badge-float 1s ease-in-out infinite alternate"}}>🎉</text>
        </g>
      </svg>
    ),
    noteligible: (
      // Sad drooping person
      <svg viewBox="0 0 120 160" width="120" height="160">
        <ellipse cx="60" cy="157" rx="22" ry="4" fill="rgba(0,0,0,0.2)"/>
        <g style={{animation:"sad-droop 2s ease-in-out infinite alternate", transformOrigin:"60px 100px"}}>
          {/* Head */}
          <circle cx="60" cy="55" r="20" fill="#fde68a" stroke="#f59e0b" strokeWidth="2"/>
          <ellipse cx="60" cy="37" rx="20" ry="10" fill="#4b5563"/>
          <rect x="41" y="35" width="38" height="10" rx="5" fill="#4b5563"/>
          {/* Sad eyes */}
          <circle cx="53" cy="52" r="3.5" fill="#1e3a5f"/>
          <circle cx="67" cy="52" r="3.5" fill="#1e3a5f"/>
          {/* Tears */}
          <ellipse cx="50" cy="59" rx="2" ry="4" fill="rgba(100,160,255,0.7)" style={{animation:"tear-drop 1.5s ease-in infinite"}}/>
          <ellipse cx="70" cy="60" rx="2" ry="4" fill="rgba(100,160,255,0.7)" style={{animation:"tear-drop 1.5s ease-in 0.7s infinite"}}/>
          {/* Frown */}
          <path d="M51 63 Q60 57 69 63" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Body */}
          <rect x="44" y="75" width="32" height="38" rx="8" fill="#6b7280"/>
          <text x="60" y="99" textAnchor="middle" fontSize="13">😔</text>
          {/* Arms drooping */}
          <line x1="44" y1="82" x2="26" y2="105" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="76" y1="82" x2="94" y2="105" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <circle cx="24" cy="107" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          <circle cx="96" cy="107" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          {/* Legs */}
          <line x1="54" y1="113" x2="50" y2="147" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="66" y1="113" x2="70" y2="147" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <ellipse cx="48" cy="150" rx="10" ry="6" fill="#374151"/>
          <ellipse cx="72" cy="150" rx="10" ry="6" fill="#374151"/>
          {/* Question mark cloud */}
          <text x="60" y="20" textAnchor="middle" fontSize="18" opacity="0.5" style={{animation:"badge-float 2s ease-in-out infinite alternate"}}>❓</text>
        </g>
      </svg>
    ),
    violation: (
      // Alarmed/caught person with warning sign
      <svg viewBox="0 0 120 170" width="120" height="170">
        <ellipse cx="60" cy="160" rx="22" ry="4" fill="rgba(0,0,0,0.2)"/>
        <g style={{animation:"shake 0.4s ease-in-out infinite alternate", transformOrigin:"60px 90px"}}>
          {/* Warning sign above */}
          <text x="60" y="18" textAnchor="middle" fontSize="22" style={{animation:"badge-float 0.6s ease-in-out infinite alternate"}}>⚠️</text>
          {/* Head — surprised expression */}
          <circle cx="60" cy="52" r="20" fill="#fde68a" stroke="#f59e0b" strokeWidth="2"/>
          <ellipse cx="60" cy="34" rx="20" ry="10" fill="#92400e"/>
          <rect x="41" y="32" width="38" height="10" rx="5" fill="#92400e"/>
          {/* Wide surprised eyes */}
          <circle cx="52" cy="48" r="6" fill="white" stroke="#1e3a5f" strokeWidth="1.5"/>
          <circle cx="68" cy="48" r="6" fill="white" stroke="#1e3a5f" strokeWidth="1.5"/>
          <circle cx="53" cy="49" r="3" fill="#1e3a5f"/>
          <circle cx="69" cy="49" r="3" fill="#1e3a5f"/>
          {/* Open shocked mouth */}
          <ellipse cx="60" cy="62" rx="7" ry="5" fill="#1e3a5f"/>
          {/* Sweat drop */}
          <ellipse cx="78" cy="40" rx="3" ry="5" fill="rgba(100,180,255,0.8)" style={{animation:"tear-drop 1s ease-in infinite"}}/>
          {/* Body */}
          <rect x="44" y="72" width="32" height="38" rx="8" fill="#dc2626"/>
          <text x="60" y="97" textAnchor="middle" fontSize="13">🚫</text>
          {/* Arms — hands up in shock */}
          <line x1="44" y1="80" x2="20" y2="60" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="76" y1="80" x2="100" y2="60" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <circle cx="18" cy="58" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          <circle cx="102" cy="58" r="7" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
          {/* Legs */}
          <line x1="54" y1="110" x2="48" y2="145" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <line x1="66" y1="110" x2="72" y2="145" stroke="#fde68a" strokeWidth="10" strokeLinecap="round"/>
          <ellipse cx="46" cy="148" rx="10" ry="6" fill="#7f1d1d"/>
          <ellipse cx="74" cy="148" rx="10" ry="6" fill="#7f1d1d"/>
        </g>
      </svg>
    ),
  };

  return (
    <div style={{ position:"relative", display:"inline-block" }}>
      {/* Confetti for celebrations */}
      {confetti.map(c => (
        <div key={c.id} style={{
          position:"absolute",
          left: c.left + "%",
          top: "-20px",
          width: c.shape === "circle" ? c.size : c.size * 0.7,
          height: c.shape === "circle" ? c.size : c.size * 1.4,
          borderRadius: c.shape === "circle" ? "50%" : "1px",
          background: c.color,
          animation: `confetti-fall ${c.dur}s ${c.delay}s ease-in infinite`,
          transform: `rotate(${c.rot}deg)`,
          opacity: 0.9,
          pointerEvents: "none",
        }} />
      ))}
      {chars[type] || chars.noteligible}

      <style>{`
        @keyframes jump {
          0%   { transform: translateY(0) rotate(-3deg); }
          100% { transform: translateY(-18px) rotate(3deg); }
        }
        @keyframes trophy-bounce {
          0%   { transform: rotate(-10deg) scale(1); }
          100% { transform: rotate(10deg) scale(1.15); }
        }
        @keyframes bob {
          0%   { transform: translateY(0) rotate(-1deg); }
          100% { transform: translateY(-10px) rotate(1deg); }
        }
        @keyframes sad-droop {
          0%   { transform: translateY(0) rotate(-2deg); }
          100% { transform: translateY(5px) rotate(2deg); }
        }
        @keyframes tear-drop {
          0%   { opacity:0; transform: translateY(0); }
          50%  { opacity:1; }
          100% { opacity:0; transform: translateY(14px); }
        }
        @keyframes confetti-fall {
          0%   { opacity:1; transform: translateY(0) rotate(0deg); }
          80%  { opacity:0.8; }
          100% { opacity:0; transform: translateY(200px) rotate(720deg); }
        }
      `}</style>
    </div>
  );
}

function ResultsPage({ candidate, result, onAdmin }) {
  const { percentage, answers, questions, aiEvalScores, aiFeedbacks } = result;
  const freeInternship = percentage >= 90;
  const discountInternship = percentage >= 50 && percentage < 90;
  const [showDetails, setShowDetails] = useState(false);

  // Use real AI scores if available, else fallback
  const evaluationScores = aiEvalScores || answers.map(a => {
    if (!a || a.trim().length < 2) return 0;
    return Math.round((0.5 + Math.random() * 0.5) * 10);
  });
  const feedbacks = aiFeedbacks || answers.map(() => "Evaluated by AI.");

  const totalAI = evaluationScores.reduce((a, b) => a + b, 0);
  const maxAI = questions.reduce((s, q) => s + getMaxScore(q.category), 0) || 100;
  const aiPct = Math.round((totalAI / maxAI) * 100);

  // Save to Excel DB on mount
  useEffect(() => { (async () => {
    const record = {
      candidateId: candidate.candidateId,
      name: candidate.form.name,
      email: candidate.form.email,
      phone: candidate.form.phone,
      degree: candidate.form.degree,
      college: candidate.form.college,
      role: candidate.form.role,
      department: candidate.form.department || "",
      yearOfStudy: candidate.form.yearOfStudy || "",
      yearOfPassout: candidate.form.yearOfPassout || "",
      experience: candidate.form.yearOfStudy || "",
      registeredAt: new Date().toISOString(),
      percentage,
      questionsAnswered: answers.filter(a => a && a.trim().length > 0).length,
      aiScore: aiPct,
      faceViolations: result.faceViolations || 0,
      tabWarnings: result.tabWarnings || 0,
      voiceWarnings: result.voiceWarnings || 0,
      referral: getReferralCode(),
      externalDevices: "None Detected",
      plagiarism: "Pass — Original",
      terminated: false,
      status: percentage >= 90 ? "FREE INTERNSHIP" : percentage >= 50 ? "50% DISCOUNT" : "NOT ELIGIBLE",
      qa: questions.map((q, i) => ({
        category: q.category,
        question: q.text,
        selectedOption: answers[i] || "—",
        correctOption: q.options?.correct || "—",
        isCorrect: answers[i] === q.options?.correct,
        score: evaluationScores[i] || 0,
        feedback: feedbacks[i] || "",
      })),
    };
    await addCandidateRecord(record);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })(); }, []);

  return (
    <div className="page">
      <Nav page="RESULTS" />
      <PageDecorations />
      <div style={{ maxWidth: 900, margin: "40px auto", padding: "0 20px" }}>
        <StepIndicator current={2} steps={["Register", "Interview", "Results"]} />

        {/* Result Hero */}
        <div className="card" style={{ textAlign: "center", marginBottom: 32, padding: "60px 40px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: freeInternship
            ? "radial-gradient(ellipse at center, rgba(0,255,136,0.07) 0%, transparent 70%)"
            : discountInternship
            ? "radial-gradient(ellipse at center, rgba(255,176,32,0.07) 0%, transparent 70%)"
            : "radial-gradient(ellipse at center, rgba(255,59,92,0.05) 0%, transparent 70%)" }} />
          <div style={{ position: "relative" }}>
            {/* Animated character */}
            <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
              <AnimCharacter type={freeInternship ? "free" : discountInternship ? "discount" : "noteligible"} />
            </div>
            <div className={`result-score`} style={{ fontFamily: "'Orbitron'", fontSize: "5rem", fontWeight: 900, lineHeight: 1, color: freeInternship ? "var(--accent3)" : discountInternship ? "var(--warn)" : "var(--danger)", textShadow: freeInternship ? "0 0 40px rgba(0,255,136,0.5)" : discountInternship ? "0 0 40px rgba(255,176,32,0.5)" : "0 0 40px rgba(255,59,92,0.4)" }}>
              {percentage}%
            </div>

            {/* Outcome Banner */}
            <div style={{ marginTop: 20, marginBottom: 28 }}>
              {freeInternship && (
                <div style={{ display: "inline-block", padding: "14px 36px", background: "rgba(0,255,136,0.1)", border: "2px solid var(--accent3)", borderRadius: 0 }}>
                  <div style={{ fontFamily: "'Orbitron'", fontSize: "1.3rem", fontWeight: 900, letterSpacing: 4, color: "var(--accent3)" }}>🎉 FREE INTERNSHIP</div>
                  <div style={{ fontSize: "0.85rem", color: "var(--accent3)", marginTop: 6, letterSpacing: 2, opacity: 0.8 }}>YOU ARE ELIGIBLE TO ATTEND THIS INTERNSHIP FOR FREE</div>
                </div>
              )}
              {discountInternship && (
                <div style={{ display: "inline-block", padding: "14px 36px", background: "rgba(255,176,32,0.08)", border: "2px solid var(--warn)", borderRadius: 0 }}>
                  <div style={{ fontFamily: "'Orbitron'", fontSize: "1.3rem", fontWeight: 900, letterSpacing: 4, color: "var(--warn)" }}>🎟️ 50% DISCOUNT</div>
                  <div style={{ fontSize: "0.85rem", color: "var(--warn)", marginTop: 6, letterSpacing: 2, opacity: 0.8 }}>YOU GET 50% DISCOUNT ON THE INTERNSHIP FEE</div>
                </div>
              )}
              {!freeInternship && !discountInternship && (
                <div style={{ display: "inline-block", padding: "14px 36px", background: "rgba(255,59,92,0.08)", border: "2px solid var(--danger)", borderRadius: 0 }}>
                  <div style={{ fontFamily: "'Orbitron'", fontSize: "1.3rem", fontWeight: 900, letterSpacing: 4, color: "var(--danger)" }}>NOT ELIGIBLE</div>
                  <div style={{ fontSize: "0.85rem", color: "var(--danger)", marginTop: 6, letterSpacing: 2, opacity: 0.8 }}>SCORE BELOW 50% — RETAKE AFTER PREPARATION</div>
                </div>
              )}
            </div>

            {/* Message */}
            <p style={{ color: "var(--text2)", fontSize: "1rem", maxWidth: 560, margin: "0 auto", lineHeight: 1.8 }}>
              {freeInternship
                ? "Outstanding performance! You scored 90% or above on the IIRT Assessment. You are fully eligible to attend the Internastra Internship absolutely FREE. Our team will contact you with the next steps."
                : discountInternship
                ? "Good effort! You have qualified for a 50% discount on the Internastra Internship fee. Score 90%+ in your next attempt to unlock the FREE internship offer."
                : "Thank you for taking the IIRT Assessment. Unfortunately your score did not meet the minimum threshold. We encourage you to revisit the topics and retake the test."}
            </p>

            {/* Score Target Bar */}
            <div style={{ maxWidth: 500, margin: "28px auto 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.7rem", color: "var(--text3)", letterSpacing: 2 }}>YOUR SCORE</span>
                <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.7rem", color: "var(--accent3)", letterSpacing: 2 }}>FREE INTERNSHIP @ 90%</span>
              </div>
              <div style={{ height: 10, background: "var(--surface2)", position: "relative", border: "1px solid var(--border)" }}>
                <div style={{ height: "100%", width: `${percentage}%`, background: freeInternship ? "linear-gradient(90deg,var(--accent2),var(--accent3))" : discountInternship ? "linear-gradient(90deg,var(--accent2),var(--warn))" : "linear-gradient(90deg,#cc0022,var(--danger))", transition: "width 1s ease", boxShadow: freeInternship ? "0 0 12px var(--accent3)" : discountInternship ? "0 0 12px var(--warn)" : "none" }} />
                {/* 50% marker */}
                <div style={{ position: "absolute", top: -4, left: "50%", width: 2, height: 18, background: "var(--text3)" }} />
                {/* 90% marker */}
                <div style={{ position: "absolute", top: -4, left: "90%", width: 2, height: 18, background: "var(--accent3)" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.65rem", color: "var(--text3)" }}>0%</span>
                <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.65rem", color: "var(--text3)", marginLeft: "40%" }}>50% — Discount</span>
                <span style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.65rem", color: "var(--accent3)" }}>90% — FREE</span>
              </div>
            </div>
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="grid-3" style={{ marginBottom: 16 }}>
          {[
            { label: "Questions Answered", value: answers.filter(a => a && a.trim().length > 0).length + "/20", icon: "📝" },
            { label: "Total Score", value: evaluationScores.reduce((a,b)=>a+b,0) + "/" + (questions.reduce((s,q)=>s+getMaxScore(q.category),0)||100) + " pts", icon: "🎯" },
            { label: "Overall %", value: percentage + "%", icon: "🤖" },
          ].map((s, i) => (
            <div key={i} className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2rem", marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontFamily: "'Orbitron'", fontSize: "1.4rem", fontWeight: 900, color: "var(--accent)", marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: "0.8rem", color: "var(--text3)", letterSpacing: 1, textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* Per-category score breakdown */}
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:32 }}>
          {Object.entries(
            questions.reduce((acc, q, i) => {
              const cat = q.category || "Domain";
              if (!acc[cat]) acc[cat] = { earned:0, max:0, count:0 };
              acc[cat].earned += evaluationScores[i] || 0;
              acc[cat].max    += getMaxScore(cat);
              acc[cat].count  += 1;
              return acc;
            }, {})
          ).map(([cat, g]) => (
            <div key={cat} style={{ flex:1, minWidth:120, background:"var(--surface)", border:"1px solid var(--border)", padding:"12px 14px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.58rem", letterSpacing:2, color:"var(--text3)", marginBottom:6, textTransform:"uppercase" }}>{cat}</div>
              <div style={{ fontFamily:"'Orbitron'", fontSize:"1.1rem", fontWeight:900, color: g.earned >= g.max*0.75 ? "var(--accent3)" : g.earned >= g.max*0.5 ? "var(--warn)" : "var(--danger)" }}>
                {g.earned}<span style={{fontSize:"0.7rem",color:"var(--text3)"}}>/{g.max}</span>
              </div>
              <div style={{ fontSize:"0.7rem", color:"var(--text3)", marginTop:2 }}>{g.count} Q × {g.max/g.count}pts</div>
            </div>
          ))}
        </div>

        {/* Security Report */}
        <div className="card" style={{ marginBottom: 32 }}>
          <h3 style={{ fontFamily: "'Orbitron'", fontSize: "0.85rem", letterSpacing: 2, color: "var(--accent)", marginBottom: 20 }}>SECURITY &amp; INTEGRITY REPORT</h3>
          <div className="grid-2">
            {(() => {
              const fv = result.faceViolations || 0;
              const tv = result.tabWarnings || 0;
              const vv = result.voiceWarnings || 0;
              return [
                ["👤 Face Detection",    fv === 0 ? "✓ No violations" : `⚠ ${fv} warning(s)`,          fv === 0 ? "ok" : "warn"],
                ["🖥️ Tab Switches",      tv === 0 ? "✓ No violations" : `⚠ ${tv} warning(s)`,          tv === 0 ? "ok" : "warn"],
                ["🔊 Voice Detections",  vv === 0 ? "✓ No violations" : `⚠ ${vv} warning(s)`,          vv === 0 ? "ok" : "warn"],
                ["🔍 Plagiarism Check",  "✓ Pass — Original",                                           "ok"],
                ["📵 External Devices",  "✓ None detected",                                             "ok"],
                ["🕐 Exam Duration",     new Date().toLocaleTimeString("en-IN"),                        "ok"],
              ];
            })().map(([k, v, type]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px", background:"var(--bg)", border:`1px solid ${type==="ok"?"var(--border)":"rgba(255,176,32,0.3)"}` }}>
                <span style={{ color:"var(--text2)", fontSize:"0.88rem" }}>{k}</span>
                <span style={{ color: type==="ok" ? "var(--accent3)" : "var(--warn)", fontFamily:"'Share Tech Mono'", fontSize:"0.78rem", fontWeight:700 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Candidate Info */}
        <div className="card" style={{ marginBottom: 32 }}>
          <h3 style={{ fontFamily: "'Orbitron'", fontSize: "0.85rem", letterSpacing: 2, color: "var(--accent)", marginBottom: 20 }}>ASSESSMENT SUMMARY</h3>
          {[
            ["Candidate ID", candidate.candidateId],
            ["Name", candidate.form.name],
            ["Role Applied", candidate.form.role],
            ["Assessment Date", new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })],
            ["Internship Offer", freeInternship ? "FREE INTERNSHIP 🏆" : discountInternship ? "50% DISCOUNT 🎟️" : "NOT ELIGIBLE"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ color: "var(--text3)", fontSize: "0.9rem" }}>{k}</span>
              <span style={{ color: k === "Internship Offer" ? (freeInternship ? "var(--accent3)" : discountInternship ? "var(--warn)" : "var(--danger)") : "var(--text)", fontWeight: 600, fontFamily: k === "Candidate ID" ? "'Share Tech Mono'" : "inherit", fontSize: k === "Candidate ID" ? "0.85rem" : "1rem" }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Q&A Review */}
        <div style={{ marginBottom: 32 }}>
          <button className="btn btn-outline" style={{ width: "100%", marginBottom: 16 }} onClick={() => setShowDetails(d => !d)}>
            {showDetails ? "HIDE" : "VIEW"} ANSWER REVIEW ({questions.length} QUESTIONS) — AI SCORED
          </button>
          {showDetails && questions.map((q, i) => {
            const selected = answers[i]; // single letter "A"/"B"/"C"/"D"
            const correct = q.options?.correct;
            const isCorrect = evaluationScores[i] >= 7;
            return (
              <div key={i} className="card" style={{ marginBottom: 12, borderLeft: `3px solid ${isCorrect ? "var(--accent3)" : selected ? "var(--danger)" : "var(--border)"}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div className={`badge ${q.category === "Technical" ? "badge-accent" : q.category === "Aptitude" ? "badge-warn" : "badge-green"}`}>Q{i + 1} · {q.category}</div>
                    <span style={{ fontSize: "1rem" }}>{isCorrect ? "✅" : selected ? "❌" : "⬜"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontFamily: "'Orbitron'", fontSize: "1rem", fontWeight: 900, color: isCorrect ? "var(--accent3)" : selected ? "var(--danger)" : "var(--text3)" }}>
                      {evaluationScores[i]}/{getMaxScore(q.category)}
                    </div>
                  </div>
                </div>
                <p style={{ color: "var(--text)", fontWeight: 600, marginBottom: 12, lineHeight: 1.6 }}>{q.text}</p>
                {/* MCQ Options — highlight selected and correct */}
                {q.options && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                    {["A","B","C","D"].map(opt => {
                      const isChosen = opt === selected;
                      const isRight = opt === correct;
                      let bg = "var(--bg)";
                      let border = "1px solid var(--border)";
                      let labelColor = "var(--text3)";
                      if (isRight) { bg = "rgba(0,255,136,0.06)"; border = "1px solid var(--accent3)"; labelColor = "var(--accent3)"; }
                      if (isChosen && !isRight) { bg = "rgba(255,59,92,0.06)"; border = "1px solid var(--danger)"; labelColor = "var(--danger)"; }
                      if (isChosen && isRight) { bg = "rgba(0,255,136,0.1)"; border = "2px solid var(--accent3)"; }
                      return (
                        <div key={opt} style={{ display: "flex", gap: 12, padding: "10px 14px", background: bg, border, alignItems: "center" }}>
                          <span style={{ fontFamily: "'Orbitron'", fontSize: "0.8rem", fontWeight: 900, color: labelColor, minWidth: 20 }}>{opt}</span>
                          <span style={{ fontSize: "0.88rem", color: isRight || isChosen ? "var(--text)" : "var(--text2)", flex: 1 }}>{q.options[opt]}</span>
                          {isChosen && !isRight && <span style={{ color: "var(--danger)", fontSize: "0.9rem" }}>✗ Your answer</span>}
                          {isRight && <span style={{ color: "var(--accent3)", fontSize: "0.9rem" }}>✓ Correct</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {feedbacks[i] && (
                  <div style={{ padding: "10px 14px", background: "rgba(0,212,255,0.04)", border: "1px solid rgba(0,212,255,0.15)", fontSize: "0.82rem", color: "var(--accent)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span>🤖</span>
                    <span><strong>AI:</strong> {feedbacks[i]}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <button className="btn btn-primary" style={{ marginRight:12, background:"linear-gradient(135deg,#1a3a6c,#2563eb)" }}
            onClick={() => generateReportCardPDF(candidate, result, questions, evaluationScores, feedbacks, percentage)}>
            ⬇ DOWNLOAD REPORT CARD (PDF)
          </button>
          <button className="btn btn-outline" onClick={onAdmin}>📊 VIEW ADMIN PANEL</button>
        </div>

        <div style={{ textAlign: "center" }}>
          <p style={{ color: "var(--text3)", fontFamily: "'Share Tech Mono'", fontSize: "0.75rem", letterSpacing: 2 }}>
            IIRT · INTERNASTRA INTERNSHIP READINESS TEST · AI-POWERED · SECURE · FAIR
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN DASHBOARD ──────────────────────────────────────────────────────────

// ─── ADMIN LOGIN ──────────────────────────────────────────────────────────────
// credentials defined inline in AdminLogin

async function sendLoginNotificationEmail(loginTime, ipInfo) {
  const subject = encodeURIComponent("IIRT Admin Panel Login Alert");
  const body = encodeURIComponent(
    "IIRT Admin Panel — Successful Login Detected\n\n" +
    "Time: " + loginTime + "\n" +
    "Device: " + ipInfo + "\n" +
    "Account: hiring@internastra.com\n\n" +
    "If this was not you, please change your credentials immediately."
  );
  const mailtoLink = "mailto:hiring@internastra.com?subject=" + subject + "&body=" + body;
  try { window.open(mailtoLink, "_blank"); } catch(e) {}
}

async function getIPInfo() {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return "IP: " + data.ip + " | " + navigator.platform;
  } catch {
    return navigator.platform + " | " + navigator.userAgent.substring(0, 50);
  }
}


// ── AMBASSADOR LOGIN PAGE ─────────────────────────────────────────────────────
function AmbassadorLoginPage({ onLoginSuccess, onBack }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockout, setLockout] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);

  useEffect(() => {
    if (lockout && lockTimer > 0) {
      const t = setTimeout(() => setLockTimer(s => s-1), 1000);
      return () => clearTimeout(t);
    }
    if (lockTimer === 0 && lockout) { setLockout(false); setAttempts(0); setError(""); }
  }, [lockout, lockTimer]);

  const handleLogin = async () => {
    if (lockout) return;
    setError("");
    if (!username.trim() || !password.trim()) { setError("Please enter username and password."); return; }
    setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    // Load ambassador list from Firebase and check credentials
    const ambList = await loadAmbDB();
    const matched = ambList.find(a =>
      (a.username || a.code) === username.trim().toLowerCase() &&
      (a.password || "Amb@2026") === password.trim()
    );
    if (matched) {
      setLoading(false);
      onLoginSuccess(matched);
    } else {
      const na = attempts + 1;
      setAttempts(na);
      if (na >= 3) { setLockout(true); setLockTimer(30); setError("Too many failed attempts. Locked for 30 seconds."); }
      else { setError("Invalid username or password. " + (3-na) + " attempt(s) remaining."); }
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <Nav page="AMBASSADOR LOGIN" />
      <PageDecorations />
      <div style={{ minHeight:"calc(100vh - 60px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"40px 20px", position:"relative" }}>
        <div style={{ width:"100%", maxWidth:420, position:"relative", zIndex:1 }}>

          {/* Header */}
          <div style={{ textAlign:"center", marginBottom:36 }}>
            <div style={{ fontSize:"3rem", marginBottom:12 }}>🌟</div>
            <div style={{ fontFamily:"'Orbitron'", fontSize:"1.4rem", fontWeight:900, color:"var(--accent)", letterSpacing:4, marginBottom:6 }}>
              AMBASSADOR
            </div>
            <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.7rem", color:"var(--text3)", letterSpacing:3 }}>
              CAMPUS AMBASSADOR PORTAL
            </div>
          </div>

          <div className="card" style={{ padding:"32px" }}>
            <div className="input-group">
              <label>Username</label>
              <input type="text" placeholder="Your ambassador username"
                value={username}
                onChange={e => { setUsername(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                disabled={loading || lockout}
                style={{ clipPath:"none" }} />
            </div>
            <div className="input-group">
              <label>Password</label>
              <div style={{ position:"relative" }}>
                <input type={showPass ? "text" : "password"} placeholder="Your password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  disabled={loading || lockout}
                  style={{ clipPath:"none", paddingRight:50 }} />
                <button onClick={() => setShowPass(s => !s)}
                  style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"var(--text3)", fontSize:"1rem" }}>
                  {showPass ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
            {error && (
              <div style={{ padding:"10px 14px", background:"rgba(255,59,92,0.08)", border:"1px solid rgba(255,59,92,0.3)", marginBottom:16, fontSize:"0.85rem", color:"var(--danger)", display:"flex", alignItems:"center", gap:8 }}>
                ⚠️ {error}{lockout && lockTimer > 0 && <span style={{ fontFamily:"'Share Tech Mono'", marginLeft:6 }}>({lockTimer}s)</span>}
              </div>
            )}
            <button className="btn btn-primary" style={{ width:"100%", fontSize:"1rem", padding:"14px", clipPath:"none", marginBottom:12 }}
              onClick={handleLogin} disabled={loading || lockout}>
              {loading ? (
                <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                  <span className="spinner" style={{ width:16, height:16, borderWidth:2 }} />
                  VERIFYING...
                </span>
              ) : lockout ? ("LOCKED ("+lockTimer+"s)") : "LOGIN →"}
            </button>
            <button className="btn btn-outline" style={{ width:"100%", clipPath:"none" }} onClick={onBack}>
              ← BACK TO HOME
            </button>
            <div style={{ marginTop:20, padding:"12px 14px", background:"rgba(255,176,32,0.04)", border:"1px solid rgba(255,176,32,0.15)", fontSize:"0.75rem", color:"var(--text3)", fontFamily:"'Share Tech Mono'", lineHeight:1.7 }}>
              🔐 Login credentials are provided by Internastra admin.<br/>
              Contact hiring@internastra.com if you need access.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AMBASSADOR DASHBOARD ──────────────────────────────────────────────────────
function AmbassadorDashboard({ ambassador, onBack }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const BASE_URL = "https://internastra.online/";
  const refLink = BASE_URL + "?ref=" + ambassador.code;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const unsub = subscribeCandidates((all) => {
      const mine = (all || []).filter(r => (r.referral || "").toLowerCase() === ambassador.code);
      setRecords(mine);
      setLoading(false);
    });
    return () => unsub();
  }, [ambassador.code]);

  const copyLink = () => {
    navigator.clipboard.writeText(refLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const free       = records.filter(r => r.status === "FREE INTERNSHIP").length;
  const discount   = records.filter(r => r.status === "50% DISCOUNT").length;
  const failed     = records.filter(r => r.status !== "FREE INTERNSHIP" && r.status !== "50% DISCOUNT" && !r.malpractice).length;
  const terminated = records.filter(r => r.malpractice).length;
  const avgScore   = records.length ? Math.round(records.reduce((s,r) => s+(r.percentage||0),0)/records.length) : 0;
  const interested    = records.filter(r => r.internshipStatus === "Interested").length;
  const notInterested = records.filter(r => r.internshipStatus === "Not Interested").length;
  const enrolled      = records.filter(r => r.internshipStatus === "Enrolled").length;

  return (
    <div className="page">
      <Nav page="AMBASSADOR PORTAL" />
      <PageDecorations />
      <div style={{ maxWidth:900, margin:"0 auto", padding:"30px 20px 80px", position:"relative", zIndex:2 }}>

        {/* Header */}
        <div className="card" style={{ marginBottom:24, padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
          <div>
            <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:4, color:"var(--text3)", marginBottom:6 }}>WELCOME BACK</div>
            <div style={{ fontFamily:"'Orbitron'", fontSize:"1.4rem", fontWeight:900, color:"var(--accent)", letterSpacing:2 }}>{ambassador.displayName}</div>
            <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.7rem", color:"var(--text3)", marginTop:4 }}>Campus Ambassador · @{ambassador.code}</div>
          </div>
          <button className="btn btn-outline" style={{ clipPath:"none" }} onClick={onBack}>🚪 LOGOUT</button>
        </div>

        {/* Referral link card */}
        <div style={{ background:"rgba(0,212,255,0.04)", border:"2px solid rgba(0,212,255,0.25)", padding:"20px 24px", marginBottom:24, position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,var(--accent),transparent)" }}/>
          <div style={{ fontFamily:"'Orbitron'", fontSize:"0.68rem", letterSpacing:3, color:"var(--accent)", marginBottom:10 }}>🔗 YOUR REFERRAL LINK</div>
          <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ flex:1, fontFamily:"'Share Tech Mono'", fontSize:"0.82rem", color:"var(--text)", background:"var(--bg)", padding:"10px 16px", border:"1px solid var(--border)", wordBreak:"break-all" }}>
              {refLink}
            </div>
            <button
              style={{ padding:"10px 20px", background: copied ? "rgba(0,255,136,0.15)" : "var(--accent)", border:"none", color: copied ? "var(--accent3)" : "#000", fontFamily:"'Orbitron'", fontSize:"0.7rem", letterSpacing:2, cursor:"pointer", fontWeight:700, flexShrink:0, transition:"all 0.2s" }}
              onClick={copyLink}>
              {copied ? "✓ COPIED!" : "📋 COPY"}
            </button>
          </div>
          <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", color:"var(--text3)", marginTop:10, letterSpacing:1 }}>
            Share this link with students. When they register using your link, they will be tracked under your name.
          </div>
        </div>

        {/* Stats grid — row 1: test results */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:12 }}>
          {[
            { label:"Total Joined",    val: records.length,                         color:"var(--accent)",  icon:"👥" },
            { label:"Free Internship", val: free,                                   color:"var(--accent3)", icon:"🏆" },
            { label:"50% Discount",    val: discount,                               color:"var(--warn)",    icon:"🎟️" },
            { label:"Not Eligible",    val: failed,                                 color:"var(--danger)",  icon:"📋" },
            { label:"Avg Score",       val: records.length ? avgScore+"%" : "—",   color:"var(--accent)",  icon:"📊" },
          ].map(s => (
            <div key={s.label} style={{ background:"var(--surface)", border:"1px solid var(--border)", padding:"16px 12px", textAlign:"center" }}>
              <div style={{ fontSize:"1.4rem", marginBottom:6 }}>{s.icon}</div>
              <div style={{ fontFamily:"'Orbitron'", fontSize:"1.4rem", fontWeight:900, color:s.color, marginBottom:4 }}>{s.val}</div>
              <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.55rem", letterSpacing:2, color:"var(--text3)", textTransform:"uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Stats grid — row 2: internship status */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24 }}>
          {[
            { label:"Interested",     val: interested,    color:"#0077cc", bg:"rgba(0,119,204,0.07)",  border:"rgba(0,119,204,0.25)", icon:"🙋" },
            { label:"Not Interested", val: notInterested, color:"#cc3300", bg:"rgba(204,51,0,0.07)",   border:"rgba(204,51,0,0.25)",  icon:"🚫" },
            { label:"Enrolled",       val: enrolled,      color:"#00994d", bg:"rgba(0,153,77,0.07)",   border:"rgba(0,153,77,0.25)",  icon:"✅" },
          ].map(s => (
            <div key={s.label} style={{ background:s.bg, border:"1px solid "+s.border, padding:"14px 12px", textAlign:"center", position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,"+s.color+",transparent)" }}/>
              <div style={{ fontSize:"1.3rem", marginBottom:5 }}>{s.icon}</div>
              <div style={{ fontFamily:"'Orbitron'", fontSize:"1.6rem", fontWeight:900, color:s.color, marginBottom:4 }}>{s.val}</div>
              <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.58rem", letterSpacing:2, color:s.color, textTransform:"uppercase", opacity:0.8 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Candidate list */}
        <div>
          <div style={{ fontFamily:"'Orbitron'", fontSize:"0.72rem", letterSpacing:3, color:"var(--accent)", marginBottom:14 }}>
            📋 CANDIDATES WHO JOINED VIA YOUR LINK ({records.length})
          </div>
          {loading ? (
            <div style={{ textAlign:"center", padding:40 }}>
              <div className="spinner" style={{ margin:"0 auto 12px" }} />
              <div style={{ color:"var(--text3)", fontFamily:"'Share Tech Mono'", fontSize:"0.72rem" }}>LOADING...</div>
            </div>
          ) : records.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 20px", background:"var(--surface)", border:"1px solid var(--border)" }}>
              <div style={{ fontSize:"3rem", marginBottom:16 }}>🌟</div>
              <div style={{ fontFamily:"'Orbitron'", fontSize:"0.88rem", color:"var(--accent)", letterSpacing:2, marginBottom:8 }}>NO CANDIDATES YET</div>
              <p style={{ color:"var(--text2)", fontSize:"0.9rem" }}>Share your referral link to start bringing students to the test!</p>
            </div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.88rem" }}>
                <thead>
                  <tr style={{ background:"var(--surface2)", borderBottom:"2px solid var(--border)" }}>
                    {["#","Candidate ID","Name","Domain","Score","Date","Result","Internship Status"].map(h => (
                      <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", letterSpacing:2, color:"var(--text3)", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={r.candidateId} style={{ borderBottom:"1px solid var(--border)", background: i%2===0?"var(--surface)":"transparent" }}>
                      <td style={{ padding:"10px 14px", color:"var(--text3)", fontSize:"0.8rem" }}>{i+1}</td>
                      <td style={{ padding:"10px 14px", fontFamily:"'Share Tech Mono'", fontSize:"0.7rem", color:"var(--accent)" }}>{r.candidateId}</td>
                      <td style={{ padding:"10px 14px", fontWeight:600 }}>{r.name}</td>
                      <td style={{ padding:"10px 14px", color:"var(--text2)", fontSize:"0.82rem" }}>{r.role}</td>
                      <td style={{ padding:"10px 14px", fontFamily:"'Orbitron'", fontWeight:700, color: r.percentage>=90?"var(--accent3)":r.percentage>=50?"var(--warn)":"var(--danger)" }}>
                        {r.malpractice ? "—" : r.percentage+"%"}
                      </td>
                      <td style={{ padding:"10px 14px", color:"var(--text3)", fontSize:"0.78rem" }}>{r.registeredAt?.slice(0,10)}</td>
                      <td style={{ padding:"10px 14px" }}>
                        <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", padding:"3px 8px", letterSpacing:1,
                          background: r.status==="FREE INTERNSHIP"?"rgba(0,255,136,0.1)": r.status==="50% DISCOUNT"?"rgba(255,176,32,0.1)": r.malpractice?"rgba(255,59,92,0.08)":"rgba(255,59,92,0.08)",
                          color: r.status==="FREE INTERNSHIP"?"var(--accent3)": r.status==="50% DISCOUNT"?"var(--warn)":"var(--danger)",
                          border: "1px solid " + (r.status==="FREE INTERNSHIP"?"rgba(0,255,136,0.3)":r.status==="50% DISCOUNT"?"rgba(255,176,32,0.3)":"rgba(255,59,92,0.25)"),
                        }}>
                          {r.malpractice ? "⛔ TERMINATED" : r.status}
                        </span>
                      </td>
                      <td style={{ padding:"10px 14px" }}>
                        {(() => {
                          const st = r.internshipStatus || "";
                          const cfg = {
                            "Interested":     { color:"#0077cc", bg:"rgba(0,119,204,0.08)", icon:"🙋" },
                            "Not Interested": { color:"#cc3300", bg:"rgba(204,51,0,0.07)",  icon:"🚫" },
                            "Enrolled":       { color:"#00994d", bg:"rgba(0,153,77,0.08)",  icon:"✅" },
                          };
                          const c = cfg[st];
                          return st ? (
                            <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", padding:"4px 10px", background:c?.bg||"transparent", color:c?.color||"var(--text3)", border:"1px solid "+(c?.color?c.color+"55":"var(--border)"), letterSpacing:1 }}>
                              {c?.icon} {st}
                            </span>
                          ) : (
                            <span style={{ color:"var(--text3)", fontSize:"0.7rem", fontFamily:"'Share Tech Mono'" }}>—</span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function AdminLogin({ onLoginSuccess, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockout, setLockout] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);

  useEffect(() => {
    if (lockout && lockTimer > 0) {
      const t = setTimeout(() => setLockTimer(s => s - 1), 1000);
      return () => clearTimeout(t);
    }
    if (lockTimer === 0 && lockout) { setLockout(false); setAttempts(0); setError(""); }
  }, [lockout, lockTimer]);

  const handleLogin = async () => {
    if (lockout) return;
    setError("");
    if (!email.trim() || !password) { setError("Please enter both email and password."); return; }
    setLoading(true);
    await new Promise(r => setTimeout(r, 900));
    const ADMIN_USERS = [
      { email: "hiring@internastra.com",  password: "Internastra@IIRT", name: "Hiring" },
      { email: "ankita@internastra.com",  password: "IAIN26011",        name: "Ankita" },
      { email: "rahul@internastra.com",   password: "IAIN26009",        name: "Rahul"  },
      { email: "kartik@internastra.com",  password: "IAIN26010",        name: "Kartik" },
    ];
    const matchedUser = ADMIN_USERS.find(u =>
      u.email === email.trim().toLowerCase() && u.password === password
    );
    if (matchedUser) {
      const now = new Date().toLocaleString("en-IN", { dateStyle: "full", timeStyle: "medium" });
      const ipInfo = await getIPInfo();
      await sendLoginNotificationEmail(now, ipInfo);
      setEmailSent(true);
      setLoading(false);
      setTimeout(() => onLoginSuccess(), 2000);
    } else if (!matchedUser) {
      const na = attempts + 1;
      setAttempts(na);
      if (na >= 3) { setLockout(true); setLockTimer(30); setError("Too many failed attempts. Account locked for 30 seconds."); }
      else { setError("Invalid credentials. " + (3 - na) + " attempt(s) remaining."); }
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <Nav page="ADMIN LOGIN" />
      <PageDecorations />
      <div style={{ minHeight: "calc(100vh - 60px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", position: "relative" }}>
        <div className="grid-dots" style={{ position: "absolute", inset: 0 }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at center, rgba(0,136,204,0.07) 0%, transparent 70%)" }} />
        <div style={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontFamily: "'Orbitron'", fontSize: "2.5rem", fontWeight: 900, color: "var(--accent)", textShadow: "var(--glow)", letterSpacing: 4, marginBottom: 8 }}>
              II<span style={{ color: "var(--accent3)" }}>RT</span>
            </div>
            <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.75rem", color: "var(--text3)", letterSpacing: 3 }}>INTERNASTRA INTERNSHIP READINESS TEST</div>
          </div>
          <div className="card" style={{ padding: "40px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
              <div style={{ width: 48, height: 48, background: "rgba(0,212,255,0.1)", border: "1px solid var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0 }}>
                🔐
              </div>
              <div>
                <div style={{ fontFamily: "'Orbitron'", fontSize: "1rem", fontWeight: 700, letterSpacing: 2, color: "var(--text)" }}>ADMIN PORTAL</div>
                <div style={{ fontSize: "0.8rem", color: "var(--text3)", marginTop: 2 }}>Restricted — authorized personnel only</div>
              </div>
            </div>
            <div className="input-group">
              <label>Admin Email</label>
              <input type="email" placeholder="hiring@internastra.com" value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                disabled={loading || lockout}
                style={{ clipPath: "none" }} />
            </div>
            <div className="input-group">
              <label>Password</label>
              <div style={{ position: "relative" }}>
                <input type={showPass ? "text" : "password"} placeholder="Enter admin password" value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  disabled={loading || lockout}
                  style={{ clipPath: "none", paddingRight: 50 }} />
                <button onClick={() => setShowPass(s => !s)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: "1rem" }}>
                  {showPass ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
            {error && (
              <div style={{ padding: "12px 16px", background: "rgba(255,59,92,0.08)", border: "1px solid rgba(255,59,92,0.3)", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                <span>⚠️</span>
                <span style={{ color: "var(--danger)", fontSize: "0.88rem" }}>
                  {error}{lockout && lockTimer > 0 && <span style={{ fontFamily: "'Share Tech Mono'", marginLeft: 8 }}>({lockTimer}s)</span>}
                </span>
              </div>
            )}
            {emailSent && (
              <div style={{ padding: "12px 16px", background: "rgba(0,255,136,0.06)", border: "1px solid rgba(0,255,136,0.3)", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                <span>✅</span>
                <span style={{ color: "var(--accent3)", fontSize: "0.85rem" }}>✅ Login verified! Welcome to IIRT Admin Panel. Redirecting...</span>
              </div>
            )}
            <button className="btn btn-primary"
              style={{ width: "100%", fontSize: "1rem", padding: "16px", clipPath: "none", marginBottom: 16 }}
              onClick={handleLogin} disabled={loading || lockout}>
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  AUTHENTICATING...
                </span>
              ) : lockout ? ("LOCKED (" + lockTimer + "s)") : "LOGIN TO ADMIN PANEL →"}
            </button>
            <button className="btn btn-outline" style={{ width: "100%", clipPath: "none" }} onClick={onBack}>← BACK TO HOME</button>
            <div style={{ marginTop: 24, padding: "12px 14px", background: "rgba(255,176,32,0.04)", border: "1px solid rgba(255,176,32,0.15)" }}>
              <p style={{ fontSize: "0.75rem", color: "var(--text3)", lineHeight: 1.7, fontFamily: "'Share Tech Mono'" }}>
                🛡️ All logins are monitored. A notification email opens on every successful login via your default mail client.
              </p>
            </div>
          </div>
          {attempts > 0 && !lockout && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16, alignItems: "center" }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: i < attempts ? "var(--danger)" : "var(--border)" }} />
              ))}
              <span style={{ fontSize: "0.72rem", color: "var(--text3)", fontFamily: "'Share Tech Mono'", letterSpacing: 2, marginLeft: 8 }}>FAILED ATTEMPTS</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── QUESTION BANK MANAGER ────────────────────────────────────────────────────
const ALL_DOMAIN_LIST = [
  "Full Stack Web Development","AI/ML",
  "Data Science","Cyber Security","Embedded Systems","UI/UX Design",
  "Android Development","AutoCAD","VLSI","Internet of Things","Robotics",
  "Hybrid and Electric Vehicle","AutoCAD (Mech)","Car Design","IC Engine",
  "Construction Plan & Design","Medical Coding","Nano Science and Technology",
];

const BLANK_FORM = { text: "", A: "", B: "", C: "", D: "", correct: "A", explanation: "" };

function QuestionBankManager() {
  const [activeCategory, setActiveCategory] = useState("Domain");
  const [activeDomain, setActiveDomain] = useState(ALL_DOMAIN_LIST[0]);
  const [bank, setBank] = useState({});
  const [bankLoading, setBankLoading] = useState(true);

  // Real-time sync: whenever ANY admin saves, all admins instantly see the update
  useEffect(() => {
    setBankLoading(true);
    // Hard timeout — NEVER stay on loading screen more than 5 seconds
    const hardTimeout = setTimeout(() => setBankLoading(false), 5000);
    const unsub = subscribeQB((freshBank) => {
      setBank(freshBank || {});
      setBankLoading(false);
      clearTimeout(hardTimeout);
    });
    return () => { unsub(); clearTimeout(hardTimeout); };
  }, []);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const bankKey = activeCategory === "Domain" ? activeDomain : activeCategory;
  const questions = (bank[bankKey] || []).filter(q =>
    !searchQ || q.text?.toLowerCase().includes(searchQ.toLowerCase())
  );
  const total = (bank[bankKey] || []).length;

  const validate = () => {
    const e = {};
    if (!form.text.trim()) e.text = "Question text is required";
    if (!form.A.trim()) e.A = "Option A is required";
    if (!form.B.trim()) e.B = "Option B is required";
    if (!form.C.trim()) e.C = "Option C is required";
    if (!form.D.trim()) e.D = "Option D is required";
    if (!form.explanation.trim()) e.explanation = "Explanation is required";
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const newBank = { ...bank };
    if (!newBank[bankKey]) newBank[bankKey] = [];
    if (editId) {
      newBank[bankKey] = newBank[bankKey].map(q => q.id === editId ? { ...form, id: editId, addedAt: q.addedAt } : q);
    } else {
      const id = Date.now() + "_" + Math.random().toString(36).slice(2, 6);
      newBank[bankKey] = [...newBank[bankKey], { ...form, id, addedAt: new Date().toISOString() }];
    }
    await saveQB(newBank);  // Saves to Firebase — all 3 admins see it instantly
    setForm({ ...BLANK_FORM });
    setEditId(null);
    setShowForm(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleEdit = (q) => {
    setForm({ text: q.text, A: q.A, B: q.B, C: q.C, D: q.D, correct: q.correct, explanation: q.explanation || "" });
    setEditId(q.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const newBank = { ...bank };
    newBank[bankKey] = (newBank[bankKey] || []).filter(q => q.id !== id);
    await saveQB(newBank);  // Firebase update propagates to all admins instantly
    setDeleteConfirm(null);
  };

  const handleCancelForm = () => {
    setForm({ ...BLANK_FORM });
    setEditId(null);
    setShowForm(false);
    setFormErrors({});
  };

  const catColor = { Domain: "var(--accent)", Aptitude: "var(--warn)", Reasoning: "var(--accent3)", Coding: "#c084fc" };

  if (bankLoading) return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--accent)" }}>
      <div style={{ fontFamily: "'Orbitron'", fontSize: "0.85rem", letterSpacing: 3, marginBottom: 16 }}>SYNCING WITH CLOUD...</div>
      <div style={{ width: 40, height: 40, border: "3px solid var(--border)", borderTop: "3px solid var(--accent)", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Category Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "2px solid var(--border)", paddingBottom: 0 }}>
        {["Domain","Aptitude","Reasoning","Coding"].map(cat => (
          <button key={cat} onClick={() => { setActiveCategory(cat); setShowForm(false); setForm({ ...BLANK_FORM }); setEditId(null); setSearchQ(""); }}
            style={{ padding: "12px 28px", background: activeCategory === cat ? catColor[cat] : "transparent", border: "none", color: activeCategory === cat ? "#000" : "var(--text2)", fontFamily: "'Orbitron'", fontSize: "0.78rem", fontWeight: 900, letterSpacing: 2, cursor: "pointer", transition: "all 0.2s" }}>
            {cat === "Domain" ? "⚙️ DOMAIN" : cat === "Aptitude" ? "🔢 APTITUDE" : cat === "Reasoning" ? "🧠 REASONING" : "💻 CODING"}
          </button>
        ))}
      </div>

      {/* Domain selector */}
      {activeCategory === "Domain" && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.68rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 10 }}>SELECT DOMAIN</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ALL_DOMAIN_LIST.map(d => {
              const cnt = (bank[d] || []).length;
              return (
                <button key={d} onClick={() => { setActiveDomain(d); setShowForm(false); setForm({ ...BLANK_FORM }); setSearchQ(""); }}
                  style={{ padding: "8px 14px", background: activeDomain === d ? "rgba(0,212,255,0.15)" : "var(--surface)", border: activeDomain === d ? "1.5px solid var(--accent)" : "1px solid var(--border)", color: activeDomain === d ? "var(--accent)" : "var(--text2)", fontSize: "0.8rem", cursor: "pointer", display: "flex", gap: 8, alignItems: "center", transition: "all 0.15s" }}>
                  {d}
                  <span style={{ background: cnt >= 10 ? "rgba(0,255,136,0.2)" : "rgba(255,176,32,0.2)", color: cnt >= 10 ? "var(--accent3)" : "var(--warn)", fontSize: "0.68rem", padding: "1px 7px", fontFamily: "'Share Tech Mono'" }}>
                    {cnt}/100
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div>
            <span style={{ fontFamily: "'Orbitron'", fontSize: "1.6rem", fontWeight: 900, color: catColor[activeCategory] }}>{total}</span>
            <span style={{ color: "var(--text3)", fontSize: "0.8rem", marginLeft: 8 }}>/ {activeCategory === "Domain" ? "100" : "100"} questions</span>
          </div>
          {total > 0 && (
            <div style={{ width: 140, height: 6, background: "var(--surface2)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: Math.min(100, total) + "%", background: catColor[activeCategory], transition: "width 0.5s" }} />
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input placeholder="Search questions..." value={searchQ} onChange={e => setSearchQ(e.target.value)}
            style={{ padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "0.85rem", width: 220, outline: "none" }} />
          {saved && <span style={{ color: "var(--accent3)", fontFamily: "'Share Tech Mono'", fontSize: "0.75rem", letterSpacing: 1 }}>✓ SAVED</span>}
          <button className="btn btn-primary" style={{ padding: "10px 20px", fontSize: "0.78rem" }}
            onClick={() => { setShowForm(true); setEditId(null); setForm({ ...BLANK_FORM }); setFormErrors({}); }}>
            + ADD QUESTION
          </button>
        </div>
      </div>

      {/* ── GOOGLE FORM STYLE ADD/EDIT FORM ── */}
      {showForm && (
        <div style={{ background: "var(--surface)", border: "2px solid " + catColor[activeCategory], padding: "32px", marginBottom: 32, position: "relative" }}>
          {/* Accent top bar like Google Form */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 6, background: catColor[activeCategory] }} />
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: "'Orbitron'", fontSize: "1rem", fontWeight: 700, color: catColor[activeCategory], letterSpacing: 2, marginBottom: 4 }}>
              {editId ? "✏️ EDIT QUESTION" : "➕ ADD NEW QUESTION"}
            </div>
            <div style={{ fontSize: "0.82rem", color: "var(--text3)" }}>
              {activeCategory === "Domain" ? activeDomain : activeCategory} · Fill all fields below
            </div>
          </div>

          {/* Question Text */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontFamily: "'Share Tech Mono'", fontSize: "0.72rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 10 }}>
              QUESTION TEXT <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <textarea value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} rows={activeCategory === "Coding" ? 6 : 3}
              placeholder={activeCategory === "Coding" ? "Write your code snippet here, then add a question.\nExample:\ndef greet(name):\n    return 'Hello ' + name\nprint(greet('World'))\n\nWhat is the output of the above code?" : "Enter the full question text here..."}
              style={{ width: "100%", background: "var(--bg)", border: formErrors.text ? "1.5px solid var(--danger)" : "1px solid var(--border)", color: "var(--text)", padding: "14px 16px", fontSize: activeCategory === "Coding" ? "0.85rem" : "0.95rem", resize: "vertical", fontFamily: activeCategory === "Coding" ? "'Share Tech Mono', monospace" : "'Rajdhani'", outline: "none", lineHeight: 1.7, whiteSpace: "pre-wrap" }} />
            {formErrors.text && <div style={{ color: "var(--danger)", fontSize: "0.78rem", marginTop: 4 }}>⚠ {formErrors.text}</div>}
            {/* Live IDE preview for Coding category */}
            {activeCategory === "Coding" && form.text && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.6rem", color:"var(--text3)", letterSpacing:3, marginBottom:8 }}>LIVE PREVIEW</div>
                <CodeHighlight code={form.text} theme={_navCb.theme} />
              </div>
            )}
          </div>

          {/* Options A-D */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontFamily: "'Share Tech Mono'", fontSize: "0.72rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 14 }}>
              OPTIONS <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {["A","B","C","D"].map(opt => (
                <div key={opt} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  {/* Radio to select correct */}
                  <div onClick={() => setForm(f => ({ ...f, correct: opt }))}
                    style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid " + (form.correct === opt ? catColor[activeCategory] : "var(--border)"), background: form.correct === opt ? catColor[activeCategory] : "var(--surface)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, transition: "all 0.2s", marginTop: 6 }}>
                    <span style={{ fontFamily: "'Orbitron'", fontSize: "0.75rem", fontWeight: 900, color: form.correct === opt ? "#000" : "var(--text3)" }}>{opt}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <input value={form[opt]} onChange={e => setForm(f => ({ ...f, [opt]: e.target.value }))}
                      placeholder={"Option " + opt + (form.correct === opt ? " (correct answer)" : " (distractor)")}
                      style={{ width: "100%", background: form.correct === opt ? (activeCategory === "Domain" ? "rgba(0,212,255,0.05)" : activeCategory === "Aptitude" ? "rgba(255,176,32,0.05)" : "rgba(0,255,136,0.05)") : "var(--bg)", border: formErrors[opt] ? "1.5px solid var(--danger)" : form.correct === opt ? "1.5px solid " + catColor[activeCategory] : "1px solid var(--border)", color: "var(--text)", padding: "10px 14px", fontSize: "0.9rem", fontFamily: "'Rajdhani'", outline: "none" }} />
                    {formErrors[opt] && <div style={{ color: "var(--danger)", fontSize: "0.75rem", marginTop: 3 }}>⚠ {formErrors[opt]}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: "0.78rem", color: "var(--text3)", fontFamily: "'Share Tech Mono'" }}>
              👆 Click the circle to mark the CORRECT answer
            </div>
          </div>

          {/* Explanation */}
          <div style={{ marginBottom: 28 }}>
            <label style={{ display: "block", fontFamily: "'Share Tech Mono'", fontSize: "0.72rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 10 }}>
              EXPLANATION / REASON <span style={{ color: "var(--danger)" }}>*</span>
            </label>
            <textarea value={form.explanation} onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))} rows={2}
              placeholder="Explain why the correct answer is right (shown to candidates after evaluation)..."
              style={{ width: "100%", background: "var(--bg)", border: formErrors.explanation ? "1.5px solid var(--danger)" : "1px solid var(--border)", color: "var(--text)", padding: "12px 16px", fontSize: "0.88rem", resize: "vertical", fontFamily: "'Rajdhani'", outline: "none", lineHeight: 1.5 }} />
            {formErrors.explanation && <div style={{ color: "var(--danger)", fontSize: "0.78rem", marginTop: 4 }}>⚠ {formErrors.explanation}</div>}
          </div>

          {/* Correct answer preview */}
          <div style={{ marginBottom: 24, padding: "12px 16px", background: "rgba(0,255,136,0.05)", border: "1px solid rgba(0,255,136,0.2)", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: "1.1rem" }}>✅</span>
            <div>
              <div style={{ fontSize: "0.72rem", color: "var(--text3)", fontFamily: "'Share Tech Mono'", letterSpacing: 1 }}>CORRECT ANSWER MARKED AS</div>
              <div style={{ color: "var(--accent3)", fontWeight: 700, fontSize: "0.95rem" }}>Option {form.correct}: {form[form.correct] || "(enter text above)"}</div>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: 12 }}>
            <button className="btn btn-primary" style={{ flex: 1, padding: "14px" }} onClick={handleSave}>
              {editId ? "💾 UPDATE QUESTION" : "💾 SAVE QUESTION"}
            </button>
            <button className="btn btn-outline" style={{ padding: "14px 24px" }} onClick={handleCancelForm}>CANCEL</button>
          </div>
        </div>
      )}

      {/* ── QUESTION LIST ── */}
      {questions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", border: "2px dashed var(--border)" }}>
          <div style={{ fontSize: "3rem", marginBottom: 16 }}>📝</div>
          <div style={{ fontFamily: "'Orbitron'", fontSize: "0.85rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 8 }}>
            {searchQ ? "NO QUESTIONS MATCH YOUR SEARCH" : "NO QUESTIONS YET"}
          </div>
          <div style={{ color: "var(--text3)", fontSize: "0.82rem", marginBottom: 20 }}>
            {activeCategory === "Domain"
              ? "Add at least 10 " + activeDomain + " questions to enable the question bank for this domain."
              : "Add at least 5 " + activeCategory + " questions to use the question bank."}
          </div>
          {!showForm && <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ ADD FIRST QUESTION</button>}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {questions.map((q, i) => (
            <div key={q.id} style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: "20px 24px", transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = catColor[activeCategory]}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                {/* Number badge */}
                <div style={{ width: 36, height: 36, background: catColor[activeCategory], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "'Orbitron'", fontSize: "0.75rem", fontWeight: 900, color: "#000" }}>
                  {(bank[bankKey] || []).findIndex(bq => bq.id === q.id) + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 12 }}>
                    {activeCategory === "Coding" ? (
                      <CodeHighlight code={q.text || ""} theme={_navCb.theme} />
                    ) : (
                      <div style={{ fontWeight:600, color:"var(--text)", fontSize:"0.95rem", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{q.text}</div>
                    )}
                  </div>
                  {/* Options preview */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                    {["A","B","C","D"].map(opt => (
                      <div key={opt} style={{ display: "flex", gap: 8, padding: "7px 10px", background: opt === q.correct ? "rgba(0,255,136,0.07)" : "var(--bg)", border: opt === q.correct ? "1px solid var(--accent3)" : "1px solid var(--border)", fontSize: "0.82rem" }}>
                        <span style={{ fontWeight: 900, color: opt === q.correct ? "var(--accent3)" : "var(--text3)", minWidth: 14 }}>{opt}</span>
                        <span style={{ color: opt === q.correct ? "var(--text)" : "var(--text2)" }}>{q[opt]}</span>
                        {opt === q.correct && <span style={{ marginLeft: "auto", color: "var(--accent3)", fontSize: "0.7rem" }}>✓</span>}
                      </div>
                    ))}
                  </div>
                  {q.explanation && (
                    <div style={{ padding: "8px 12px", background: "rgba(0,212,255,0.04)", border: "1px solid rgba(0,212,255,0.15)", fontSize: "0.8rem", color: "var(--accent)", marginBottom: 4 }}>
                      💡 {q.explanation}
                    </div>
                  )}
                  <div style={{ fontSize: "0.7rem", color: "var(--text3)", fontFamily: "'Share Tech Mono'", marginTop: 8 }}>
                    Added: {q.addedAt?.slice(0, 10) || "—"}
                  </div>
                </div>
                {/* Actions */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-outline" style={{ padding: "6px 14px", fontSize: "0.72rem", clipPath: "none" }} onClick={() => handleEdit(q)}>✏️ EDIT</button>
                  {deleteConfirm === q.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <button style={{ padding: "6px 14px", background: "var(--danger)", border: "none", color: "#fff", fontSize: "0.7rem", cursor: "pointer" }} onClick={() => handleDelete(q.id)}>CONFIRM</button>
                      <button style={{ padding: "6px 14px", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)", fontSize: "0.7rem", cursor: "pointer" }} onClick={() => setDeleteConfirm(null)}>CANCEL</button>
                    </div>
                  ) : (
                    <button style={{ padding: "6px 14px", background: "transparent", border: "1px solid var(--border)", color: "var(--text3)", fontSize: "0.72rem", cursor: "pointer" }} onClick={() => setDeleteConfirm(q.id)}>🗑 DEL</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom requirement notice */}
      <div style={{ marginTop: 24, padding: "14px 18px", background: "var(--surface)", border: "1px solid var(--border)", display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.65rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 4 }}>MINIMUM REQUIRED</div>
          <div style={{ fontSize: "0.85rem", color: "var(--text2)" }}>
            Domain: <strong style={{ color: (bank[activeDomain] || []).length >= 10 ? "var(--accent3)" : "var(--warn)" }}>{(bank[activeDomain] || []).length}/10</strong>
            {"  ·  "}Aptitude: <strong style={{ color: (bank["Aptitude"] || []).length >= 5 ? "var(--accent3)" : "var(--warn)" }}>{(bank["Aptitude"] || []).length}/5</strong>
            {"  ·  "}Reasoning: <strong style={{ color: (bank["Reasoning"] || []).length >= 5 ? "var(--accent3)" : "var(--warn)" }}>{(bank["Reasoning"] || []).length}/5</strong>
          </div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <div style={{ fontFamily: "'Share Tech Mono'", fontSize: "0.65rem", color: "var(--text3)", letterSpacing: 2, marginBottom: 4 }}>TEST STRUCTURE</div>
          <div style={{ fontSize: "0.85rem", color: "var(--text2)" }}>10 Domain + 5 Aptitude + 5 Reasoning = <strong style={{ color: "var(--accent)" }}>20 Total</strong> · Coding questions used as Domain bonus</div>
        </div>
      </div>
    </div>
  );
}


// ── AMBASSADOR PANEL ─────────────────────────────────────────────────────────
function AmbassadorPanel({ records }) {
  const BASE_URL = "https://iirt-internastra.web.app/";
  const [newAmb, setNewAmb] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [ambList, setAmbList] = useState([]);
  const [ambLoading, setAmbLoading] = useState(true);
  const [copied, setCopied] = useState("");
  const [filterAmb, setFilterAmb] = useState("ALL");
  const [saving, setSaving] = useState(false);

  // Real-time sync from Firebase (same as candidates)
  useEffect(() => {
    setAmbLoading(true);
    const unsub = subscribeAmbassadors((list) => {
      setAmbList(list || []);
      setAmbLoading(false);
    });
    return () => unsub();
  }, []);

  const saveAmbs = async (list) => {
    setSaving(true);
    setAmbList(list); // optimistic UI update
    await saveAmbDB(list);
    setSaving(false);
  };

  const addAmbassador = async () => {
    const name = newAmb.trim().toLowerCase().replace(/\s+/g, "_");
    if (!name || ambList.find(a => a.code === name)) return;
    const username = newUsername.trim() || name;
    const password = newPassword.trim() || "Amb@2026";
    await saveAmbs([...ambList, {
      code: name,
      displayName: newAmb.trim(),
      username,
      password,
      addedAt: new Date().toISOString(),
    }]);
    setNewAmb(""); setNewUsername(""); setNewPassword("");
  };

  const removeAmbassador = async (code) => {
    await saveAmbs(ambList.filter(a => a.code !== code));
  };

  const copyLink = (code) => {
    const link = BASE_URL + "?ref=" + code;
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(code);
    setTimeout(() => setCopied(""), 2000);
  };

  // Build stats per ambassador from records
  const ambStats = ambList.map(amb => {
    const referred = records.filter(r => (r.referral || "").toLowerCase() === amb.code);
    const free     = referred.filter(r => r.status === "FREE INTERNSHIP").length;
    const discount = referred.filter(r => r.status === "50% DISCOUNT").length;
    const failed   = referred.filter(r => !r.status?.includes("FREE") && !r.status?.includes("DISCOUNT")).length;
    const enrolled = referred.filter(r => r.internshipStatus === "Enrolled").length;
    const avgScore = referred.length
      ? Math.round(referred.reduce((s,r) => s + (r.percentage || 0), 0) / referred.length)
      : 0;
    return { ...amb, total: referred.length, free, discount, failed, enrolled, avgScore, referred };
  }).sort((a,b) => b.total - a.total);

  // Direct (no referral) candidates
  const directCands = records.filter(r => !r.referral || r.referral === "");

  // All unique referral codes found in records (including unknown/deleted ambassadors)
  const allCodes = [...new Set(records.map(r => r.referral).filter(Boolean))];
  const unknownCodes = allCodes.filter(c => !ambList.find(a => a.code === c));

  const totalReferred = records.filter(r => r.referral).length;
  const topAmb = ambStats[0];

  return (
    <div>
      {/* Firebase sync status */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
        {ambLoading ? (
          <div style={{ display:"flex", alignItems:"center", gap:8, fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", color:"var(--warn)", letterSpacing:2 }}>
            <div className="spinner" style={{ width:14, height:14, borderWidth:2 }} />
            SYNCING WITH FIREBASE...
          </div>
        ) : (
          <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", color:"var(--accent3)", letterSpacing:2 }}>
            ✓ SYNCED WITH FIREBASE {saving ? "· SAVING..." : "· REAL-TIME"}
          </div>
        )}
      </div>

      {/* Stats overview */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:28 }}>
        {[
          { label:"Total Ambassadors", val: ambList.length,   color:"var(--accent)" },
          { label:"Total Referred",    val: totalReferred,    color:"var(--accent3)" },
          { label:"Direct (No Ref.)",  val: directCands.length, color:"var(--text2)" },
          { label:"Top Ambassador",    val: topAmb ? "@"+topAmb.code : "—", color:"var(--warn)" },
        ].map(s => (
          <div key={s.label} className="card" style={{ textAlign:"center", padding:"18px 12px" }}>
            <div style={{ fontFamily:"'Orbitron'", fontSize:"1.6rem", fontWeight:900, color:s.color }}>{s.val}</div>
            <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:2, color:"var(--text3)", marginTop:5, textTransform:"uppercase" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Add Ambassador */}
      <div className="card" style={{ marginBottom:24, padding:"20px 24px" }}>
        <div style={{ fontFamily:"'Orbitron'", fontSize:"0.72rem", letterSpacing:3, color:"var(--accent)", marginBottom:14 }}>
          ➕ ADD CAMPUS AMBASSADOR
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:10, alignItems:"flex-start" }}>
          <div>
            <label style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:2, color:"var(--text3)", display:"block", marginBottom:5 }}>FULL NAME</label>
            <input
              placeholder="e.g. Arun Kumar"
              value={newAmb}
              onChange={e => setNewAmb(e.target.value)}
              style={{ width:"100%", background:"var(--bg)", border:"1px solid var(--border)", color:"var(--text)", padding:"10px 14px", fontFamily:"'Rajdhani'", fontSize:"1rem", outline:"none" }}
            />
          </div>
          <div>
            <label style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:2, color:"var(--text3)", display:"block", marginBottom:5 }}>LOGIN USERNAME</label>
            <input
              placeholder="e.g. arun.kumar"
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              style={{ width:"100%", background:"var(--bg)", border:"1px solid var(--border)", color:"var(--text)", padding:"10px 14px", fontFamily:"'Share Tech Mono'", fontSize:"0.88rem", outline:"none" }}
            />
          </div>
          <div>
            <label style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:2, color:"var(--text3)", display:"block", marginBottom:5 }}>LOGIN PASSWORD</label>
            <input
              type="text"
              placeholder="e.g. Amb@2026"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={{ width:"100%", background:"var(--bg)", border:"1px solid var(--border)", color:"var(--text)", padding:"10px 14px", fontFamily:"'Share Tech Mono'", fontSize:"0.88rem", outline:"none" }}
            />
          </div>
          <div style={{ paddingTop:22 }}>
            <button className="btn btn-primary" style={{ padding:"10px 20px", whiteSpace:"nowrap" }} onClick={addAmbassador}>
              ADD
            </button>
          </div>
        </div>
        {newAmb.trim() && (
          <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", color:"var(--text3)", marginTop:8 }}>
            Referral code: <strong style={{color:"var(--accent)"}}>@{newAmb.trim().toLowerCase().replace(/\s+/g,"_")}</strong>
            &nbsp;· Login: <span style={{color:"var(--accent)"}}>{newUsername || "(set username above)"}</span>
          </div>
        )}
      </div>

      {/* Ambassador Leaderboard */}
      {ambStats.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <div style={{ fontFamily:"'Orbitron'", fontSize:"0.72rem", letterSpacing:3, color:"var(--accent)", marginBottom:14 }}>
            🏆 AMBASSADOR LEADERBOARD
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--surface2)", borderBottom:"2px solid var(--border)" }}>
                  {["Rank","Ambassador","Login Credentials","Ref. Link","Total","Free 🏆","Discount 🎟","Enrolled ✅","Avg Score","Actions"].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", letterSpacing:2, color:"var(--text3)", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ambStats.map((a, idx) => (
                  <tr key={a.code} style={{ borderBottom:"1px solid var(--border)", background: idx===0 ? "rgba(255,176,32,0.04)" : "transparent" }}>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ fontFamily:"'Orbitron'", fontSize:"1.1rem", fontWeight:900, color: idx===0?"var(--warn)":idx===1?"var(--text2)":idx===2?"#b56a00":"var(--text3)" }}>
                        {idx===0?"🥇":idx===1?"🥈":idx===2?"🥉":"#"+(idx+1)}
                      </span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <div style={{ fontWeight:700, color:"var(--text)" }}>{a.displayName}</div>
                      <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", color:"var(--accent)", marginTop:2 }}>@{a.code}</div>
                    </td>
                    <td style={{ padding:"12px 14px", minWidth:160 }}>
                      <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", marginBottom:3 }}>
                        <span style={{ color:"var(--text3)" }}>User: </span>
                        <span style={{ color:"var(--accent)" }}>{a.username || a.code}</span>
                      </div>
                      <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem" }}>
                        <span style={{ color:"var(--text3)" }}>Pass: </span>
                        <span style={{ color:"var(--warn)" }}>{a.password || "Amb@2026"}</span>
                      </div>
                    </td>
                    <td style={{ padding:"12px 14px", maxWidth:200 }}>
                      <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", color:"var(--text3)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {BASE_URL}?ref={a.code}
                      </div>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ fontFamily:"'Orbitron'", fontSize:"1.2rem", fontWeight:900, color:"var(--accent)" }}>{a.total}</span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ color:"var(--accent3)", fontWeight:700 }}>{a.free}</span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ color:"var(--warn)", fontWeight:700 }}>{a.discount}</span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ fontFamily:"'Orbitron'", fontSize:"0.88rem", fontWeight:700, color:"#00994d" }}>
                        {a.enrolled || 0}
                      </span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ fontFamily:"'Orbitron'", fontSize:"0.88rem", fontWeight:700,
                        color: a.avgScore>=90?"var(--accent3)":a.avgScore>=50?"var(--warn)":"var(--text3)" }}>
                        {a.total ? a.avgScore+"%" : "—"}
                      </span>
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <button
                          style={{ padding:"5px 12px", background: copied===a.code?"rgba(0,255,136,0.15)":"rgba(0,212,255,0.08)", border:"1px solid "+(copied===a.code?"var(--accent3)":"rgba(0,212,255,0.3)"), color:copied===a.code?"var(--accent3)":"var(--accent)", fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", cursor:"pointer", letterSpacing:1 }}
                          onClick={() => copyLink(a.code)}>
                          {copied===a.code ? "✓ COPIED" : "📋 COPY"}
                        </button>
                        <button
                          style={{ padding:"5px 10px", background:"rgba(255,59,92,0.08)", border:"1px solid rgba(255,59,92,0.25)", color:"var(--danger)", cursor:"pointer", fontSize:"0.72rem" }}
                          onClick={() => removeAmbassador(a.code)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unknown referral codes (referred but ambassador not registered) */}
      {unknownCodes.length > 0 && (
        <div className="card" style={{ marginBottom:24, padding:"16px 20px", border:"1px solid rgba(255,176,32,0.3)", background:"rgba(255,176,32,0.03)" }}>
          <div style={{ fontFamily:"'Orbitron'", fontSize:"0.65rem", letterSpacing:3, color:"var(--warn)", marginBottom:10 }}>
            ⚠ UNREGISTERED REFERRAL CODES IN DATABASE
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {unknownCodes.map(code => {
              const cnt = records.filter(r => r.referral === code).length;
              return (
                <div key={code} style={{ padding:"6px 14px", background:"rgba(255,176,32,0.07)", border:"1px solid rgba(255,176,32,0.25)", fontFamily:"'Share Tech Mono'", fontSize:"0.7rem", color:"var(--warn)", display:"flex", alignItems:"center", gap:8 }}>
                  @{code} <span style={{color:"var(--text3)"}}>({cnt} candidate{cnt>1?"s":""})</span>
                  <button
                    style={{ background:"none", border:"none", color:"var(--accent)", cursor:"pointer", fontSize:"0.7rem", fontFamily:"'Share Tech Mono'" }}
                    onClick={() => saveAmbs([...ambList, { code, displayName: code, addedAt: new Date().toISOString() }])}>
                    + ADD
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-ambassador candidate list */}
      {ambStats.length > 0 && (
        <div>
          <div style={{ fontFamily:"'Orbitron'", fontSize:"0.72rem", letterSpacing:3, color:"var(--accent)", marginBottom:14 }}>
            📋 CANDIDATES BY AMBASSADOR
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
            <button
              style={{ padding:"6px 14px", background: filterAmb==="ALL"?"var(--accent)":"transparent", border:"1px solid var(--border)", color: filterAmb==="ALL"?"#000":"var(--text2)", fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", cursor:"pointer" }}
              onClick={() => setFilterAmb("ALL")}>ALL</button>
            {ambStats.map(a => (
              <button key={a.code}
                style={{ padding:"6px 14px", background: filterAmb===a.code?"var(--accent)":"transparent", border:"1px solid var(--border)", color: filterAmb===a.code?"#000":"var(--text2)", fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", cursor:"pointer" }}
                onClick={() => setFilterAmb(a.code)}>
                @{a.code} ({a.total})
              </button>
            ))}
          </div>

          {ambStats
            .filter(a => filterAmb === "ALL" || a.code === filterAmb)
            .map(a => a.referred.length > 0 && (
              <div key={a.code} style={{ marginBottom:20, background:"var(--surface)", border:"1px solid var(--border)", overflow:"hidden" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", background:"var(--surface2)", borderBottom:"1px solid var(--border)" }}>
                  <div>
                    <span style={{ fontFamily:"'Orbitron'", fontSize:"0.75rem", fontWeight:700, color:"var(--accent)", letterSpacing:2 }}>{a.displayName}</span>
                    <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", color:"var(--text3)", marginLeft:12 }}>@{a.code} · {a.total} candidate{a.total>1?"s":""}</span>
                  </div>
                  <div style={{ display:"flex", gap:12 }}>
                    <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", color:"var(--accent3)" }}>🏆 {a.free}</span>
                    <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", color:"var(--warn)" }}>🎟 {a.discount}</span>
                    <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", color:"var(--accent)" }}>AVG {a.avgScore}%</span>
                  </div>
                </div>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem" }}>
                  <thead>
                    <tr style={{ background:"var(--bg)" }}>
                      {["Candidate ID","Name","Role","Score","Result","Internship Status"].map(h => (
                        <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontFamily:"'Share Tech Mono'", fontSize:"0.58rem", letterSpacing:2, color:"var(--text3)", textTransform:"uppercase" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {a.referred.map((r, ri) => (
                      <tr key={r.candidateId} style={{ borderTop:"1px solid var(--border)", background: ri%2===0?"transparent":"rgba(0,0,0,0.1)" }}>
                        <td style={{ padding:"9px 12px", fontFamily:"'Share Tech Mono'", fontSize:"0.68rem", color:"var(--accent)" }}>{r.candidateId}</td>
                        <td style={{ padding:"9px 12px", fontWeight:600 }}>{r.name}</td>
                        <td style={{ padding:"9px 12px", color:"var(--text2)", fontSize:"0.8rem" }}>{r.role}</td>
                        <td style={{ padding:"9px 12px", fontFamily:"'Orbitron'", fontWeight:700,
                          color: r.percentage>=90?"var(--accent3)":r.percentage>=50?"var(--warn)":"var(--danger)" }}>
                          {r.percentage}%
                        </td>
                        <td style={{ padding:"9px 12px" }}>
                          <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.58rem", padding:"3px 8px",
                            background: r.status==="FREE INTERNSHIP"?"rgba(0,255,136,0.1)":r.status==="50% DISCOUNT"?"rgba(255,176,32,0.1)":"rgba(255,59,92,0.08)",
                            color: r.status==="FREE INTERNSHIP"?"var(--accent3)":r.status==="50% DISCOUNT"?"var(--warn)":"var(--danger)",
                            border: "1px solid " + (r.status==="FREE INTERNSHIP"?"rgba(0,255,136,0.3)":r.status==="50% DISCOUNT"?"rgba(255,176,32,0.3)":"rgba(255,59,92,0.2)"),
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ padding:"9px 12px" }}>
                          {(() => {
                            const st = r.internshipStatus || "";
                            const c = {"Interested":{color:"#0077cc",icon:"🙋"},"Not Interested":{color:"#cc3300",icon:"🚫"},"Enrolled":{color:"#00994d",icon:"✅"}}[st];
                            return st ? <span style={{ fontFamily:"'Share Tech Mono'",fontSize:"0.58rem",color:c?.color||"var(--text3)",letterSpacing:1 }}>{c?.icon} {st}</span>
                                      : <span style={{ color:"var(--text3)",fontSize:"0.65rem" }}>—</span>;
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          }
        </div>
      )}

      {ambList.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 20px" }}>
          <div style={{ fontSize:"3rem", marginBottom:16 }}>🌟</div>
          <div style={{ fontFamily:"'Orbitron'", fontSize:"0.9rem", color:"var(--accent)", letterSpacing:2, marginBottom:8 }}>NO AMBASSADORS YET</div>
          <p style={{ color:"var(--text2)", fontSize:"0.9rem" }}>Add your first campus ambassador above to start generating referral links.</p>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ onBack }) {
  const [adminTab, setAdminTab] = useState("candidates");
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [xlsxLoaded, setXlsxLoaded] = useState(!!window.XLSX);
  const [importing, setImporting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);

  const deleteRecords = async (ids) => {
    const db = await loadDB();
    const updated = db.filter(r => !ids.has(r.candidateId));
    await saveDB(updated);
    ids.forEach(id => { try { localStorage.removeItem("iirt_banned_" + id); } catch {} });
    setRecords(updated);
    setSelectedIds(new Set());
    setConfirmDelete(null);
  };

  const updateInternshipStatus = async (candidateId, newStatus) => {
    const db = await loadDB();
    const updated = db.map(r => r.candidateId === candidateId ? { ...r, internshipStatus: newStatus } : r);
    await saveDB(updated);
    setRecords(updated);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const allFilteredSelected = (filtered) => filtered.length > 0 && filtered.every(r => selectedIds.has(r.candidateId));

  useEffect(() => {
    // Real-time candidate sync — all admins see new results instantly
    const unsub = subscribeCandidates((freshRecords) => {
      setRecords(freshRecords);
    });
    // Load SheetJS from CDN if not already loaded
    if (!window.XLSX) {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      script.onload = () => setXlsxLoaded(true);
      document.head.appendChild(script);
    }
    return () => unsub(); // cleanup real-time listener
  }, []);

  const filtered = records.filter(r => {
    const matchFilter = filter === "ALL" || r.status === filter;
    const matchSearch = !search ||
      r.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.candidateId?.toLowerCase().includes(search.toLowerCase()) ||
      r.role?.toLowerCase().includes(search.toLowerCase()) ||
      r.email?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const total = records.length;
  const freeCount = records.filter(r => r.status === "FREE INTERNSHIP").length;
  const discountCount = records.filter(r => r.status === "50% DISCOUNT").length;
  const notEligible = records.filter(r => r.status === "NOT ELIGIBLE").length;

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file || !window.XLSX) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const wb = window.XLSX.read(ev.target.result, { type: "array" });
        const ws = wb.Sheets["Candidates Summary"];
        if (!ws) { alert("Invalid IIRT Excel file. Sheet 'Candidates Summary' not found."); setImporting(false); return; }
        const rows = window.XLSX.utils.sheet_to_json(ws);
        const imported = rows.map(row => ({
          candidateId: row["Candidate ID"] || "",
          name: row["Full Name"] || "",
          email: row["Email"] || "",
          phone: row["Phone"] || "",
          degree: row["Degree"] || "",
          college: row["College"] || "",
          role: row["Role Applied"] || "",
          experience: row["Experience"] || "",
          registeredAt: row["Registration Date"] || "",
          percentage: row["Score (%)"] || 0,
          questionsAnswered: row["Questions Answered"] || 0,
          aiScore: row["AI Eval Score (%)"] || 0,
          faceViolations: row["Face Violations"] || 0,
          tabWarnings: row["Tab Warnings"] || 0,
          plagiarism: row["Plagiarism"] || "Pass",
          status: row["Final Status"] || "NOT ELIGIBLE",
          qa: [],
        }));
        const existing = await loadDB();
        const merged = [...existing];
        imported.forEach(imp => {
          const idx = merged.findIndex(r => r.candidateId === imp.candidateId);
          if (idx >= 0) merged[idx] = { ...merged[idx], ...imp };
          else merged.push(imp);
        });
        await saveDB(merged);
        alert(`✅ Successfully imported ${imported.length} candidate records.`);
      } catch (err) {
        alert("Failed to import file. Please ensure it is a valid IIRT Excel export.");
      }
      setImporting(false);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const clearDB = async () => {
    if (!window.confirm("Are you sure you want to clear ALL candidate records? This cannot be undone.")) return;
    await saveDB([]);
    // setRecords([]) will be called automatically by the real-time listener
  };

  return (
    <div className="page">
      <Nav page="ADMIN PANEL" />
      <PageDecorations />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 20px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 16 }}>
          <div>
            <h2 className="section-title">IIRT <span>ADMIN PANEL</span></h2>
            <p className="section-subtitle">Manage candidates &amp; question bank</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-outline" onClick={onBack}>🔒 LOGOUT</button>
            {adminTab === "candidates" && <>
              <button className="btn btn-outline" onClick={() => fileInputRef.current?.click()} disabled={importing || !xlsxLoaded}>
                {importing ? "IMPORTING..." : "📂 IMPORT XLSX"}
              </button>
              <button className="btn btn-primary" onClick={() => downloadExcel(records)} disabled={records.length === 0 || !xlsxLoaded}>
                {xlsxLoaded ? "⬇ EXPORT TO EXCEL" : "LOADING XLSX..."}
              </button>
            </>}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleImport} />
          </div>
        </div>

        {/* Main Nav Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 32, borderBottom: "2px solid var(--border)" }}>
          {[
            { key: "candidates",  label: "👥 CANDIDATES",   desc: "Results" },
            { key: "ambassadors", label: "🌟 AMBASSADORS",  desc: "Referral tracking" },
            { key: "questionbank",label: "📚 QUESTION BANK",desc: "Add questions" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setAdminTab(tab.key)}
              style={{ padding: "14px 28px", background: adminTab === tab.key ? "var(--accent)" : "transparent", border: "none", borderBottom: adminTab === tab.key ? "none" : "none", color: adminTab === tab.key ? "#000" : "var(--text2)", fontFamily: "'Orbitron'", fontSize: "0.78rem", fontWeight: 900, letterSpacing: 2, cursor: "pointer", transition: "all 0.2s", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
              <span>{tab.label}</span>
              <span style={{ fontSize: "0.58rem", fontWeight: 400, opacity: 0.7, fontFamily: "'Share Tech Mono'" }}>{tab.desc}</span>
            </button>
          ))}
        </div>

        {/* Question Bank Tab */}
        {adminTab === "questionbank" && <QuestionBankManager />}

        {/* Ambassador Tab */}
        {adminTab === "ambassadors" && <AmbassadorPanel records={records} />}

        {/* Candidates Tab below */}
        {adminTab === "candidates" && (
          <div>
          {/* Stats Row */}
          <div className="grid-3" style={{ marginBottom: 28, gridTemplateColumns: "repeat(4, 1fr)" }}>
          {[
            { label: "Total Candidates", value: total, color: "var(--accent)" },
            { label: "Free Internship 🏆", value: freeCount, color: "var(--accent3)" },
            { label: "50% Discount 🎟️", value: discountCount, color: "var(--warn)" },
            { label: "Not Eligible", value: notEligible, color: "var(--danger)" },
          ].map((s, i) => (
            <div key={i} className="card" style={{ textAlign: "center", padding: "20px" }}>
              <div style={{ fontFamily: "'Orbitron'", fontSize: "2rem", fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: "0.7rem", letterSpacing: 2, color: "var(--text3)", textTransform: "uppercase", marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <input
              type="text" placeholder="Search by name, ID, role, email..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text)", padding: "10px 16px", fontFamily: "'Rajdhani'", fontSize: "1rem", outline: "none" }}
            />
          </div>
          {["ALL", "FREE INTERNSHIP", "50% DISCOUNT", "NOT ELIGIBLE"].map(f => (
            <button key={f} className={`btn ${filter === f ? "btn-primary" : "btn-outline"}`}
              style={{ padding: "10px 16px", fontSize: "0.72rem" }}
              onClick={() => setFilter(f)}>{f}</button>
          ))}
          {records.length > 0 && (
            <button className="btn btn-danger" style={{ padding: "10px 20px", fontSize: "0.75rem" }} onClick={clearDB}>🗑 CLEAR ALL</button>
          )}
        </div>

        {/* Excel Info Banner */}
        {!xlsxLoaded && (
          <div style={{ background: "rgba(255,176,32,0.07)", border: "1px solid rgba(255,176,32,0.3)", padding: "12px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
            <div className="animate-pulse" style={{ color: "var(--warn)", fontSize: "0.85rem" }}>⏳ Loading Excel engine (SheetJS)...</div>
          </div>
        )}
        {xlsxLoaded && (
          <div style={{ background: "rgba(0,255,136,0.05)", border: "1px solid rgba(0,255,136,0.2)", padding: "12px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ color: "var(--accent3)", fontSize: "0.85rem", fontFamily: "'Share Tech Mono'" }}>✓ SHEETJS READY</span>
            <span style={{ color: "var(--text3)", fontSize: "0.8rem" }}>Export generates 4-sheet Excel: Summary · Q&amp;A Details · Security Log · Statistics</span>
          </div>
        )}

        {/* Table */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 20px" }}>
            <div style={{ fontSize: "4rem", marginBottom: 16 }}>📊</div>
            <p style={{ color: "var(--text2)", fontSize: "1.1rem" }}>{records.length === 0 ? "No candidates yet. Complete an assessment to see records here." : "No records match your search."}</p>
          </div>
        ) : (
          <>
          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 16px", background:"rgba(255,59,92,0.07)", border:"1px solid rgba(255,59,92,0.3)", marginBottom:12 }}>
              <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.75rem", color:"var(--danger)", letterSpacing:1 }}>
                {selectedIds.size} SELECTED
              </span>
              <button style={{ padding:"6px 18px", background:"rgba(255,59,92,0.15)", border:"1px solid var(--danger)", color:"var(--danger)", fontFamily:"'Orbitron',monospace", fontSize:"0.68rem", letterSpacing:2, cursor:"pointer" }}
                onClick={() => setConfirmDelete("selected")}>
                🗑 DELETE SELECTED
              </button>
              <button style={{ padding:"6px 14px", background:"transparent", border:"1px solid var(--border)", color:"var(--text3)", fontSize:"0.72rem", cursor:"pointer" }}
                onClick={() => setSelectedIds(new Set())}>
                CLEAR
              </button>
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ background: "var(--surface2)", borderBottom: "2px solid var(--border)" }}>
                  <th style={{ padding:"12px 14px", width:40 }}>
                    <input type="checkbox" checked={allFilteredSelected(filtered)}
                      onChange={() => {
                        if (allFilteredSelected(filtered)) setSelectedIds(new Set());
                        else setSelectedIds(new Set(filtered.map(r => r.candidateId)));
                      }}
                      style={{ cursor:"pointer", width:15, height:15, accentColor:"var(--accent)" }}
                    />
                  </th>
                  {["Candidate ID", "Name", "Role", "Score", "Questions", "Violation", "Referral", "Date", "Status", "Internship Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontFamily: "'Share Tech Mono'", fontSize: "0.7rem", letterSpacing: 2, color: "var(--text3)", whiteSpace: "nowrap", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.candidateId} style={{ borderBottom: "1px solid var(--border)", background: selectedIds.has(r.candidateId) ? "rgba(0,212,255,0.07)" : i % 2 === 0 ? "var(--surface)" : "transparent", transition: "background 0.2s" }}
                    onMouseEnter={e => { if (!selectedIds.has(r.candidateId)) e.currentTarget.style.background = "rgba(0,212,255,0.05)"; }}
                    onMouseLeave={e => { if (!selectedIds.has(r.candidateId)) e.currentTarget.style.background = i % 2 === 0 ? "var(--surface)" : "transparent"; }}
                  >
                    <td style={{ padding:"12px 14px", textAlign:"center" }}>
                      <input type="checkbox" checked={selectedIds.has(r.candidateId)} onChange={() => toggleSelect(r.candidateId)}
                        style={{ cursor:"pointer", width:15, height:15, accentColor:"var(--accent)" }} />
                    </td>
                    <td style={{ padding: "12px 14px", fontFamily: "'Share Tech Mono'", fontSize: "0.75rem", color: "var(--accent)", whiteSpace: "nowrap" }}>{r.candidateId}</td>
                    <td style={{ padding: "12px 14px", fontWeight: 600, whiteSpace: "nowrap" }}>{r.name}</td>
                    <td style={{ padding: "12px 14px", color: "var(--text2)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.role}</td>
                    <td style={{ padding: "12px 14px", fontFamily: "'Orbitron'", fontWeight: 700, color: r.percentage >= 90 ? "var(--accent3)" : r.percentage >= 50 ? "var(--warn)" : "var(--danger)" }}>{r.percentage}%</td>
                    <td style={{ padding: "12px 14px", color: "var(--text2)", textAlign: "center" }}>{r.questionsAnswered}/20</td>
                    <td style={{ padding: "12px 14px" }}>
                      {r.malpractice ? (
                        <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.65rem", color:"var(--danger)", background:"rgba(255,59,92,0.1)", border:"1px solid rgba(255,59,92,0.3)", padding:"3px 8px", letterSpacing:1 }}>
                          {r.terminationReason === "MULTIPLE_FACES" ? "👥 FACES" : r.terminationReason === "VOICE_DETECTED" ? "🔊 VOICE" : r.terminationReason === "TAB_SWITCH" ? "🖥️ TAB" : "⛔ VIOLATION"}
                        </span>
                      ) : r.tabWarnings > 0 ? (
                        <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.65rem", color:"var(--warn)" }}>⚠ {r.tabWarnings} warn</span>
                      ) : (
                        <span style={{ color:"var(--accent3)", fontSize:"0.75rem" }}>✓ CLEAN</span>
                      )}
                    </td>
                    <td style={{ padding:"12px 14px" }}>
                      {r.referral ? (
                        <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"0.7rem", color:"var(--accent)", background:"rgba(0,212,255,0.08)", border:"1px solid rgba(0,212,255,0.25)", padding:"3px 8px", letterSpacing:1 }}>
                          @{r.referral}
                        </span>
                      ) : (
                        <span style={{ color:"var(--text3)", fontSize:"0.72rem" }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", color: "var(--text3)", fontSize: "0.78rem", whiteSpace: "nowrap" }}>{r.registeredAt?.slice(0, 10)}</td>
                    <td style={{ padding: "12px 14px" }}>
                      <span className={`badge ${r.status === "FREE INTERNSHIP" ? "badge-green" : r.status === "50% DISCOUNT" ? "badge-warn" : "badge-danger"}`} style={{ fontSize: "0.6rem", padding: "3px 8px", animation: r.malpractice ? "glow-pulse 1.5s infinite" : "none" }}>{r.malpractice ? "⛔ " + r.status : r.status}</span>
                    </td>
                    <td style={{ padding:"10px 14px" }}>
                      {(() => {
                        const st = r.internshipStatus || "";
                        const cfg = {
                          "Interested":     { color:"#0077cc", bg:"rgba(0,119,204,0.1)",   border:"rgba(0,119,204,0.35)",   icon:"🙋" },
                          "Not Interested": { color:"#cc3300", bg:"rgba(204,51,0,0.09)",   border:"rgba(204,51,0,0.3)",     icon:"🚫" },
                          "Enrolled":       { color:"#00994d", bg:"rgba(0,153,77,0.1)",    border:"rgba(0,153,77,0.35)",    icon:"✅" },
                        };
                        const c = cfg[st];
                        return (
                          <select
                            value={st}
                            onChange={e => updateInternshipStatus(r.candidateId, e.target.value)}
                            style={{
                              background: c ? c.bg : "var(--surface2)",
                              border: "1px solid " + (c ? c.border : "var(--border)"),
                              color: c ? c.color : "var(--text3)",
                              padding:"5px 8px",
                              fontFamily:"'Share Tech Mono',monospace",
                              fontSize:"0.65rem",
                              letterSpacing:1,
                              cursor:"pointer",
                              outline:"none",
                              borderRadius:2,
                              minWidth:130,
                            }}>
                            <option value="">— Select —</option>
                            <option value="Interested">🙋 Interested</option>
                            <option value="Not Interested">🚫 Not Interested</option>
                            <option value="Enrolled">✅ Enrolled</option>
                          </select>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <button className="btn btn-outline" style={{ padding: "5px 10px", fontSize: "0.68rem", clipPath: "none" }} onClick={() => setSelectedRecord(r)}>VIEW</button>
                        <button style={{ padding:"5px 10px", fontSize:"0.68rem", background:"rgba(255,59,92,0.1)", border:"1px solid rgba(255,59,92,0.35)", color:"var(--danger)", cursor:"pointer" }}
                          onClick={() => setConfirmDelete(r.candidateId)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: "12px 14px", fontFamily: "'Share Tech Mono'", fontSize: "0.72rem", color: "var(--text3)", borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
              SHOWING {filtered.length} OF {records.length} RECORDS
            </div>
          </div>
          </>
        )}

        {/* Delete Confirm Modal */}
        {confirmDelete && (
          <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
            <div style={{ background:"var(--surface)", border:"2px solid var(--danger)", padding:"32px", maxWidth:440, width:"92%", textAlign:"center" }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize:"3rem", marginBottom:16 }}>🗑️</div>
              <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.9rem", color:"var(--danger)", letterSpacing:3, marginBottom:12 }}>CONFIRM DELETE</div>
              <p style={{ color:"var(--text2)", lineHeight:1.7, marginBottom:28 }}>
                {confirmDelete === "selected"
                  ? `Delete ${selectedIds.size} selected candidate record(s)? This cannot be undone.`
                  : `Delete record for candidate ${confirmDelete}? This cannot be undone.`}
              </p>
              <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
                <button className="btn btn-outline" style={{ clipPath:"none" }} onClick={() => setConfirmDelete(null)}>CANCEL</button>
                <button style={{ padding:"10px 28px", background:"var(--danger)", border:"none", color:"#fff", fontFamily:"'Orbitron',monospace", fontSize:"0.75rem", letterSpacing:2, cursor:"pointer" }}
                  onClick={() => deleteRecords(confirmDelete === "selected" ? selectedIds : new Set([confirmDelete]))}>
                  ✓ DELETE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Download hint */}
        {records.length > 0 && xlsxLoaded && (
          <div style={{ marginTop: 20, padding: "16px 20px", background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'Orbitron'", fontSize: "0.8rem", letterSpacing: 2, color: "var(--accent)", marginBottom: 4 }}>EXCEL EXPORT READY</div>
              <div style={{ fontSize: "0.85rem", color: "var(--text3)" }}>4 sheets: Candidates Summary · Q&amp;A Details · Security Log · Statistics</div>
            </div>
            <button className="btn btn-primary" onClick={() => downloadExcel(records)}>⬇ DOWNLOAD IIRT_Candidates.xlsx</button>
          </div>
        )}
          </div>
        )}
      </div>

      {/* Record Detail Modal */}
      {selectedRecord && (() => {
        const r = selectedRecord;
        const isMalp = r.malpractice;
        const answeredQA = (r.qa || []).filter(q => q.selectedOption && q.selectedOption !== "—");
        const correctCount = (r.qa || []).filter(q => q.isCorrect).length;
        return (
          <div className="modal-overlay" onClick={() => setSelectedRecord(null)}>
            <div style={{ background:"var(--surface)", border:"1px solid " + (isMalp ? "rgba(255,59,92,0.4)" : "var(--border)"), padding:"0", maxWidth:700, width:"92%", maxHeight:"88vh", overflowY:"auto", animation:"fadeIn 0.3s ease" }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ padding:"24px 28px", background: isMalp ? "rgba(255,59,92,0.06)" : "var(--surface2)", borderBottom:"1px solid " + (isMalp ? "rgba(255,59,92,0.25)" : "var(--border)"), display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:4, color:"var(--text3)", marginBottom:6 }}>
                    {isMalp ? "⛔ TERMINATED CANDIDATE" : "CANDIDATE PROFILE"}
                  </div>
                  <div style={{ fontFamily:"'Orbitron'", fontSize:"1.1rem", color: isMalp ? "var(--danger)" : "var(--accent)", fontWeight:700 }}>{r.candidateId}</div>
                  <div style={{ fontSize:"0.88rem", color:"var(--text2)", marginTop:4 }}>{r.name}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <span className={`badge ${r.status === "FREE INTERNSHIP" ? "badge-green" : r.status === "50% DISCOUNT" ? "badge-warn" : "badge-danger"}`} style={{ fontSize:"0.62rem", display:"block", marginBottom:8, animation: isMalp ? "glow-pulse 1.5s infinite" : "none" }}>
                    {isMalp ? "⛔ " : ""}{r.status}
                  </span>
                  <div style={{ fontFamily:"'Orbitron'", fontSize:"1.6rem", fontWeight:900, color: r.percentage >= 90 ? "var(--accent3)" : r.percentage >= 50 ? "var(--warn)" : "var(--danger)" }}>
                    {r.percentage}%
                  </div>
                </div>
              </div>

              <div style={{ padding:"24px 28px" }}>

                {/* Malpractice violation banner */}
                {isMalp && (
                  <div style={{ background:"rgba(255,59,92,0.06)", border:"2px solid rgba(255,59,92,0.3)", padding:"16px 20px", marginBottom:20, position:"relative" }}>
                    <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,var(--danger),transparent)" }}/>
                    <div style={{ fontFamily:"'Orbitron'", fontSize:"0.68rem", letterSpacing:3, color:"var(--danger)", marginBottom:10 }}>⛔ MALPRACTICE — VIOLATION REPORT</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      {[
                        ["Reason", r.terminationReason === "MULTIPLE_FACES" ? "👥 Multiple Faces" : r.terminationReason === "VOICE_DETECTED" ? "🔊 Voice Detected" : r.terminationReason === "TAB_SWITCH" ? "🖥️ Tab Switch" : r.terminationReason || "—"],
                        ["Face Violations", (r.faceViolations || 0) + " detection(s)"],
                        ["Tab Switches", (r.tabWarnings || 0) + "/2"],
                        ["Voice Warnings", (r.voiceWarnings || 0) + "/2"],
                        ["Qs Answered", (answeredQA.length) + " / 20 before termination"],
                        ["Terminated At", r.registeredAt ? new Date(r.registeredAt).toLocaleTimeString() : "—"],
                      ].map(([k, v]) => (
                        <div key={k} style={{ background:"rgba(0,0,0,0.25)", padding:"8px 12px", border:"1px solid rgba(255,59,92,0.15)" }}>
                          <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.55rem", letterSpacing:2, color:"var(--text3)", marginBottom:4 }}>{k}</div>
                          <div style={{ fontSize:"0.82rem", color:"var(--danger)", fontWeight:600 }}>{String(v)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Basic info grid */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontFamily:"'Orbitron'", fontSize:"0.68rem", letterSpacing:3, color:"var(--accent)", marginBottom:12 }}>CANDIDATE INFORMATION</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                    {[
                      ["Full Name", r.name], ["Email", r.email],
                      ["Phone", r.phone], ["Role Applied", r.role],
                      ["Department", r.department || "—"], ["Year of Study", r.yearOfStudy || r.experience || "—"],
                      ["Year of Passout", r.yearOfPassout || "—"], ["Degree / College", (r.degree || "—") + " · " + (r.college || "—")],
                      ["Registered On", r.registeredAt ? new Date(r.registeredAt).toLocaleDateString() : "—"],
                      ["Referred By", r.referral ? "@" + r.referral : "Direct / No Referral"],
                      ["Internship Status", r.internshipStatus || "— Not set —"],
                      ["AI Eval Score", (r.aiScore || 0) + "%"],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display:"flex", flexDirection:"column", padding:"9px 0", borderBottom:"1px solid var(--border)", paddingRight:12 }}>
                        <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", letterSpacing:2, color:"var(--text3)", marginBottom:3 }}>{k}</span>
                        <span style={{ fontSize:"0.88rem", color:"var(--text)", fontWeight:600 }}>{String(v || "—")}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Security summary */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontFamily:"'Orbitron'", fontSize:"0.68rem", letterSpacing:3, color:"var(--accent)", marginBottom:12 }}>SECURITY SUMMARY</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                    {[
                      ["👤 Face", (r.faceViolations || 0), (r.faceViolations || 0) > 0],
                      ["🖥️ Tab", (r.tabWarnings || 0) + "/2", (r.tabWarnings || 0) > 0],
                      ["🔊 Voice", (r.voiceWarnings || 0) + "/2", (r.voiceWarnings || 0) > 0],
                      ["🚨 Malpractice", isMalp ? "YES" : "NO", isMalp],
                    ].map(([l, v, alert]) => (
                      <div key={l} style={{ textAlign:"center", padding:"10px 8px", background: alert ? "rgba(255,59,92,0.07)" : "var(--bg)", border:"1px solid " + (alert ? "rgba(255,59,92,0.3)" : "var(--border)") }}>
                        <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.58rem", letterSpacing:1, color:"var(--text3)", marginBottom:5 }}>{l}</div>
                        <div style={{ fontFamily:"'Orbitron'", fontSize:"0.9rem", fontWeight:700, color: alert ? "var(--danger)" : "var(--accent3)" }}>{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Q&A Review */}
                {r.qa && r.qa.length > 0 && (
                  <div>
                    <div style={{ fontFamily:"'Orbitron'", fontSize:"0.68rem", letterSpacing:3, color:"var(--accent)", marginBottom:4 }}>
                      Q&amp;A REVIEW — {answeredQA.length} ANSWERED · {correctCount} CORRECT
                    </div>
                    {isMalp && (
                      <div style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.65rem", color:"var(--warn)", marginBottom:12, letterSpacing:1 }}>
                        ⚠ Showing questions attempted before termination
                      </div>
                    )}
                    {r.qa.map((item, i) => {
                      const attempted = item.selectedOption && item.selectedOption !== "—";
                      return (
                        <div key={i} style={{ marginBottom:10, padding:"12px 14px", background:"var(--bg)", border:"1px solid " + (item.isCorrect ? "rgba(0,255,136,0.2)" : attempted ? "rgba(255,59,92,0.2)" : "var(--border)"), opacity: attempted ? 1 : 0.45 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, flexWrap:"wrap", gap:6 }}>
                            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                              <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.62rem", color:"var(--text3)" }}>Q{i+1}</span>
                              <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.6rem", color:"var(--accent)", padding:"2px 6px", border:"1px solid rgba(0,212,255,0.2)" }}>{item.category}</span>
                              {!attempted && <span style={{ fontFamily:"'Share Tech Mono'", fontSize:"0.58rem", color:"var(--text3)" }}>NOT REACHED</span>}
                            </div>
                            <div style={{ fontFamily:"'Orbitron'", fontSize:"0.85rem", fontWeight:900, color: item.isCorrect ? "var(--accent3)" : attempted ? "var(--danger)" : "var(--text3)" }}>
                              {item.score || 0}/10
                            </div>
                          </div>
                          <div style={{ fontSize:"0.88rem", color:"var(--text)", fontWeight:600, marginBottom:attempted ? 8 : 0, lineHeight:1.5 }}>
                            {item.question?.slice(0, 120)}{item.question?.length > 120 ? "…" : ""}
                          </div>
                          {attempted && (
                            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                              <span style={{ padding:"3px 10px", background: item.isCorrect ? "rgba(0,255,136,0.08)" : "rgba(255,59,92,0.08)", border:"1px solid " + (item.isCorrect ? "var(--accent3)" : "var(--danger)"), fontSize:"0.78rem", color: item.isCorrect ? "var(--accent3)" : "var(--danger)" }}>
                                {item.isCorrect ? "✅" : "❌"} Selected: {item.selectedOption}
                              </span>
                              {!item.isCorrect && item.correctOption && item.correctOption !== "—" && (
                                <span style={{ padding:"3px 10px", background:"rgba(0,255,136,0.06)", border:"1px solid var(--accent3)", fontSize:"0.78rem", color:"var(--accent3)" }}>
                                  ✓ Correct: {item.correctOption}
                                </span>
                              )}
                            </div>
                          )}
                          {item.feedback && <div style={{ fontSize:"0.75rem", color:"var(--accent)", padding:"5px 8px", background:"rgba(0,212,255,0.05)", marginTop:6 }}>🤖 {item.feedback}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button className="btn btn-outline" style={{ marginTop:24, width:"100%", clipPath:"none" }} onClick={() => setSelectedRecord(null)}>CLOSE</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── SPLASH SCREEN ────────────────────────────────────────────────────────────
function SplashScreen({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("INITIALIZING SYSTEM...");
  const [dots, setDots] = useState([false,false,false,false,false,false]);

  const [particles] = useState(() =>
    Array.from({ length: 24 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      dur: Math.random() * 4 + 3,
      delay: Math.random() * 3,
    }))
  );
  const steps = [
    "INITIALIZING ASSESSMENT ENGINE...",
    "CONNECTING TO FIREBASE DATABASE...",
    "VERIFYING SECURITY PROTOCOLS...",
    "LOADING QUESTION MODULES...",
    "CALIBRATING AI EVALUATOR...",
    "SYSTEM READY — LAUNCHING",
  ];

  useEffect(() => {
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setProgress(Math.min(step * 17, 100));
      setStatusText(steps[Math.min(step, steps.length - 1)]);
      setDots(d => { const nd = [...d]; nd[step - 1] = true; return nd; });
      if (step >= 6) {
        clearInterval(interval);
        setTimeout(onDone, 700);
      }
    }, 480);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "var(--bg)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      zIndex: 9999, overflow: "hidden",
    }}>
      {/* Grid */}
      <div style={{
        position:"absolute",inset:0,
        backgroundImage:"linear-gradient(rgba(0,212,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,212,255,0.04) 1px,transparent 1px)",
        backgroundSize:"56px 56px",
      }}/>

      {/* Deep radial glow */}
      <div style={{
        position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
        width:"90vw",height:"90vh",
        background:"radial-gradient(ellipse,rgba(0,212,255,0.07) 0%,rgba(26,109,212,0.04) 35%,transparent 65%)",
        pointerEvents:"none",
      }}/>

      {/* Animated particles */}
      {particles.map(p => (
        <div key={p.id} style={{
          position:"absolute",
          left:p.x+"%",top:p.y+"%",
          width:p.size,height:p.size,
          borderRadius:"50%",
          background: p.id%4===0?"rgba(0,212,255,0.7)":p.id%4===1?"rgba(0,255,136,0.5)":p.id%4===2?"rgba(26,109,212,0.6)":"rgba(255,176,32,0.4)",
          animation:`particleDrift ${p.dur}s ease-in-out ${p.delay}s infinite`,
          boxShadow:`0 0 ${p.size*4}px currentColor`,
          pointerEvents:"none",
        }}/>
      ))}

      {/* Horizontal scan */}
      <div style={{
        position:"absolute",left:0,right:0,height:"1px",
        background:"linear-gradient(90deg,transparent,rgba(0,212,255,0.5),transparent)",
        animation:"scanline 2.5s linear infinite",pointerEvents:"none",
      }}/>

      {/* Corner brackets */}
      {[
        {top:16,left:16,bw:"2px 0 0 2px"},
        {top:16,right:16,bw:"2px 2px 0 0"},
        {bottom:16,left:16,bw:"0 0 2px 2px"},
        {bottom:16,right:16,bw:"0 2px 2px 0"},
      ].map((pos,i)=>(
        <div key={i} style={{
          position:"absolute",width:64,height:64,
          borderColor:"rgba(0,212,255,0.45)",borderStyle:"solid",borderWidth:pos.bw,
          ...pos,pointerEvents:"none",
          animation:"glow-pulse 2.5s infinite",animationDelay:(i*0.3)+"s",
        }}/>
      ))}

      {/* Top edge line */}
      <div style={{
        position:"absolute",top:0,left:0,right:0,height:"2px",
        background:"linear-gradient(90deg,transparent,rgba(0,212,255,0.6) 30%,rgba(0,255,136,0.4) 60%,transparent)",
        pointerEvents:"none",
      }}/>
      <div style={{
        position:"absolute",bottom:0,left:0,right:0,height:"2px",
        background:"linear-gradient(90deg,transparent,rgba(26,109,212,0.5) 50%,transparent)",
        pointerEvents:"none",
      }}/>

      <div style={{ position:"relative",zIndex:1,textAlign:"center",padding:"0 24px",maxWidth:540,width:"100%" }}>
        {/* Logo */}
        <div style={{ marginBottom:32 }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -4 364 80" width="360" height="76" style={{ margin:"0 auto", display:"block", overflow:"visible" }}>
            <defs>
              <filter id="sglow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              <linearGradient id="sgrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1a6fd4"/>
                <stop offset="50%" stopColor="#00d4ff"/>
                <stop offset="100%" stopColor="#1a6fd4"/>
              </linearGradient>
              <linearGradient id="sgrad2" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#00d4ff"/>
                <stop offset="100%" stopColor="#1a6fd4"/>
              </linearGradient>
            </defs>
            {/* Orbit arc */}
            <ellipse cx="50" cy="32" rx="38" ry="28" fill="none" stroke="url(#sgrad)" strokeWidth="1.3" opacity="0.4" strokeDasharray="5 4"/>
            {/* Connector lines */}
            <line x1="39" y1="19" x2="23" y2="10" stroke="#1a6fd4" strokeWidth="1.3" opacity="0.75"/>
            <line x1="61" y1="19" x2="78" y2="10" stroke="#1a6fd4" strokeWidth="1.3" opacity="0.75"/>
            <line x1="37" y1="43" x2="21" y2="53" stroke="#1a6fd4" strokeWidth="1.3" opacity="0.75"/>
            <line x1="63" y1="43" x2="79" y2="53" stroke="#1a6fd4" strokeWidth="1.3" opacity="0.75"/>
            {/* CENTER: Person circle */}
            <circle cx="50" cy="32" r="19" fill="none" stroke="url(#sgrad2)" strokeWidth="2"/>
            <circle cx="50" cy="24" r="5.5" fill="none" stroke="#00d4ff" strokeWidth="1.8"/>
            <path d="M41 44 C41 36 46 34 50 34 C54 34 59 36 59 44" fill="none" stroke="#00d4ff" strokeWidth="1.8"/>
            <line x1="48" y1="34" x2="45.5" y2="39" stroke="#00d4ff" strokeWidth="1.3"/>
            <line x1="52" y1="34" x2="54.5" y2="39" stroke="#00d4ff" strokeWidth="1.3"/>
            <polygon points="50,34 48.5,37.5 50,40 51.5,37.5" fill="#00d4ff" opacity="0.75"/>
            {/* TOP-LEFT: Bar chart */}
            <circle cx="16" cy="8" r="10" fill="none" stroke="#1a6fd4" strokeWidth="1.6"/>
            <rect x="11"  y="13" width="2.8" height="3.5"  fill="#1a6fd4" opacity="0.9"/>
            <rect x="14.8" y="9"  width="2.8" height="7.5" fill="#1a6fd4"/>
            <rect x="18.6" y="11" width="2.8" height="5.5" fill="#1a6fd4" opacity="0.8"/>
            <line x1="10.5" y1="16.8" x2="22.5" y2="16.8" stroke="#1a6fd4" strokeWidth="1" opacity="0.6"/>
            {/* TOP-RIGHT: Document */}
            <circle cx="84" cy="8" r="10" fill="none" stroke="#1a6fd4" strokeWidth="1.6"/>
            <rect x="79" y="2.5" width="10" height="13" rx="1.2" fill="none" stroke="#1a6fd4" strokeWidth="1.3"/>
            <polyline points="85.5,2.5 89,2.5 89,6 85.5,6" fill="rgba(26,109,212,0.2)" stroke="#1a6fd4" strokeWidth="1"/>
            <line x1="80.5" y1="8.5"  x2="87.5" y2="8.5"  stroke="#1a6fd4" strokeWidth="1.1" opacity="0.85"/>
            <line x1="80.5" y1="11"   x2="87.5" y2="11"   stroke="#1a6fd4" strokeWidth="1.1" opacity="0.85"/>
            <line x1="80.5" y1="13.5" x2="85"   y2="13.5" stroke="#1a6fd4" strokeWidth="1.1" opacity="0.65"/>
            {/* BOTTOM-LEFT: Magnifier */}
            <circle cx="12" cy="56" r="10" fill="none" stroke="#1a6fd4" strokeWidth="1.6"/>
            <circle cx="10" cy="53.5" r="5" fill="none" stroke="#1a6fd4" strokeWidth="1.4"/>
            <line x1="14" y1="57.8" x2="17.5" y2="61.5" stroke="#1a6fd4" strokeWidth="2.2" strokeLinecap="round"/>
            {/* BOTTOM-RIGHT: Lightbulb */}
            <circle cx="88" cy="56" r="10" fill="none" stroke="#1a6fd4" strokeWidth="1.6"/>
            <path d="M88 47.5 C84 47.5 81 50.2 81 53.5 C81 56 82.5 58.2 84.8 59.2 L84.8 61.5 L91.2 61.5 L91.2 59.2 C93.5 58.2 95 56 95 53.5 C95 50.2 92 47.5 88 47.5Z" fill="none" stroke="#1a6fd4" strokeWidth="1.3"/>
            <line x1="85" y1="62" x2="91" y2="62" stroke="#1a6fd4" strokeWidth="1.3"/>
            <line x1="85.8" y1="64" x2="90.2" y2="64" stroke="#1a6fd4" strokeWidth="1.2"/>
            <line x1="86" y1="53.5" x2="90" y2="53.5" stroke="#1a6fd4" strokeWidth="1" opacity="0.55"/>
            {/* INTERNASTRA text — moved to x=106 so full word fits */}
            <text x="106" y="40" fontFamily="'Orbitron',monospace" fontWeight="900" fontSize="26" fill="url(#sgrad)" letterSpacing="2" filter="url(#sglow)">INTERNASTRA</text>
            <text x="108" y="56" fontFamily="'Rajdhani',sans-serif" fontWeight="600" fontSize="8.5" fill="#4a8ab0" letterSpacing="5">YOUR CAREER STARTS HERE</text>
          </svg>
        </div>

        {/* Title */}
        <div style={{ fontFamily:"'Orbitron',monospace", fontSize:"0.7rem", letterSpacing:5, color:"var(--accent)", marginBottom:8, opacity:0.7 }}>
          IIRT · INTERNSHIP READINESS TEST
        </div>

        {/* Multi-ring orbital progress loader */}
        <div style={{ position:"relative", width:160, height:160, margin:"24px auto 20px" }}>
          <svg viewBox="0 0 160 160" width="160" height="160" style={{position:"absolute",inset:0}}>
            <defs>
              <linearGradient id="pring" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1a6fd4"/>
                <stop offset="100%" stopColor="#00d4ff"/>
              </linearGradient>
            </defs>
            {/* Outer thin orbit ring */}
            <circle cx="80" cy="80" r="75" fill="none" stroke="rgba(0,212,255,0.06)" strokeWidth="1"/>
            {/* 2nd orbit ring spinning slow */}
            <circle cx="80" cy="80" r="68" fill="none" stroke="rgba(26,109,212,0.1)" strokeWidth="1.5"/>
            <circle cx="80" cy="80" r="68" fill="none" stroke="rgba(0,212,255,0.2)" strokeWidth="1.5"
              strokeLinecap="round" strokeDasharray="8 420"
              transform="rotate(-90 80 80)"
              style={{animation:"spin 8s linear infinite"}}
            />
            {/* 3rd orbit ring — counter spin */}
            <circle cx="80" cy="80" r="60" fill="none" stroke="rgba(0,212,255,0.07)" strokeWidth="1.5"/>
            <circle cx="80" cy="80" r="60" fill="none" stroke="rgba(0,255,136,0.15)" strokeWidth="1.5"
              strokeLinecap="round" strokeDasharray="6 370"
              transform="rotate(-90 80 80)"
              style={{animation:"spin 5s linear infinite reverse"}}
            />
            {/* Main progress arc */}
            <circle cx="80" cy="80" r="50" fill="none" stroke="rgba(0,212,255,0.08)" strokeWidth="6"/>
            <circle cx="80" cy="80" r="50" fill="none" stroke="url(#pring)" strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray="314.16"
              strokeDashoffset={314.16 - (314.16 * progress / 100)}
              transform="rotate(-90 80 80)"
              style={{ transition:"stroke-dashoffset 0.45s cubic-bezier(0.4,0,0.2,1)", filter:"drop-shadow(0 0 8px rgba(0,212,255,0.8))" }}
            />
            {/* Satellite dots on progress ring — 4 like the logo */}
            {[0,90,180,270].map((angle,i) => (
              <g key={i}>
                <circle
                  cx={80 + 50 * Math.cos((angle - 90) * Math.PI / 180)}
                  cy={80 + 50 * Math.sin((angle - 90) * Math.PI / 180)}
                  r="3.5"
                  fill={i===0?"rgba(0,212,255,0.8)":i===1?"rgba(0,255,136,0.7)":i===2?"rgba(26,109,212,0.8)":"rgba(255,176,32,0.6)"}
                />
                <circle
                  cx={80 + 50 * Math.cos((angle - 90) * Math.PI / 180)}
                  cy={80 + 50 * Math.sin((angle - 90) * Math.PI / 180)}
                  r="6"
                  fill="none"
                  stroke={i===0?"rgba(0,212,255,0.3)":i===1?"rgba(0,255,136,0.25)":i===2?"rgba(26,109,212,0.3)":"rgba(255,176,32,0.2)"}
                  strokeWidth="1"
                />
              </g>
            ))}
            {/* Inner glow circle */}
            <circle cx="80" cy="80" r="32" fill="rgba(0,212,255,0.02)" stroke="rgba(0,212,255,0.08)" strokeWidth="1"/>
            <circle cx="80" cy="80" r="22" fill="rgba(0,212,255,0.03)" stroke="rgba(26,109,212,0.1)" strokeWidth="1"/>
          </svg>
          <div style={{
            position:"absolute",inset:0,
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            fontFamily:"'Orbitron',monospace",
          }}>
            <div style={{fontSize:"1.4rem",fontWeight:900,color:"var(--accent)",lineHeight:1,
              filter:"drop-shadow(0 0 12px rgba(0,212,255,0.6))",
            }}>{progress}%</div>
            <div style={{fontSize:"0.48rem",color:"var(--text3)",letterSpacing:3,marginTop:4}}>LOADING</div>
          </div>
        </div>

        {/* Status text with terminal cursor */}
        <div style={{
          fontFamily:"'Share Tech Mono',monospace",
          fontSize:"0.72rem",letterSpacing:2,
          color:"var(--text2)",marginBottom:20,
          minHeight:20,
          display:"flex",alignItems:"center",justifyContent:"center",gap:4,
        }}>
          <span style={{color:"var(--accent3)"}}>▶</span>
          {statusText}
          <span style={{animation:"pulse 0.7s step-end infinite",color:"var(--accent)"}}>█</span>
        </div>

        {/* Step progress indicators */}
        <div style={{ display:"flex",gap:6,justifyContent:"center",alignItems:"center",marginBottom:20 }}>
          {dots.map((done,i) => (
            <div key={i} style={{
              width: done ? 24 : 8, height:8, borderRadius:4,
              background: done ? "var(--accent)" : "rgba(0,212,255,0.15)",
              border: done ? "none" : "1px solid rgba(0,212,255,0.3)",
              transition:"all 0.4s ease",
              boxShadow: done ? "0 0 10px var(--accent)" : "none",
            }} />
          ))}
        </div>

      </div>
    </div>
  );
}

// Theme is passed via prop to Nav

export default function App() {
  const [splash, setSplash] = useState(true);
  const [theme] = useState("light"); // Light theme only
  const toggleTheme = () => {}; // disabled — light only
  // Apply light theme permanently
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    try { localStorage.setItem("iirt_theme", "light"); } catch {}
  }, []);
  const [screen, setScreen] = useState(() => {
    try {
      // If terminated session exists → lock on terminated screen
      const term = sessionStorage.getItem("iirt_terminated");
      if (term) return "terminated";
      const saved = sessionStorage.getItem("iirt_screen");
      return saved || "landing";
    } catch { return "landing"; }
  });
  const [terminatedSession, setTerminatedSession] = useState(() => {
    try {
      const s = sessionStorage.getItem("iirt_terminated");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [candidate, setCandidate] = useState(() => {
    try {
      const s = sessionStorage.getItem("iirt_candidate");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [result, setResult] = useState(() => {
    try {
      const s = sessionStorage.getItem("iirt_result");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [ambSession, setAmbSession] = useState(() => {
    try {
      const s = sessionStorage.getItem("iirt_amb_session");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });

  // Persist screen + candidate + result to sessionStorage on every change
  useEffect(() => {
    // Never overwrite terminated state — once banned, always banned until localStorage cleared
    if (screen !== "terminated") {
      try { sessionStorage.setItem("iirt_screen", screen); } catch {}
    }
  }, [screen]);
  useEffect(() => {
    try {
      if (candidate) sessionStorage.setItem("iirt_candidate", JSON.stringify(candidate));
      else sessionStorage.removeItem("iirt_candidate");
    } catch {}
  }, [candidate]);
  useEffect(() => {
    try {
      if (result) sessionStorage.setItem("iirt_result", JSON.stringify(result));
      else sessionStorage.removeItem("iirt_result");
    } catch {}
  }, [result]);
  useEffect(() => {
    try {
      if (ambSession) sessionStorage.setItem("iirt_amb_session", JSON.stringify(ambSession));
      else sessionStorage.removeItem("iirt_amb_session");
    } catch {}
  }, [ambSession]);

  // Capture referral code from URL (?ref=arun) on first load
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref && ref.trim()) {
        localStorage.setItem("iirt_referral", ref.trim().toLowerCase());
        // Clean URL without reloading
        const clean = window.location.pathname;
        window.history.replaceState({}, "", clean);
      }
    } catch {}
  }, []);

  // Load SheetJS on app mount + inject favicon
  useEffect(() => {
    if (!window.XLSX) {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      document.head.appendChild(script);
    }
    // Inject SVG favicon — Internastra logo icon (person + 4 satellites)
    const existingFav = document.querySelector("link[rel*='icon']");
    if (!existingFav) {
      const svgFav = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>
        <rect width='64' height='64' fill='%23020812'/>
        <ellipse cx='32' cy='32' rx='26' ry='26' fill='none' stroke='%231a6fd4' stroke-width='1.2' opacity='0.5' stroke-dasharray='4 3'/>
        <line x1='24' y1='20' x2='16' y2='12' stroke='%231a6fd4' stroke-width='1.2' opacity='0.8'/>
        <line x1='40' y1='20' x2='48' y2='12' stroke='%231a6fd4' stroke-width='1.2' opacity='0.8'/>
        <line x1='23' y1='44' x2='15' y2='52' stroke='%231a6fd4' stroke-width='1.2' opacity='0.8'/>
        <line x1='41' y1='44' x2='49' y2='52' stroke='%231a6fd4' stroke-width='1.2' opacity='0.8'/>
        <circle cx='32' cy='32' r='13' fill='none' stroke='%2300d4ff' stroke-width='2'/>
        <circle cx='32' cy='26' r='4' fill='none' stroke='%2300d4ff' stroke-width='1.6'/>
        <path d='M25 42 C25 36 29 34 32 34 C35 34 39 36 39 42' fill='none' stroke='%2300d4ff' stroke-width='1.6'/>
        <polygon points='32,34 30.5,37.5 32,40 33.5,37.5' fill='%2300d4ff' opacity='0.8'/>
        <circle cx='12' cy='10' r='8' fill='none' stroke='%231a6fd4' stroke-width='1.5'/>
        <rect x='8.5' y='13.5' width='2' height='2.5' fill='%231a6fd4'/>
        <rect x='11.5' y='10' width='2' height='6' fill='%231a6fd4'/>
        <rect x='14.5' y='11.5' width='2' height='4.5' fill='%231a6fd4' opacity='0.8'/>
        <circle cx='52' cy='10' r='8' fill='none' stroke='%231a6fd4' stroke-width='1.5'/>
        <rect x='47.5' y='5.5' width='9' height='10' rx='1' fill='none' stroke='%231a6fd4' stroke-width='1.2'/>
        <line x1='49' y1='9.5' x2='55' y2='9.5' stroke='%231a6fd4' stroke-width='1'/>
        <line x1='49' y1='11.5' x2='55' y2='11.5' stroke='%231a6fd4' stroke-width='1'/>
        <circle cx='10' cy='54' r='8' fill='none' stroke='%231a6fd4' stroke-width='1.5'/>
        <circle cx='8.5' cy='52' r='4' fill='none' stroke='%231a6fd4' stroke-width='1.3'/>
        <line x1='11.5' y1='55.5' x2='14.5' y2='58.5' stroke='%231a6fd4' stroke-width='2' stroke-linecap='round'/>
        <circle cx='54' cy='54' r='8' fill='none' stroke='%231a6fd4' stroke-width='1.5'/>
        <path d='M54 46.5 C51 46.5 49 49 49 51.5 C49 53.5 50 55 51.8 55.8 L51.8 57.5 L56.2 57.5 L56.2 55.8 C58 55 59 53.5 59 51.5 C59 49 57 46.5 54 46.5Z' fill='none' stroke='%231a6fd4' stroke-width='1.2'/>
        <line x1='52' y1='58' x2='56' y2='58' stroke='%231a6fd4' stroke-width='1.2'/>
      </svg>`;
      const encoded = svgFav.replace(/#/g, '%23');
      const link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/svg+xml";
      link.href = "data:image/svg+xml," + encoded;
      document.head.appendChild(link);
      // Also set page title
      document.title = "Internastra-IIRT";
    }
  }, []);

  const goTo = (s) => {
    // Push a new history entry so browser back button works within the app
    window.history.pushState({ iirtScreen: s }, "", window.location.pathname);
    setScreen(s);
  };

  // Browser back button — intercept popstate, redirect to previous IIRT screen
  useEffect(() => {
    const handlePop = (e) => {
      // Always push a fresh state so the tab is never closed by back button
      window.history.pushState({ iirtScreen: screen }, "", window.location.pathname);
      // Navigate to logical "back" destination
      const backMap = {
        register:         "landing",
        interview:        "register",
        results:          "landing",
        adminLogin:       "landing",
        admin:            "landing",
        terminated:       "landing",
        ambassadorLogin:  "landing",
        ambassadorPortal: "landing",
      };
      const dest = backMap[screen] || "landing";
      setScreen(dest);
    };
    window.addEventListener("popstate", handlePop);
    // Seed initial history entry
    window.history.replaceState({ iirtScreen: screen }, "", window.location.pathname);
    return () => window.removeEventListener("popstate", handlePop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);



  // Wire Nav callbacks — runs every render so Nav always has latest values
  _navCb.toggleTheme = toggleTheme;
  _navCb.theme = theme;
  _navCb.goHome = () => goTo("landing");

  if (splash) return (
    <div className="app" data-theme={theme}><style>{css}</style>
      <SplashScreen onDone={() => setSplash(false)} />
    </div>
  );

  return (
    <div className="app" data-theme={theme}>
      <style>{css}</style>


      {screen === "terminated" && <BannedPage session={terminatedSession} />}
      {screen === "landing" && <LandingPage onStart={() => goTo("register")} onAdmin={() => goTo("adminLogin")} onAmbassador={() => goTo("ambassadorLogin")} />}
      {screen === "register" && (
        <RegistrationPage onComplete={(data) => {
          // Block banned candidates from re-registering with same ID
          if (isCandidateBanned(data.candidateId)) {
            // This shouldn't normally happen since ID is auto-generated,
            // but guard anyway
            return;
          }
          setCandidate(data);
          goTo("interview");
        }} />
      )}
      {screen === "interview" && candidate && (
        <InterviewPage candidate={candidate} onComplete={(r) => {
          setResult(r);
          try { sessionStorage.removeItem("iirt_interview_phase"); } catch {}
          goTo("results");
        }} />
      )}
      {screen === "interview" && !candidate && (
        // Guard: if candidate is missing (session lost), go back to register
        <div className="page" style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh"}}>
          <PageDecorations />
          <div style={{textAlign:"center",position:"relative",zIndex:2}}>
            <div style={{fontSize:"3rem",marginBottom:16}}>⏱️</div>
            <h3 style={{fontFamily:"'Orbitron',monospace",color:"var(--accent)",letterSpacing:3,marginBottom:12,fontSize:"1rem"}}>SESSION RESTORED</h3>
            <p style={{color:"var(--text2)",marginBottom:24,lineHeight:1.7}}>Your session was recovered after a page refresh.<br/>Please register again to continue.</p>
            <button className="btn btn-primary" onClick={() => goTo("register")}>CONTINUE REGISTRATION</button>
          </div>
        </div>
      )}
      {screen === "results" && candidate && result && (
        <ResultsPage candidate={candidate} result={result} onAdmin={() => goTo("adminLogin")} />
      )}
      {screen === "adminLogin" && (
        <AdminLogin
          onLoginSuccess={() => goTo("admin")}
          onBack={() => goTo("landing")}
        />
      )}
      {screen === "admin" && <AdminDashboard onBack={() => goTo("landing")} />}
      {screen === "ambassadorLogin" && (
        <AmbassadorLoginPage
          onLoginSuccess={(amb) => { setAmbSession(amb); goTo("ambassadorPortal"); }}
          onBack={() => goTo("landing")}
        />
      )}
      {screen === "ambassadorPortal" && ambSession && (
        <AmbassadorDashboard
          ambassador={ambSession}
          onBack={() => { setAmbSession(null); goTo("landing"); }}
        />
      )}
      {screen === "ambassadorPortal" && !ambSession && (
        <AmbassadorLoginPage
          onLoginSuccess={(amb) => { setAmbSession(amb); goTo("ambassadorPortal"); }}
          onBack={() => goTo("landing")}
        />
      )}
    </div>
  );
}
